import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Users, Building2, Package, ShoppingCart, LogOut, Plus, MoreVertical, Shield, Bell, CheckCircle, XCircle, Clock, X, Download, Paperclip, User, Mail, Phone, MapPin, Briefcase, Calendar, Trash2, Search, Send, Hammer, AlertTriangle, FileText, ChevronLeft, ChevronRight, DollarSign, Globe } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import AdminSidebar from '../components/AdminSidebar';
import LanguageToggle from '../components/LanguageToggle';
import StandPickerModal from '../components/StandPickerModal';
import UltraAdminISG from '../components/UltraAdminISG';
import NotificationBell from '../components/NotificationBell';
import AdminActivityFeed from '../components/AdminActivityFeed';
import PageIntro from '../components/PageIntro';
import { getPageIntro } from '../lib/pageIntros';
import { useLanguage } from '../contexts/LanguageContext';
import { useTranslations } from '../translations';
import { toast } from '../components/ui/sonner';
import { apiClient } from '../lib/apiClient';
import { blockersToBullets } from '../lib/blockerMessages';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UltraAdminDashboard = () => {
  const navigate = useNavigate();
  const { tab = 'overview' } = useParams();
  const { language } = useLanguage();
  const t = useTranslations(language);
  const tr = language === 'tr';
  const [stats, setStats] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [tenantStatusTab, setTenantStatusTab] = React.useState('all');
  const [tenantZoneTab,   setTenantZoneTab]   = React.useState('all');
  const [users, setUsers] = useState([]);
  const [pendingRegs, setPendingRegs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [standChangeRequests, setStandChangeRequests] = useState([]);
  const [reviewLoading, setReviewLoading] = useState({});
  const [reviewNote, setReviewNote]       = useState({});
  // Modal for displaying long customer notes from change requests
  const [changeReqNoteModal, setChangeReqNoteModal] = useState(null); // { text, country, stand_no } | null
  // Alternatif öner state'leri
  const [proposeMode, setProposeMode]     = useState({}); // {[reqId]: bool}
  const [proposeLoading, setProposeLoading] = useState({});
  const [alternatives, setAlternatives]   = useState({}); // {[reqId]: stand[]}
  const [standPickerReqId, setStandPickerReqId] = useState(null); // hangi talep için picker açık
  // 'propose' (default) → alternatifler önerilir; 'direct' → admin direkt atar (Item 3)
  const [pickerMode, setPickerMode] = useState({});
  const [designUnreadCount, setDesignUnreadCount] = useState(0); // unread customer msgs badge
  const [newOrdersCount, setNewOrdersCount] = useState(0); // unread order-related notifications
  // URL'den aktif tab türetiliyor
  const activeTab = ['overview', 'pending', 'tenants', 'users', 'stand-requests', 'stand-designs', 'isg', 'activity', 'settings'].includes(tab) ? tab
    : tab === 'applications' ? 'pending'
    : 'overview';
  const [approvalLoading, setApprovalLoading] = useState({});
  // Current admin identity — drives the change-request ownership rule (only
  // the admin who assigned the tenant's stand may approve/reject/propose).
  const [currentAdmin, setCurrentAdmin] = useState(null);
  // Super-admin override modal: { reqId, ownerName, action: 'review'|'propose', onConfirm }
  const [crOverride, setCrOverride] = useState(null);
  // Phase 86AT — Delete-with-password modal for Stand Change Requests.
  // Holds the request being deleted plus the password input state. The
  // backend requires the shared Ultra Password before purging the doc.
  const [crDeleteModal, setCrDeleteModal] = useState(null); // { req, password, loading, error }

  useEffect(() => {
    let cancelled = false;
    apiClient.get('/auth/me').then(r => { if (!cancelled) setCurrentAdmin(r.data || null); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Returns { allowed, ownerName, isOverride }.
  //  • allowed: false → Approve/Reject/Propose buttons disabled in the UI.
  //  • isOverride: true → Super Admin acting on another admin's territory;
  //    we'll surface a confirm modal before firing the API call.
  const ownershipForReq = (req) => {
    if (!currentAdmin || !req) return { allowed: true, ownerName: '', isOverride: false };
    const ownerId = req.assigned_by_admin_id || '';
    const me = currentAdmin.id || '';
    const ownerName = req.assigned_by_admin_name || '';
    if (!ownerId || ownerId === me) return { allowed: true, ownerName: '', isOverride: false };
    if (currentAdmin.is_super_admin) return { allowed: true, ownerName, isOverride: true };
    return { allowed: false, ownerName, isOverride: false };
  };
  // Synchronous guard against double-click race — state updates above are async.
  const approvalInFlightRef = useRef(new Set());
  const [appTab, setAppTab] = useState('pending');
  const [appZone, setAppZone] = useState('all'); // Phase 86AS — zone filter chips
  const [appSearch, setAppSearch] = useState('');
  const [userRoleTab, setUserRoleTab] = useState('all');
  const [userSearch, setUserSearch] = useState('');
  const [selectedReg, setSelectedReg] = useState(null);
  // Phase 86AZ.25 — Lightbox state for attachment carousel in the
  // unified Applications detail modal. -1 = closed; otherwise the
  // index inside the currently-selected reg's attachment list.
  const [attPreviewIdx, setAttPreviewIdx] = useState(-1);

  // Reset lightbox state when the user switches to a different reg
  // or closes the detail modal entirely.
  useEffect(() => { setAttPreviewIdx(-1); }, [selectedReg?.id]);

  // Lightbox keyboard nav (ESC / ←/→)
  useEffect(() => {
    if (attPreviewIdx < 0) return;
    const files = selectedReg?.attachment_filenames || (selectedReg?.attachment_filename ? [selectedReg.attachment_filename] : []);
    const total = files.length;
    if (!total) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setAttPreviewIdx(-1);
      else if (e.key === 'ArrowRight') setAttPreviewIdx(i => (i + 1) % total);
      else if (e.key === 'ArrowLeft')  setAttPreviewIdx(i => (i - 1 + total) % total);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [attPreviewIdx, selectedReg]);

  // Bulk selection state
  const [selectedRegs, setSelectedRegs] = useState([]);   // array of reg.id
  const [bulkLoading, setBulkLoading] = useState(false);

  // Tab navigasyonu
  const goTab = (t) => navigate(`/ultra-admin/dashboard/${t}`);

  // ── Poll unread design messages every 10s ─────────────────────────────
  const pollDesignUnread = React.useCallback(async () => {
    const token = localStorage.getItem('cop31_ultra_token');
    if (!token) return;
    try {
      const r = await apiClient.get(`/stand-designs/admin/unread-count`);
      setDesignUnreadCount(r.data.unread || 0);
    } catch {}
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('cop31_ultra_token');
    if (!token) {
      navigate('/giris?tab=ultra');
      return;
    }
    fetchData();
    pollDesignUnread();
    pollNewOrders();
    const interval = setInterval(() => {
      pollDesignUnread();
      pollNewOrders();
    }, 10000);
    return () => clearInterval(interval);
  }, []);

  const pollNewOrders = async () => {
    try {
      const token = localStorage.getItem('cop31_ultra_token');
      const res = await apiClient.get(`/notifications/?only_unread=true&limit=50`);
      const items = res.data?.items || [];
      const orderTypes = new Set(['order.placed', 'quote.requested', 'quote.accepted', 'quote.rejected']);
      setNewOrdersCount(items.filter(n => orderTypes.has(n.type)).length);
    } catch {
      // silent
    }
  };

  const fetchData = async () => {
    const token = localStorage.getItem('cop31_ultra_token');
    if (!token) { navigate('/giris?tab=ultra'); return; }
    const config = { headers: { Authorization: `Bearer ${token}` } };

    // Promise.allSettled — tek istek failse diğerleri yine de yüklenir
    const [statsRes, tenantsRes, usersRes, pendingRes, changeReqRes] = await Promise.allSettled([
      apiClient.get(`/ultra-admin/stats`, config),
      apiClient.get(`/ultra-admin/tenants?page=1&limit=50&include_archived=true`, config),
      apiClient.get(`/ultra-admin/users`, config),
      apiClient.get(`/ultra-admin/pending-registrations`, config),
      apiClient.get(`/ultra-admin/stand-change-requests`, config),
    ]);

    // 401 kontrolü
    const has401 = [statsRes, tenantsRes, usersRes, pendingRes].some(
      r => r.status === 'rejected' && r.reason?.response?.status === 401
    );
    if (has401) { navigate('/giris?tab=ultra'); return; }

    if (statsRes.status   === 'fulfilled') setStats(statsRes.value.data);
    if (tenantsRes.status === 'fulfilled') setTenants(tenantsRes.value.data.tenants);
    if (usersRes.status   === 'fulfilled') setUsers(usersRes.value.data);
    if (pendingRes.status === 'fulfilled') setPendingRegs(pendingRes.value.data);
    if (changeReqRes.status === 'fulfilled') setStandChangeRequests(changeReqRes.value.data);

    setLoading(false);
  };

  const handleLogout = () => {
    localStorage.removeItem('cop31_ultra_token');
    localStorage.removeItem('cop31_ultra_user');
    navigate('/giris?tab=ultra');
  };

  const handleReviewStandRequest = async (reqId, status) => {
    const token = localStorage.getItem('cop31_ultra_token');
    setReviewLoading(prev => ({ ...prev, [reqId]: status }));
    try {
      const res = await apiClient.put(`/ultra-admin/stand-change-requests/${reqId}`, {
        status,
        admin_note: reviewNote[reqId] || '',
      });
      setStandChangeRequests(prev => prev.map(r => r.id === reqId ? res.data : r));
      toast.success(tr ? (status === 'approved' ? 'Talep onaylandı.' : 'Talep reddedildi.') : (status === 'approved' ? 'Request approved.' : 'Request rejected.'));
    } catch (e) {
      toast.error(tr ? 'Hata oluştu.' : 'An error occurred.');
    } finally {
      setReviewLoading(prev => ({ ...prev, [reqId]: null }));
    }
  };

  const handleProposeAlternatives = async (reqId, selectedStands) => {
    const standsToUse = selectedStands || (alternatives[reqId] || []);
    if (!standsToUse.length) {
      toast.error(tr ? 'En az 1 stand seçiniz.' : 'Select at least 1 stand.');
      return;
    }

    // Item 3 (Faz C) — Single-pick + "direct" mode means admin re-assigns the
    // customer to that exact stand WITHOUT going through the alternatives
    // approval flow. We piggy-back the existing picker UI: pickerMode flag
    // determines which endpoint we hit.
    const direct = pickerMode[reqId] === 'direct';
    if (direct) {
      if (standsToUse.length !== 1) {
        toast.error(tr ? 'Direkt atama için tam 1 stand seçin.' : 'Select exactly 1 stand for direct re-assign.');
        return;
      }
      const s = standsToUse[0];
      setProposeLoading(prev => ({ ...prev, [reqId]: true }));
      try {
        const res = await apiClient.put(`/ultra-admin/stand-change-requests/${reqId}`, {
          status:     'approved',
          admin_note: reviewNote[reqId] || '',
          direct_stand: {
            stand_id:    s.stand_id || s.id,
            stand_no:    s.stand_no || '',
            hall_id:     s.hall_id  || '',
            hall_name:   s.hall_name || '',
            cells:       s.cells || [],
            open_sides:  s.open_sides || [],
            area_sqm:    s.area_sqm || 0,
          },
        });
        setStandChangeRequests(prev => prev.map(r => r.id === reqId ? res.data : r));
        setStandPickerReqId(null);
        setPickerMode(prev => ({ ...prev, [reqId]: null }));
        toast.success(tr ? 'Stand direkt atandı ve onaylandı.' : 'Stand directly assigned and approved.');
      } catch (e) {
        toast.error(tr ? 'Hata: ' + (e.response?.data?.detail || e.message) : 'Error: ' + (e.response?.data?.detail || e.message));
      } finally {
        setProposeLoading(prev => ({ ...prev, [reqId]: false }));
      }
      return;
    }

    const token = localStorage.getItem('cop31_ultra_token');
    setProposeLoading(prev => ({ ...prev, [reqId]: true }));

    // Standları API formatına dönüştür
    const alts = standsToUse.map(s => ({
      id:           s.stand_id || s.id || String(Date.now()),
      stand_id:     s.stand_id || s.id,
      stand_no:     s.stand_no || '',
      hall_id:      s.hall_id  || '',
      hall_name:    s.hall_name || '',
      hall_width:   s.hall_width || 50,
      hall_height:  s.hall_height || 50,
      cells:        s.cells || [],
      open_sides:   s.open_sides || [],
      area_sqm:     s.area_sqm || 0,
      location_note:s.location_note || '',
    }));

    try {
      const res = await apiClient.put(`/ultra-admin/stand-change-requests/${reqId}/propose`, {
        alternatives: alts,
        admin_note:   reviewNote[reqId] || '',
      });
      setStandChangeRequests(prev => prev.map(r => r.id === reqId ? res.data : r));
      setProposeMode(prev => ({ ...prev, [reqId]: false }));
      setAlternatives(prev => ({ ...prev, [reqId]: [] }));
      setStandPickerReqId(null);
      toast.success(tr ? 'Alternatifler müşteriye iletildi!' : 'Alternatives sent to customer!');
    } catch (e) {
      toast.error(tr ? 'Hata: ' + (e.response?.data?.detail || e.message) : 'Error: ' + (e.response?.data?.detail || e.message));
    } finally {
      setProposeLoading(prev => ({ ...prev, [reqId]: false }));
    }
  };

  // Phase 86AT — Delete a Stand Change Request with Ultra Password.
  // Mirrors the password-gated tenant-delete UX: confirmation modal asks for
  // the shared Ultra Password and pipes it to the backend as a query param.
  const handleDeleteChangeRequest = async () => {
    const req = crDeleteModal?.req;
    const pw  = (crDeleteModal?.password || '').trim();
    if (!req || !pw) return;
    setCrDeleteModal(prev => ({ ...prev, loading: true, error: '' }));
    try {
      await apiClient.delete(`/ultra-admin/stand-change-requests/${req.id}`, {
        params: { ultra_password: pw },
      });
      setStandChangeRequests(prev => prev.filter(r => r.id !== req.id));
      setCrDeleteModal(null);
      toast.success(tr ? 'Talep silindi.' : 'Request deleted.');
    } catch (e) {
      const msg = e.response?.data?.detail || e.message;
      setCrDeleteModal(prev => ({ ...prev, loading: false, error: typeof msg === 'string' ? msg : 'Error' }));
    }
  };



  const addAlternative = (reqId) => {
    setAlternatives(prev => {
      const cur = prev[reqId] || [];
      if (cur.length >= 3) return prev;
      return { ...prev, [reqId]: [...cur, { id: Date.now().toString(), stand_no: '', hall: '', area: '', stand_type: '', location_note: '' }] };
    });
  };

  const updateAlt = (reqId, idx, field, value) => {
    setAlternatives(prev => {
      const cur = [...(prev[reqId] || [])];
      cur[idx] = { ...cur[idx], [field]: value };
      return { ...prev, [reqId]: cur };
    });
  };

  const removeAlt = (reqId, idx) => {
    setAlternatives(prev => {
      const cur = [...(prev[reqId] || [])];
      cur.splice(idx, 1);
      return { ...prev, [reqId]: cur };
    });
  };

  const refreshPendingRegs = async () => {
    const token = localStorage.getItem('cop31_ultra_token');
    try {
      const res = await apiClient.get(`/ultra-admin/pending-registrations`);
      setPendingRegs(res.data);
    } catch (_) {}
  };

  const handleApprove = async (regId) => {
    // Defensive against rapid double-click: React state update is async and
    // a quick second click can fire before `disabled` propagates. The ref-based
    // guard is synchronous.
    if (approvalInFlightRef.current.has(regId)) return;
    approvalInFlightRef.current.add(regId);
    const token = localStorage.getItem('cop31_ultra_token');
    setApprovalLoading(prev => ({ ...prev, [regId]: 'approving' }));
    try {
      await apiClient.post(`/ultra-admin/pending-registrations/${regId}/approve`, {});
      toast.success(tr ? 'Başvuru onaylandı!' : 'Application approved!');
      await refreshPendingRegs();
    } catch (e) {
      if (e.response?.status === 401) {
        toast.error(tr ? 'Oturum süresi doldu. Lütfen tekrar giriş yapın.' : 'Session expired. Please login again.');
      } else {
        toast.error(tr ? 'Hata: ' + (e.response?.data?.detail || e.message) : 'Error: ' + (e.response?.data?.detail || e.message));
      }
      // Re-fetch so UNFCCC-lock / other block reasons become visible on the card
      await refreshPendingRegs();
    } finally {
      setApprovalLoading(prev => ({ ...prev, [regId]: null }));
      approvalInFlightRef.current.delete(regId);
    }
  };

  const handleReject = async (regId) => {
    const token = localStorage.getItem('cop31_ultra_token');
    setApprovalLoading(prev => ({ ...prev, [regId]: 'rejecting' }));
    try {
      await apiClient.post(`/ultra-admin/pending-registrations/${regId}/reject`, {});
      toast.success(tr ? 'Başvuru reddedildi.' : 'Application rejected.');
      await refreshPendingRegs();
    } catch (e) {
      if (e.response?.status === 401) {
        toast.error(tr ? 'Oturum süresi doldu. Lütfen tekrar giriş yapın.' : 'Session expired. Please login again.');
      } else {
        toast.error(tr ? 'Hata: ' + (e.response?.data?.detail || e.message) : 'Error: ' + (e.response?.data?.detail || e.message));
      }
    } finally {
      setApprovalLoading(prev => ({ ...prev, [regId]: null }));
    }
  };

  const handleBulkAction = async (action) => {
    if (!selectedRegs.length) return;
    const confirmMsg = action === 'approve'
      ? (tr ? `${selectedRegs.length} başvuruyu onaylamak istediğinizden emin misiniz?` : `Approve ${selectedRegs.length} applications?`)
      : (tr ? `${selectedRegs.length} başvuruyu reddetmek istediğinizden emin misiniz?` : `Reject ${selectedRegs.length} applications?`);
    if (!window.confirm(confirmMsg)) return;
    const token = localStorage.getItem('cop31_ultra_token');
    setBulkLoading(true);
    try {
      const res = await apiClient.post(`/ultra-admin/pending-registrations/bulk-action`,
        { action, ids: selectedRegs }
      );
      const { success_count, failed_count, skipped_count } = res.data;
      toast.success(tr
        ? `Toplu işlem: ${success_count} başarılı${failed_count ? `, ${failed_count} başarısız` : ''}${skipped_count ? `, ${skipped_count} atlandı` : ''}`
        : `Bulk action: ${success_count} success${failed_count ? `, ${failed_count} failed` : ''}${skipped_count ? `, ${skipped_count} skipped` : ''}`
      );
      setSelectedRegs([]);
      await refreshPendingRegs();
    } catch (e) {
      toast.error(tr ? 'Toplu işlem hatası: ' + (e.response?.data?.detail || e.message) : 'Bulk action error: ' + (e.response?.data?.detail || e.message));
    } finally {
      setBulkLoading(false);
    }
  };

  const handleDeleteReg = async (regId) => {    if (!window.confirm(tr ? 'Bu başvuru "Silinmiş" sekmesine taşınacak. Devam edilsin mi?' : 'This application will be moved to "Deleted" tab. Continue?')) return;
    const token = localStorage.getItem('cop31_ultra_token');
    try {
      await apiClient.delete(`/ultra-admin/pending-registrations/${regId}`);
      setPendingRegs(prev => prev.map(r => r.id === regId ? { ...r, status: 'deleted' } : r));
      setSelectedReg(null);
      toast.success(tr ? 'Başvuru silinmiş sekmesine taşındı.' : 'Application moved to deleted tab.');
    } catch (e) {
      if (e.response?.status !== 404) {
        toast.error(tr ? 'Silinemedi: ' + (e.response?.data?.detail || e.message) : 'Delete failed: ' + (e.response?.data?.detail || e.message));
      }
    }
  };

  const handleDeactivateReg = async (regId) => {
    if (!window.confirm(tr ? 'Bu başvuru "Pasif" sekmesine taşınacak. Devam edilsin mi?' : 'This application will be moved to "Passive" tab. Continue?')) return;
    try {
      await apiClient.put(`/ultra-admin/pending-registrations/${regId}/deactivate`, {});
      setPendingRegs(prev => prev.map(r => r.id === regId ? { ...r, status: 'inactive' } : r));
      setSelectedReg(null);
      toast.success(tr ? 'Başvuru pasif sekmesine taşındı.' : 'Application moved to passive tab.');
    } catch (e) {
      toast.error(tr ? 'Pasife alınamadı: ' + (e.response?.data?.detail || e.message) : 'Deactivation failed: ' + (e.response?.data?.detail || e.message));
    }
  };

  // Phase 86AT — Reactivate a previously-passive registration.
  const handleReactivateReg = async (regId) => {
    if (!window.confirm(tr ? 'Bu kayıt yeniden aktife alınsın mı? Kullanıcı tekrar giriş yapabilecek.' : 'Reactivate this entry? The user will be able to sign in again.')) return;
    try {
      await apiClient.put(`/ultra-admin/pending-registrations/${regId}/reactivate`, {});
      setPendingRegs(prev => prev.map(r => r.id === regId ? { ...r, status: 'approved' } : r));
      setSelectedReg(null);
      toast.success(tr ? 'Kayıt aktife alındı.' : 'Entry reactivated.');
    } catch (e) {
      toast.error(tr ? 'Aktifleştirilemedi: ' + (e.response?.data?.detail || e.message) : 'Reactivate failed: ' + (e.response?.data?.detail || e.message));
    }
  };

  const handleArchiveTenant = async (tenantId, tenantName) => {
    if (!window.confirm(tr
      ? `"${tenantName}" tenant'ı arşivlensin mi?\n\nArşivlenince bu şirkete bağlı kullanıcılar giriş yapamaz ama verileri korunur. İstediğiniz zaman arşivden çıkarabilirsiniz.`
      : `Archive tenant "${tenantName}"?\n\nUsers will be unable to log in but data is preserved. Can be un-archived anytime.`)) return;
    const token = localStorage.getItem('cop31_ultra_token');
    try {
      await apiClient.post(`/ultra-admin/tenants/${tenantId}/archive`, {});
      setTenants(prev => prev.map(t => t.id === tenantId ? { ...t, status: 'archived', previous_status: t.status } : t));
      toast.success(tr ? `'${tenantName}' arşivlendi.` : `'${tenantName}' archived.`);
    } catch (e) {
      toast.error(tr ? 'Arşivlenemedi: ' + (e.response?.data?.detail || e.message) : 'Archive failed: ' + (e.response?.data?.detail || e.message));
    }
  };

  const handleUnarchiveTenant = async (tenantId, tenantName) => {
    const token = localStorage.getItem('cop31_ultra_token');
    try {
      const res = await apiClient.post(`/ultra-admin/tenants/${tenantId}/unarchive`, {});
      setTenants(prev => prev.map(t => t.id === tenantId ? { ...t, status: res.data.new_status || 'active' } : t));
      toast.success(tr ? `'${tenantName}' arşivden çıkarıldı.` : `'${tenantName}' un-archived.`);
    } catch (e) {
      toast.error(tr ? 'Hata: ' + (e.response?.data?.detail || e.message) : 'Error: ' + (e.response?.data?.detail || e.message));
    }
  };

  const handleDeleteTenant = async (tenantId, tenantName) => {
    const ultraPassword = window.prompt(tr
      ? `"${tenantName}" tenant'ını KALICI OLARAK silmek üzeresiniz. Bu işlem geri alınamaz.\n\nUltra Admin şifresini girin:`
      : `You are about to PERMANENTLY delete "${tenantName}". This cannot be undone.\n\nEnter Ultra Admin password:`);
    if (!ultraPassword) return;
    const token = localStorage.getItem('cop31_ultra_token');
    try {
      const res = await apiClient.delete(`/ultra-admin/tenants/${tenantId}?ultra_password=${encodeURIComponent(ultraPassword)}`);
      setTenants(prev => prev.filter(t => t.id !== tenantId));
      toast.success(tr ? (res.data.message || 'Tenant silindi.') : 'Tenant deleted.');
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail;
      if (status === 409 && detail && typeof detail === 'object' && detail.blockers) {
        const lines = blockersToBullets(detail.blockers, tr);
        window.alert((tr ? 'Tenant silinemez:\n\n' : 'Tenant cannot be deleted:\n\n') + lines);
      } else if (status === 403) {
        toast.error(tr ? 'Ultra Password hatalı veya yetki yok.' : 'Wrong Ultra Password or no permission.');
      } else {
        toast.error(tr ? 'Hata: ' + (detail || e.message) : 'Error: ' + (detail || e.message));
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin h-12 w-12 border-4 border-emerald-600 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-600">{t('loading_dashboard')}</p>
        </div>
      </div>
    );
  }

  // ─── Application Detail Modal ───────────────────────────────────────────
  const token = localStorage.getItem('cop31_ultra_token');
  // Host-relative URL so it works on any deploy (preview, prod, etc) and
  // ALWAYS hits the same backend the SPA is currently bound to. We append
  // the JWT as a query param because <a href> downloads can't send the
  // Authorization header — backend now accepts ?token= for this exact case.
  // Phase 86AZ.52 — Two URL helpers:
  //   • previewUrl(): renders inline (PDF iframe, image <img>) — default
  //   • downloadUrl(): forces Save-As dialog via ?disposition=attachment
  const previewUrl  = (filename) =>
    `/api/ultra-admin/attachments/${filename}?token=${encodeURIComponent(token || '')}`;
  const downloadUrl = (filename) =>
    `/api/ultra-admin/attachments/${filename}?disposition=attachment&token=${encodeURIComponent(token || '')}`;

  const zoneBadgeModal = (zone) => {
    if (zone === 'blue_zone') return 'bg-blue-100 text-blue-700 border border-blue-300';
    if (zone === 'green_zone') return 'bg-emerald-100 text-emerald-700 border border-emerald-300';
    if (zone === 'supplier') return 'bg-amber-100 text-amber-800 border border-amber-300';
    return 'bg-gray-100 text-gray-600';
  };
  const zoneLabelModal = (zone) => {
    if (zone === 'blue_zone') return 'Blue Zone';
    if (zone === 'green_zone') return 'Green Zone';
    if (zone === 'supplier') return tr ? 'Tedarikçi' : 'Supplier';
    return (zone || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  const AppDetailModal = () => {
    if (!selectedReg) return null;
    const reg = selectedReg;
    const files = reg.attachment_filenames || (reg.attachment_filename ? [reg.attachment_filename] : []);
    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
        onClick={() => setSelectedReg(null)}
      >
        <div
          className="bg-white rounded-3xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
          onClick={e => e.stopPropagation()}
        >
          {/* Modal Header */}
          <div className={`px-6 pt-6 pb-4 flex items-start justify-between border-b border-gray-100`}>
            <div className="flex items-center gap-3">
              <div className={`h-12 w-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                reg.status === 'pending' ? 'bg-orange-100' : reg.status === 'approved' ? 'bg-emerald-100' : 'bg-red-100'
              }`}>
                <Users className={`h-6 w-6 ${reg.status === 'pending' ? 'text-orange-500' : reg.status === 'approved' ? 'text-emerald-600' : 'text-red-500'}`} />
              </div>
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-bold text-gray-900 text-lg">{reg.full_name}</h3>
                  {reg.is_individual && (
                    <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                      {tr ? 'Bireysel' : 'Individual'}
                    </span>
                  )}
                  {reg.zone && (
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${zoneBadgeModal(reg.zone)}`}>
                      {zoneLabelModal(reg.zone)}
                    </span>
                  )}
                </div>
                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                  reg.status === 'pending' ? 'bg-orange-100 text-orange-700' : reg.status === 'approved' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
                }`}>
                  {reg.status === 'pending' ? (tr ? 'Bekliyor' : 'Pending') : reg.status === 'approved' ? (tr ? 'Onaylandı' : 'Approved') : (tr ? 'Reddedildi' : 'Rejected')}
                </span>
              </div>
            </div>
            <button onClick={() => setSelectedReg(null)} className="text-gray-400 hover:text-gray-600 transition-colors ml-2 mt-1">
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Modal Body */}
          <div className="px-6 py-5 space-y-4">
            {/* Contact */}
            <div className="space-y-2">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">{tr ? 'İletişim Bilgileri' : 'Contact Info'}</p>
              <div className="grid grid-cols-1 gap-2">
                <div className="flex items-center gap-2 text-sm text-gray-700">
                  <Mail className="h-4 w-4 text-gray-400 shrink-0" />
                  <span>{reg.email}</span>
                </div>
                {reg.phone && (
                  <div className="flex items-center gap-2 text-sm text-gray-700">
                    <Phone className="h-4 w-4 text-gray-400 shrink-0" />
                    <span>{reg.phone}</span>
                  </div>
                )}
                {(reg.country || reg.city) && (
                  <div className="flex items-center gap-2 text-sm text-gray-700">
                    <MapPin className="h-4 w-4 text-gray-400 shrink-0" />
                    <span>{[reg.city, reg.country].filter(Boolean).join(', ')}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Company / Role */}
            {(!reg.is_individual) && (reg.company || reg.organization || reg.title) && (
              <div className="space-y-2">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">{tr ? 'Firma & Görev' : 'Company & Role'}</p>
                <div className="grid grid-cols-1 gap-2">
                  {(reg.company || reg.organization) && (
                    <div className="flex items-center gap-2 text-sm text-gray-700">
                      <Building2 className="h-4 w-4 text-gray-400 shrink-0" />
                      <span>{reg.company || reg.organization}</span>
                    </div>
                  )}
                  {reg.title && (
                    <div className="flex items-center gap-2 text-sm text-gray-700">
                      <Briefcase className="h-4 w-4 text-gray-400 shrink-0" />
                      <span>{reg.title}</span>
                    </div>
                  )}
                  {/* Phase 86AZ.50 — Website link (contractor form Step 1) */}
                  {reg.website && (
                    <div className="flex items-center gap-2 text-sm text-gray-700">
                      <Globe className="h-4 w-4 text-gray-400 shrink-0" />
                      <a href={/^https?:\/\//i.test(reg.website) ? reg.website : `https://${reg.website}`}
                         target="_blank" rel="noopener noreferrer"
                         className="text-orange-600 hover:underline truncate" data-testid="reg-website-link">
                        {reg.website}
                      </a>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Phase 86AZ.50 — About Us (contractor self-description) */}
            {reg.about_us && (
              <div className="space-y-2" data-testid="reg-about-us">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">{tr ? 'Hakkımızda' : 'About Us'}</p>
                <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed bg-gray-50 border border-gray-100 rounded-xl p-3">
                  {reg.about_us}
                </p>
              </div>
            )}

            {/* Supplier Category */}
            {reg.supplier_category_name && (
              <div className="space-y-2">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">{tr ? 'Tedarikçi Kategorisi' : 'Supplier Category'}</p>
                <div className="flex items-center gap-2">
                  <span className="px-3 py-1 bg-amber-100 text-amber-800 rounded-full text-sm font-medium">
                    {reg.supplier_category_name}
                  </span>
                  {reg.supplier_sub_category_name && (
                    <span className="px-3 py-1 bg-amber-50 text-amber-700 rounded-full text-sm border border-amber-200">
                      ↳ {reg.supplier_sub_category_name}
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Dates */}
            <div className="space-y-1.5">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">{tr ? 'Tarihler' : 'Dates'}</p>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Calendar className="h-4 w-4 text-gray-400 shrink-0" />
                <span>{tr ? 'Başvuru:' : 'Applied:'} {new Date(reg.created_at).toLocaleString()}</span>
              </div>
              {reg.reviewed_at && (
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <CheckCircle className="h-4 w-4 text-gray-400 shrink-0" />
                  <span>{tr ? 'İncelendi:' : 'Reviewed:'} {new Date(reg.reviewed_at).toLocaleString()}</span>
                </div>
              )}
            </div>

            {/* Attachments — thumbnail grid + lightbox carousel */}
            {files.length > 0 && (() => {
              // Phase 86AZ.25 — Ported the contractor-applications style:
              // 3-col thumbnail grid (image inline, doc icon for the rest),
              // click opens a full-screen lightbox with prev/next nav.
              const isImage = fn => /\.(jpe?g|png|webp|gif|bmp|heic|heif)$/i.test(fn);
              return (
                <div className="space-y-2" data-testid="attachments-block">
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
                    {tr ? 'Ekli Dosyalar' : 'Attached Files'} ({files.length})
                  </p>
                  <div className="grid grid-cols-3 gap-2">
                    {files.map((fname, idx) => (
                      <button
                        key={fname}
                        type="button"
                        onClick={() => setAttPreviewIdx(idx)}
                        data-testid={`attachment-${idx}`}
                        className="group relative block aspect-square rounded-xl overflow-hidden border border-gray-200 bg-gray-50 hover:border-emerald-400 hover:shadow-md transition-all"
                        title={tr ? `Dosya ${idx + 1} — önizlemek için tıkla` : `File ${idx + 1} — click to preview`}
                      >
                        {isImage(fname) ? (
                          <img
                            src={previewUrl(fname)}
                            alt={`Ek ${idx + 1}`}
                            loading="lazy"
                            className="w-full h-full object-cover"
                            onError={e => { e.target.style.display = 'none'; }}
                          />
                        ) : (
                          <div className="w-full h-full flex flex-col items-center justify-center gap-1 p-2">
                            <FileText className={`w-7 h-7 ${/\.pdf$/i.test(fname) ? 'text-red-500' : 'text-gray-400'}`} />
                            <span className={`text-[9px] font-mono uppercase ${/\.pdf$/i.test(fname) ? 'text-red-600 font-bold' : 'text-gray-500'}`}>
                              {fname.substring(fname.lastIndexOf('.') + 1)}
                            </span>
                          </div>
                        )}
                        <span className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent text-white text-[10px] py-1 px-1.5 opacity-0 group-hover:opacity-100 transition-opacity text-center font-semibold">
                          {tr ? 'Önizle' : 'Preview'}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              );
            })()}
          </div>

          {/* Modal Footer — Action buttons */}
          {reg.status === 'pending' && (
            <div className="px-6 pb-6 flex gap-3">
              <Button
                className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl"
                onClick={() => { handleApprove(reg.id); setSelectedReg(null); }}
                disabled={!!approvalLoading[reg.id]}
              >
                <CheckCircle className="h-4 w-4 mr-2" />{tr ? 'Onayla' : 'Approve'}
              </Button>
              <Button
                variant="outline"
                className="flex-1 border-red-300 text-red-600 hover:bg-red-50 rounded-xl"
                onClick={() => { handleReject(reg.id); setSelectedReg(null); }}
                disabled={!!approvalLoading[reg.id]}
              >
                <XCircle className="h-4 w-4 mr-2" />{tr ? 'Reddet' : 'Reject'}
              </Button>
            </div>
          )}
          {(reg.status === 'approved' || reg.status === 'rejected') && (
            <div className="px-6 pb-6">
              <Button
                variant="outline"
                className="w-full border-red-300 text-red-600 hover:bg-red-50 rounded-xl"
                onClick={() => handleDeleteReg(reg.id)}
              >
                <Trash2 className="h-4 w-4 mr-2" />{tr ? 'Başvuruyu Sil' : 'Delete Application'}
              </Button>
            </div>
          )}
        </div>

        {/* Phase 86AZ.25 — Attachment lightbox carousel.
            Sits inside AppDetailModal so closing the outer modal also
            closes the lightbox. Image inline, PDF in iframe, others fall
            back to a download CTA. */}
        {attPreviewIdx >= 0 && files[attPreviewIdx] && (() => {
          const fn = files[attPreviewIdx];
          const inlineUrl = previewUrl(fn);
          const dlUrl = downloadUrl(fn);
          const ext = fn.substring(fn.lastIndexOf('.') + 1).toLowerCase();
          const isImage = ['jpg','jpeg','png','webp','gif','bmp','heic','heif'].includes(ext);
          const isPdf   = ext === 'pdf';
          return (
            <div
              className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center"
              onClick={(e) => { e.stopPropagation(); setAttPreviewIdx(-1); }}
              data-testid="attachment-preview-modal"
            >
              <div className="absolute top-0 left-0 right-0 px-5 py-3 flex items-center justify-between text-white bg-gradient-to-b from-black/60 to-transparent">
                <div className="flex items-center gap-3 min-w-0">
                  <span className="text-sm font-bold">{attPreviewIdx + 1} / {files.length}</span>
                  <span className="text-xs text-white/70 truncate">{fn}</span>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <a href={dlUrl} download target="_blank" rel="noopener noreferrer"
                    onClick={e => e.stopPropagation()}
                    data-testid="preview-download-btn"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 text-xs font-semibold">
                    <Download className="w-3.5 h-3.5" /> {tr ? 'İndir' : 'Download'}
                  </a>
                  <button type="button"
                    onClick={(e) => { e.stopPropagation(); setAttPreviewIdx(-1); }}
                    data-testid="preview-close-btn"
                    className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-white/10 hover:bg-white/20">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
              {files.length > 1 && (
                <button type="button"
                  onClick={(e) => { e.stopPropagation(); setAttPreviewIdx((attPreviewIdx - 1 + files.length) % files.length); }}
                  data-testid="preview-prev-btn"
                  className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center">
                  <ChevronLeft className="w-6 h-6" />
                </button>
              )}
              <div className="max-w-[90vw] max-h-[85vh] w-full h-full flex items-center justify-center px-16" onClick={e => e.stopPropagation()}>
                {isImage ? (
                  <img src={inlineUrl} alt={fn} className="max-w-full max-h-full object-contain rounded-lg shadow-2xl" />
                ) : isPdf ? (
                  <iframe src={inlineUrl} title={fn} className="w-full h-full bg-white rounded-lg shadow-2xl" />
                ) : (
                  <div className="bg-white rounded-2xl p-10 text-center max-w-md shadow-2xl">
                    <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p className="font-bold text-gray-800 break-all mb-1">{fn}</p>
                    <p className="text-xs text-gray-500 uppercase font-mono mb-5">{ext} {tr ? 'dosyası — tarayıcıda önizlenemez' : 'file — cannot preview in browser'}</p>
                    <a href={dlUrl} download target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold">
                      <Download className="w-4 h-4" /> {tr ? 'İndir' : 'Download'}
                    </a>
                  </div>
                )}
              </div>
              {files.length > 1 && (
                <button type="button"
                  onClick={(e) => { e.stopPropagation(); setAttPreviewIdx((attPreviewIdx + 1) % files.length); }}
                  data-testid="preview-next-btn"
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center">
                  <ChevronRight className="w-6 h-6" />
                </button>
              )}
            </div>
          );
        })()}
      </div>
    );
  };

  return (
    <>
    <div className="min-h-screen bg-gray-50 flex">
      <AppDetailModal />
      <AdminSidebar />
      
      <div className="flex-1">
        <nav className="bg-white border-b border-gray-200 sticky top-0 z-40">
          <div className="px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <div>
                {(() => {
                  // Phase 86R — header title reflects the active sub-route so
                  // sub-pages don't all read "Panel / Welcome back".
                  const titleKey = activeTab === 'pending'         ? (language === 'tr' ? 'Başvurular'        : 'Applications')
                                : activeTab === 'stand-requests'   ? (language === 'tr' ? 'Değişiklik Talepleri' : 'Change Requests')
                                : activeTab === 'stand-designs'    ? (language === 'tr' ? 'Stand Tasarımları'   : 'Stand Designs')
                                : activeTab === 'isg'              ? (language === 'tr' ? 'ISG'                  : 'ISG')
                                : activeTab === 'activity'         ? (language === 'tr' ? 'Etkinlik'             : 'Activity')
                                : t('dashboard');
                  const subKey   = ['overview','tenants','users'].includes(activeTab) ? t('welcome_back')
                                 : (language === 'tr' ? 'Yönetim çalışma alanı' : 'Management workspace');
                  return (
                    <>
                      <h1 className="text-xl font-bold text-gray-900">{titleKey}</h1>
                      <p className="text-xs text-gray-500">{subKey}</p>
                    </>
                  );
                })()}
              </div>
              <div className="flex items-center gap-4">
                <NotificationBell language={language || 'tr'} variant="dark" />
                <LanguageToggle variant="admin" />
                <Button onClick={handleLogout} variant="outline" className="rounded-full">
                  <LogOut className="h-4 w-4 mr-2" />
                  {t('logout')}
                </Button>
              </div>
            </div>
          </div>
        </nav>

        <div className="px-4 sm:px-6 lg:px-8 py-8">
        {/* Phase 86R — Page intro is route-aware:
              overview/tenants/users → dashboard intro
              pending                → applications intro
              stand-requests (deep)  → change-requests intro
              stand-designs          → stand-designs intro
            KPI cards & 3-tab Overview/Tenants/Users header are only shown for
            the actual dashboard view; sub-routes (Başvurular / Change
            Requests / Stand Designs) are full-page workspaces, not dashboard
            sub-tabs. */}
        {(() => {
          const introKey = activeTab === 'pending'         ? 'applications'
                         : activeTab === 'stand-requests'  ? 'change-requests'
                         : activeTab === 'stand-designs'   ? 'stand-designs'
                         : activeTab === 'activity'        ? 'audit-logs'
                         : 'dashboard';
          const intro = getPageIntro(introKey, language);
          return intro ? <PageIntro {...intro} /> : null;
        })()}
        {(['overview','tenants','users'].includes(activeTab)) && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card
            data-testid="kpi-card-tenants"
            onClick={() => navigate('/ultra-admin/tenants')}
            className="border-0 shadow-lg rounded-2xl cursor-pointer hover:shadow-xl hover:-translate-y-0.5 transition-all active:scale-[0.98]"
            role="button" tabIndex={0}
            onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && navigate('/ultra-admin/tenants')}
          >
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 mb-1">{t('total_tenants')}</p>
                  <p className="text-3xl font-bold text-gray-900">{stats?.total_tenants || 0}</p>
                  <p className="text-xs text-emerald-600 mt-1">{stats?.active_tenants || 0} {t('active')}</p>
                </div>
                <div className="h-12 w-12 rounded-full bg-emerald-100 flex items-center justify-center">
                  <Building2 className="h-6 w-6 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card
            data-testid="kpi-card-users"
            onClick={() => navigate('/ultra-admin/users')}
            className="border-0 shadow-lg rounded-2xl cursor-pointer hover:shadow-xl hover:-translate-y-0.5 transition-all active:scale-[0.98]"
            role="button" tabIndex={0}
            onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && navigate('/ultra-admin/users')}
          >
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 mb-1">{t('total_users')}</p>
                  <p className="text-3xl font-bold text-gray-900">{stats?.total_users || 0}</p>
                  <p className="text-xs text-gray-600 mt-1">{t('across_all')}</p>
                </div>
                <div className="h-12 w-12 rounded-full bg-sky-100 flex items-center justify-center">
                  <Users className="h-6 w-6 text-sky-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card
            data-testid="kpi-card-products"
            onClick={() => navigate('/ultra-admin/platform-products')}
            className="border-0 shadow-lg rounded-2xl cursor-pointer hover:shadow-xl hover:-translate-y-0.5 transition-all active:scale-[0.98]"
            role="button" tabIndex={0}
            onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && navigate('/ultra-admin/platform-products')}
          >
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 mb-1">{t('total_products')}</p>
                  <p className="text-3xl font-bold text-gray-900">{stats?.total_products || 0}</p>
                  <p className="text-xs text-gray-600 mt-1">{t('from_suppliers')}</p>
                </div>
                <div className="h-12 w-12 rounded-full bg-purple-100 flex items-center justify-center">
                  <Package className="h-6 w-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card
            data-testid="kpi-card-orders"
            onClick={() => navigate('/ultra-admin/sales')}
            role="button" tabIndex={0}
            onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && navigate('/ultra-admin/sales')}
            className={`border-0 shadow-lg rounded-2xl cursor-pointer hover:shadow-xl hover:-translate-y-0.5 transition-all active:scale-[0.98] relative ${newOrdersCount > 0 ? 'ring-2 ring-orange-400 ring-offset-2' : ''}`}
          >
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 mb-1">{t('total_orders')}</p>
                  <p className="text-3xl font-bold text-gray-900">{stats?.total_orders || 0}</p>
                  <p className="text-xs text-emerald-600 mt-1">{stats?.recent_orders || 0} {t('this_week')}</p>
                </div>
                <div className="h-12 w-12 rounded-full bg-orange-100 flex items-center justify-center relative">
                  <ShoppingCart className="h-6 w-6 text-orange-600" />
                  {newOrdersCount > 0 && (
                    <span
                      data-testid="dashboard-new-orders-badge"
                      className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[20px] h-[20px] px-1 flex items-center justify-center animate-pulse shadow-md"
                    >
                      {newOrdersCount > 99 ? '99+' : newOrdersCount}
                    </span>
                  )}
                </div>
              </div>
              {newOrdersCount > 0 && (
                <div className="mt-3 flex items-center gap-1.5 text-[11px] font-semibold text-orange-600 bg-orange-50 rounded-full px-2.5 py-1 w-fit">
                  <span className="h-1.5 w-1.5 rounded-full bg-orange-500 animate-pulse" />
                  {language === 'tr' ? `${newOrdersCount} yeni sipariş` : `${newOrdersCount} new order${newOrdersCount === 1 ? '' : 's'}`}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
        )}

        <Card className="border-0 shadow-lg rounded-2xl">
          <CardContent className="p-6">
            <Tabs value={activeTab} onValueChange={goTab}>
              {/* Phase 82: Yalnızca Overview / Tenants / Users sekmeleri başlıkta
                  kalır. "Applications", "Change Requests", "Stand Designs"
                  yan menüye; "Activity" ve "Settings" (Business Hours) Sistem
                  Ayarları altına taşındı — TabsContent'leri deep-link için
                  olduğu gibi tutuyoruz.
                  Phase 86R: TabsList sadece bu üç ana sekme için görünür;
                  alt-route sayfalar (Başvurular / Change Requests / vs)
                  artık tam-sayfa workspace, dashboard sub-tab değil. */}
              {(['overview','tenants','users'].includes(activeTab)) && (
                <TabsList className="grid w-full grid-cols-3 mb-6">
                  <TabsTrigger value="overview">{t('overview')}</TabsTrigger>
                  <TabsTrigger value="tenants">{t('tenants')}</TabsTrigger>
                  <TabsTrigger value="users">{t('users')}</TabsTrigger>
                </TabsList>
              )}

              <TabsContent value="overview" className="space-y-4">
                <h3 className="text-2xl font-bold mb-4">{t('system_overview')}</h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-emerald-50 rounded-2xl p-6">
                    <h4 className="text-lg font-semibold text-emerald-900 mb-3">{t('customer_tenants')}</h4>
                    <p className="text-4xl font-bold text-emerald-600 mb-2">{stats?.total_customers || 0}</p>
                    <p className="text-sm text-gray-600">{t('orgs_placing')}</p>
                  </div>
                  <div className="bg-sky-50 rounded-2xl p-6">
                    <h4 className="text-lg font-semibold text-sky-900 mb-3">{t('supplier_tenants')}</h4>
                    <p className="text-4xl font-bold text-sky-600 mb-2">{stats?.total_suppliers || 0}</p>
                    <p className="text-sm text-gray-600">{t('orgs_providing')}</p>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="tenants" className="space-y-4">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-2xl font-bold">{t('tenant_management')}</h3>
                  <Button className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full">
                    <Plus className="h-4 w-4 mr-2" />
                    {t('add_tenant')}
                  </Button>
                </div>

                {/* Durum tabları */}
                {(() => {
                  const STATUS_TABS = [
                    { key: 'all',      label: tr ? 'Tümü'      : 'All',      activeCls: 'bg-gray-800 text-white'    },
                    { key: 'active',   label: tr ? 'Aktif'     : 'Active',   activeCls: 'bg-emerald-600 text-white' },
                    { key: 'pending',  label: tr ? 'Beklemede' : 'Pending',  activeCls: 'bg-amber-500 text-white'   },
                    { key: 'inactive', label: tr ? 'Pasif'     : 'Inactive', activeCls: 'bg-red-500 text-white'     },
                    { key: 'archived', label: tr ? 'Arşivli'   : 'Archived', activeCls: 'bg-zinc-600 text-white'    },
                  ];

                  const ZONE_TABS = [
                    { key: 'all',        label: tr ? 'Tüm Zonlar' : 'All Zones', activeCls: 'bg-gray-700 text-white'    },
                    { key: 'blue_zone',  label: 'Blue Zone',                      activeCls: 'bg-blue-600 text-white'    },
                    { key: 'green_zone', label: 'Green Zone',                      activeCls: 'bg-emerald-600 text-white' },
                    { key: 'supplier',   label: tr ? 'Tedarikçi' : 'Supplier',    activeCls: 'bg-amber-500 text-white'   },
                    { key: 'contractor', label: tr ? 'Yüklenici'  : 'Contractor',  activeCls: 'bg-orange-500 text-white'  },
                  ];

                  const filteredTenants = tenants.filter(tn => {
                    const st = tn.status || 'active';
                    // Default "all" tab hides archived; explicit "archived" tab shows only archived
                    if (tenantStatusTab === 'all'      && st === 'archived') return false;
                    if (tenantStatusTab !== 'all'      && st !== tenantStatusTab) return false;
                    if (tenantZoneTab !== 'all' && (tn.zone || tn.company_type) !== tenantZoneTab) return false;
                    return true;
                  });

                  return (
                    <>
                      {/* Satır 1: Durum tabları */}
                      <div className="flex gap-2 flex-wrap mb-2">
                        {STATUS_TABS.map(({ key, label, activeCls }) => {
                          const cnt =
                            key === 'all'     ? tenants.filter(t => (t.status || 'active') !== 'archived').length
                          : tenants.filter(t => (t.status || 'active') === key).length;
                          return (
                            <button
                              key={key}
                              onClick={() => setTenantStatusTab(key)}
                              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                                tenantStatusTab === key ? activeCls : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                              }`}
                            >
                              {label}
                              <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${
                                tenantStatusTab === key ? 'bg-white/20' : 'bg-gray-200 text-gray-500'
                              }`}>{cnt}</span>
                            </button>
                          );
                        })}
                      </div>

                      {/* Satır 2: Zone tabları */}
                      <div className="flex gap-2 flex-wrap mb-4">
                        {ZONE_TABS.map(({ key, label, activeCls }) => {
                          const cnt = key === 'all' ? tenants.length : tenants.filter(t => (t.zone || t.company_type) === key).length;
                          return (
                            <button
                              key={key}
                              onClick={() => setTenantZoneTab(key)}
                              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                                tenantZoneTab === key ? activeCls : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                              }`}
                            >
                              {label}
                              {key !== 'all' && (
                                <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${
                                  tenantZoneTab === key ? 'bg-white/20' : 'bg-gray-200 text-gray-500'
                                }`}>{cnt}</span>
                              )}
                            </button>
                          );
                        })}
                      </div>

                      {/* Tenant listesi */}
                      <div className="space-y-3">
                        {filteredTenants.length === 0 ? (
                          <p className="text-center text-gray-500 py-8">{t('no_tenants')}</p>
                        ) : (
                          filteredTenants.map((tenant) => {
                            const type = tenant.company_type || tenant.zone || '';
                            const iconBg   = type === 'supplier'   ? 'bg-amber-100'   : type === 'green_zone' ? 'bg-emerald-100' : 'bg-blue-100';
                            const iconClr  = type === 'supplier'   ? 'text-amber-600' : type === 'green_zone' ? 'text-emerald-600' : 'text-blue-600';
                            const badgeCls = type === 'supplier'   ? 'bg-amber-100 text-amber-800 border border-amber-300'
                                           : type === 'green_zone' ? 'bg-emerald-100 text-emerald-700 border border-emerald-300'
                                           :                         'bg-blue-100 text-blue-700 border border-blue-300';
                            const zoneLabel = type === 'supplier'   ? (tr ? 'Tedarikçi' : 'Supplier')
                                            : type === 'green_zone' ? 'Green Zone'
                                            :                         'Blue Zone';
                            const statusCls = (tenant.status || 'active') === 'active'   ? 'bg-green-100 text-green-700 border border-green-300'
                                            : (tenant.status || '') === 'pending'         ? 'bg-amber-100 text-amber-700 border border-amber-300'
                                            : (tenant.status || '') === 'archived'        ? 'bg-zinc-100 text-zinc-700 border border-zinc-300'
                                            :                                               'bg-gray-100 text-gray-600 border border-gray-200';
                            const isArchived      = tenant.status === 'archived';
                            const isPlatformTenant = tenant.id === 'summitflow-platform';
                            return (
                              <div key={tenant.id} className={`bg-white border rounded-2xl p-4 transition-shadow ${isArchived ? 'border-zinc-300 bg-zinc-50 opacity-80' : 'border-gray-200 hover:shadow-md'}`} data-testid={`tenant-card-${tenant.id}`}>
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-4">
                                    <div className={`h-12 w-12 rounded-full flex items-center justify-center ${iconBg}`}>
                                      <Building2 className={`h-6 w-6 ${iconClr}`} />
                                    </div>
                                    <div>
                                      <h4 className="font-semibold text-gray-900">{tenant.company_name}</h4>
                                      <p className="text-sm text-gray-500">{tenant.email}</p>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <Badge className={`rounded-full font-semibold ${badgeCls}`}>{zoneLabel}</Badge>
                                    <Badge className={`rounded-full font-semibold ${statusCls}`}>
                                      {tenant.status === 'active'   ? (tr ? 'Aktif'     : 'Active')
                                     : tenant.status === 'pending'  ? (tr ? 'Beklemede' : 'Pending')
                                     : tenant.status === 'archived' ? (tr ? 'Arşivli'   : 'Archived')
                                     :                                (tr ? 'Pasif'     : 'Inactive')}
                                    </Badge>

                                    {/* Platform tenant cannot be archived or deleted */}
                                    {!isPlatformTenant && (
                                      <>
                                        {isArchived ? (
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            className="rounded-full text-emerald-600 hover:bg-emerald-50"
                                            onClick={() => handleUnarchiveTenant(tenant.id, tenant.company_name)}
                                            title={tr ? 'Arşivden Çıkar' : 'Un-archive'}
                                            data-testid={`tenant-unarchive-btn-${tenant.id}`}
                                          >
                                            {tr ? 'Arşivden Çıkar' : 'Un-archive'}
                                          </Button>
                                        ) : (
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            className="rounded-full text-zinc-600 hover:bg-zinc-100"
                                            onClick={() => handleArchiveTenant(tenant.id, tenant.company_name)}
                                            title={tr ? 'Arşivle' : 'Archive'}
                                            data-testid={`tenant-archive-btn-${tenant.id}`}
                                          >
                                            {tr ? 'Arşivle' : 'Archive'}
                                          </Button>
                                        )}
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          className="rounded-full text-red-600 hover:bg-red-50"
                                          onClick={() => handleDeleteTenant(tenant.id, tenant.company_name)}
                                          title={tr ? 'Kalıcı Sil (Ultra Password gerekir)' : 'Permanent Delete (Ultra Password required)'}
                                          data-testid={`tenant-delete-btn-${tenant.id}`}
                                        >
                                          <Trash2 className="h-4 w-4" />
                                        </Button>
                                      </>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })
                        )}
                      </div>
                    </>
                  );
                })()}
              </TabsContent>

              <TabsContent value="users" className="space-y-4">
                {(() => {
                  const tr = language === 'tr';

                  const ROLE_TABS = [
                    { key: 'all',               label: tr ? 'Tümü'          : 'All',            color: 'slate',  activeCls: 'bg-slate-800 text-white'   },
                    { key: 'ultra_admin',        label: 'Ultra Admin',                            color: 'slate',  activeCls: 'bg-slate-800 text-white'   },
                    { key: 'customer_admin',     label: tr ? 'Müşteri Admin' : 'Customer Admin',  color: 'blue',   activeCls: 'bg-blue-600 text-white'    },
                    { key: 'supplier_admin',     label: tr ? 'Tedarikçi'     : 'Supplier',        color: 'amber',  activeCls: 'bg-amber-500 text-white'   },
                    { key: 'contractor_admin',   label: tr ? 'Yüklenici'     : 'Contractor',      color: 'orange', activeCls: 'bg-orange-500 text-white'  },
                    { key: 'sub_user',           label: tr ? 'Alt Kullanıcı' : 'Sub User',        color: 'purple', activeCls: 'bg-purple-600 text-white'  },
                  ];

                  const roleBadgeCls = (role) => ({
                    ultra_admin:       'bg-slate-800 text-white',
                    customer_admin:    'bg-blue-100 text-blue-700 border border-blue-200',
                    supplier_admin:    'bg-amber-100 text-amber-800 border border-amber-200',
                    contractor_admin:  'bg-orange-100 text-orange-700 border border-orange-200',
                    sub_user:          'bg-purple-100 text-purple-700 border border-purple-200',
                  }[role] || 'bg-gray-100 text-gray-600');

                  const roleLabel = (role) => ({
                    ultra_admin:       'Ultra Admin',
                    customer_admin:    tr ? 'Müşteri Admin'  : 'Customer Admin',
                    supplier_admin:    tr ? 'Tedarikçi'      : 'Supplier',
                    contractor_admin:  tr ? 'Yüklenici'      : 'Contractor',
                    sub_user:          tr ? 'Alt Kullanıcı'  : 'Sub User',
                  }[role] || role);

                  const filtered = users.filter(u => {
                    const matchRole = userRoleTab === 'all' || u.role === userRoleTab;
                    const q = userSearch.toLowerCase();
                    const matchSearch = !q || (u.full_name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q);
                    return matchRole && matchSearch;
                  });

                  return (
                    <>
                      {/* Header */}
                      <div className="flex justify-between items-center">
                        <h3 className="text-2xl font-bold">{t('user_management')}</h3>
                        <Button className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full">
                          <Plus className="h-4 w-4 mr-2" />{t('add_user')}
                        </Button>
                      </div>

                      {/* Role sub-tabs */}
                      <div className="flex flex-wrap gap-2">
                        {ROLE_TABS.map(tab => {
                          const cnt = tab.key === 'all' ? users.length : users.filter(u => u.role === tab.key).length;
                          const isActive = userRoleTab === tab.key;
                          return (
                            <button
                              key={tab.key}
                              onClick={() => { setUserRoleTab(tab.key); setUserSearch(''); }}
                              className={`flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-medium transition-all ${
                                isActive ? tab.activeCls : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                              }`}
                            >
                              {tab.label}
                              <span className={`text-xs px-1.5 py-0.5 rounded-full font-bold ${
                                isActive ? 'bg-white/25 text-white' : 'bg-gray-200 text-gray-500'
                              }`}>{cnt}</span>
                            </button>
                          );
                        })}
                      </div>

                      {/* Search */}
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input
                          type="text"
                          value={userSearch}
                          onChange={e => setUserSearch(e.target.value)}
                          placeholder={tr ? 'Ad veya e-posta ara...' : 'Search by name or email...'}
                          className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-gray-300 bg-white"
                        />
                      </div>

                      {/* User list */}
                      <div className="space-y-2">
                        {filtered.length === 0 ? (
                          <p className="text-center text-gray-400 py-10 text-sm">
                            {userSearch ? (tr ? 'Sonuç bulunamadı.' : 'No results found.') : (tr ? 'Bu tipte kullanıcı yok.' : 'No users of this type.')}
                          </p>
                        ) : filtered.map(user => (
                          <div key={user.id} className="bg-white border border-gray-100 rounded-2xl p-4 hover:shadow-sm transition-shadow flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className={`h-10 w-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                                user.role === 'ultra_admin'    ? 'bg-slate-100' :
                                user.role === 'supplier_admin' ? 'bg-amber-100' :
                                user.role === 'sub_user'       ? 'bg-purple-100' : 'bg-blue-100'
                              }`}>
                                <Users className={`h-5 w-5 ${
                                  user.role === 'ultra_admin'    ? 'text-slate-600' :
                                  user.role === 'supplier_admin' ? 'text-amber-600' :
                                  user.role === 'sub_user'       ? 'text-purple-600' : 'text-blue-600'
                                }`} />
                              </div>
                              <div className="min-w-0">
                                <p className="font-semibold text-gray-900 text-sm leading-tight">{user.full_name || '—'}</p>
                                <p className="text-xs text-gray-400 truncate max-w-xs">{user.email}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <Badge className={`rounded-full font-semibold text-xs ${roleBadgeCls(user.role)}`}>
                                {roleLabel(user.role)}
                              </Badge>
                              <Button variant="ghost" size="sm" className="rounded-full h-8 w-8 p-0">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  );
                })()}
              </TabsContent>

              {/* ── Applications Tab ── */}
              <TabsContent value="pending" className="space-y-4">
                {/* Sub-tabs: Pending / Approved / Rejected / Passive / Deleted */}
                {(() => {
                  const tr = language === 'tr';
                  const pendingList  = pendingRegs.filter(r => r.status === 'pending');
                  const approvedList = pendingRegs.filter(r => r.status === 'approved');
                  const rejectedList = pendingRegs.filter(r => r.status === 'rejected');
                  const inactiveList = pendingRegs.filter(r => r.status === 'inactive');
                  const deletedList  = pendingRegs.filter(r => r.status === 'deleted');

                  const subTabs = [
                    { key: 'pending',  label: tr ? 'Bekleyen'    : 'Pending',  count: pendingList.length,  activeCls: 'bg-orange-500 text-white',  cntCls: 'bg-orange-100 text-orange-700' },
                    { key: 'approved', label: tr ? 'Onaylanan'   : 'Approved', count: approvedList.length, activeCls: 'bg-emerald-600 text-white', cntCls: 'bg-emerald-100 text-emerald-700' },
                    { key: 'rejected', label: tr ? 'Reddedilen'  : 'Rejected', count: rejectedList.length, activeCls: 'bg-red-500 text-white',     cntCls: 'bg-red-100 text-red-700' },
                    { key: 'inactive', label: tr ? 'Pasif'       : 'Passive',  count: inactiveList.length, activeCls: 'bg-gray-500 text-white',    cntCls: 'bg-gray-100 text-gray-600' },
                    { key: 'deleted',  label: tr ? 'Silinmiş'    : 'Deleted',  count: deletedList.length,  activeCls: 'bg-zinc-700 text-white',    cntCls: 'bg-zinc-100 text-zinc-600' },
                  ];

                  const zoneBadge = (zone) => ({
                    blue_zone:  'bg-blue-100 text-blue-700 border border-blue-300',
                    green_zone: 'bg-emerald-100 text-emerald-700 border border-emerald-300',
                    supplier:   'bg-amber-100 text-amber-800 border border-amber-300',
                  }[zone] || 'bg-gray-100 text-gray-600');

                  const zoneLabel = (zone) => ({
                    blue_zone:  tr ? 'Blue Zone' : 'Blue Zone',
                    green_zone: tr ? 'Green Zone' : 'Green Zone',
                    supplier:   tr ? 'Tedarikçi' : 'Supplier',
                  }[zone] || (zone || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()));

                  const activeList =
                    appTab === 'pending'  ? pendingList  :
                    appTab === 'approved' ? approvedList :
                    appTab === 'rejected' ? rejectedList :
                    appTab === 'inactive' ? inactiveList :
                    deletedList;

                  // Turkish-safe case-insensitive search normalization
                  const norm = (s) => (s || '').toLowerCase()
                    .replace(/İ/g, 'i').replace(/I/g, 'i').replace(/ı/g, 'i')
                    .replace(/Ş/g, 's').replace(/ş/g, 's')
                    .replace(/Ç/g, 'c').replace(/ç/g, 'c')
                    .replace(/Ğ/g, 'g').replace(/ğ/g, 'g')
                    .replace(/Ö/g, 'o').replace(/ö/g, 'o')
                    .replace(/Ü/g, 'u').replace(/ü/g, 'u');

                  const q = norm(appSearch.trim());
                  // Phase 86AS — Zone filter chips below status tabs
                  const zoneFiltered = appZone === 'all'
                    ? activeList
                    : activeList.filter(r => (r.zone || '') === appZone);
                  const filteredList = q
                    ? zoneFiltered.filter(r =>
                        norm(r.full_name).includes(q) ||
                        norm(r.email).includes(q) ||
                        norm(r.country).includes(q) ||
                        norm(r.company).includes(q) ||
                        norm(r.organization).includes(q) ||
                        norm(r.title).includes(q) ||
                        norm(r.supplier_category_name).includes(q) ||
                        norm(r.supplier_sub_category_name).includes(q) ||
                        norm(r.zone).includes(q)
                      )
                    : zoneFiltered;

                  // Zone tab counts (computed from currently-active status list)
                  const zoneTabs = [
                    { key: 'all',        label: tr ? 'Tümü'       : 'All',        cls: 'bg-gray-700 text-white',     dotCls: 'bg-gray-400' },
                    { key: 'blue_zone',  label: 'Blue Zone',                       cls: 'bg-blue-600 text-white',     dotCls: 'bg-blue-500' },
                    { key: 'green_zone', label: 'Green Zone',                      cls: 'bg-emerald-600 text-white',  dotCls: 'bg-emerald-500' },
                    { key: 'supplier',   label: tr ? 'Tedarikçi'  : 'Supplier',    cls: 'bg-amber-600 text-white',    dotCls: 'bg-amber-500' },
                    { key: 'contractor', label: tr ? 'Yüklenici'  : 'Contractor',  cls: 'bg-rose-600 text-white',     dotCls: 'bg-rose-500' },
                  ].map(z => ({
                    ...z,
                    count: z.key === 'all'
                      ? activeList.length
                      : activeList.filter(r => (r.zone || '') === z.key).length,
                  }));

                  return (
                    <>
                      {/* Tab strip */}
                      <div className="flex gap-2 flex-wrap">
                        {subTabs.map(tab => (
                          <button
                            key={tab.key}
                            data-testid={`app-subtab-${tab.key}`}
                            onClick={() => { setAppTab(tab.key); setAppSearch(''); setAppZone('all'); }}
                            className={`flex items-center gap-2 px-5 py-2 rounded-full text-sm font-medium transition-all ${appTab === tab.key ? tab.activeCls : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                          >
                            {tab.label}
                            <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${appTab === tab.key ? 'bg-white/25 text-white' : tab.cntCls}`}>
                              {tab.count}
                            </span>
                          </button>
                        ))}
                      </div>

                      {/* Phase 86AS — Zone filter chips */}
                      <div className="flex gap-2 flex-wrap pt-1">
                        {zoneTabs.map(z => {
                          if (z.key !== 'all' && z.count === 0) return null;
                          const active = appZone === z.key;
                          return (
                            <button
                              key={z.key}
                              data-testid={`app-zone-${z.key}`}
                              onClick={() => setAppZone(z.key)}
                              className={`flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-semibold border transition-all ${
                                active
                                  ? `${z.cls} border-transparent shadow-sm`
                                  : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                              }`}
                            >
                              <span className={`h-1.5 w-1.5 rounded-full ${active ? 'bg-white/80' : z.dotCls}`} />
                              {z.label}
                              <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${active ? 'bg-white/25 text-white' : 'bg-gray-100 text-gray-500'}`}>{z.count}</span>
                            </button>
                          );
                        })}
                      </div>

                      {/* Search bar */}
                      <div className="relative">
                        <svg className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                        <input
                          data-testid="app-search-input"
                          type="text"
                          value={appSearch}
                          onChange={e => setAppSearch(e.target.value)}
                          placeholder={tr ? 'Ad, e-posta, kurum, kategori, ülke...' : 'Name, email, company, category, country...'}
                          className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-gray-300 bg-white"
                        />
                        {appSearch && (
                          <button onClick={() => setAppSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                          </button>
                        )}
                      </div>

                      {/* Cards */}
                      <div className="space-y-3">
                        {/* Bulk Action Toolbar */}
                        {appTab === 'pending' && filteredList.length > 0 && (
                          <div className="flex items-center justify-between bg-gray-50 rounded-xl px-4 py-2.5 border border-gray-200">
                            <div className="flex items-center gap-3">
                              <input
                                type="checkbox"
                                data-testid="select-all-regs"
                                checked={selectedRegs.length > 0 && filteredList.filter(r => r.status === 'pending').every(r => selectedRegs.includes(r.id))}
                                ref={el => { if (el) el.indeterminate = selectedRegs.length > 0 && !filteredList.filter(r => r.status === 'pending').every(r => selectedRegs.includes(r.id)); }}
                                onChange={e => {
                                  const pendingIds = filteredList.filter(r => r.status === 'pending').map(r => r.id);
                                  setSelectedRegs(e.target.checked ? pendingIds : []);
                                }}
                                className="h-4 w-4 rounded border-gray-300 accent-gray-900"
                              />
                              <span className="text-sm text-gray-600">
                                {selectedRegs.length > 0
                                  ? (tr ? `${selectedRegs.length} seçildi` : `${selectedRegs.length} selected`)
                                  : (tr ? 'Tümünü Seç' : 'Select All')}
                              </span>
                            </div>
                            {selectedRegs.length > 0 && (
                              <div className="flex items-center gap-2">
                                <Button
                                  data-testid="bulk-approve-btn"
                                  size="sm"
                                  onClick={() => handleBulkAction('approve')}
                                  disabled={bulkLoading}
                                  className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full gap-1.5">
                                  {bulkLoading
                                    ? <div className="animate-spin h-3.5 w-3.5 border-2 border-white border-t-transparent rounded-full" />
                                    : <CheckCircle className="h-3.5 w-3.5" />}
                                  {tr ? `${selectedRegs.length} Onayla` : `Approve ${selectedRegs.length}`}
                                </Button>
                                <Button
                                  data-testid="bulk-reject-btn"
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleBulkAction('reject')}
                                  disabled={bulkLoading}
                                  className="border-red-300 text-red-600 hover:bg-red-50 rounded-full gap-1.5">
                                  <XCircle className="h-3.5 w-3.5" />
                                  {tr ? `${selectedRegs.length} Reddet` : `Reject ${selectedRegs.length}`}
                                </Button>
                                <button
                                  onClick={() => setSelectedRegs([])}
                                  className="text-gray-400 hover:text-gray-600 ml-1 text-xs"
                                >
                                  ✕
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                        {filteredList.length === 0 ? (
                          <div className="text-center py-14">
                            <Clock className="h-14 w-14 mx-auto mb-4 text-gray-200" />
                            <p className="text-gray-400 text-sm">
                              {q
                                ? (tr ? `"${appSearch}" için sonuç bulunamadı.` : `No results for "${appSearch}".`)
                                : appTab === 'pending'  ? (tr ? 'Bekleyen başvuru yok.' : 'No pending applications.')
                                : appTab === 'approved' ? (tr ? 'Onaylanan başvuru yok.' : 'No approved applications.')
                                : appTab === 'rejected' ? (tr ? 'Reddedilen başvuru yok.' : 'No rejected applications.')
                                : appTab === 'inactive' ? (tr ? 'Pasif başvuru yok.' : 'No passive applications.')
                                : (tr ? 'Silinmiş başvuru yok.' : 'No deleted applications.')}
                            </p>
                          </div>
                        ) : filteredList.map((reg) => (
                          <div key={reg.id} data-testid={`app-card-${reg.id}`}
                            className={`bg-white border rounded-2xl p-5 hover:shadow-md transition-shadow cursor-pointer hover:border-amber-200 ${selectedRegs.includes(reg.id) ? 'border-gray-900 ring-2 ring-gray-900/10' : 'border-gray-100'}`}
                            onClick={() => setSelectedReg(reg)}
                          >
                            <div className="flex items-start justify-between gap-4">
                              <div className="flex items-start gap-4">
                                {/* Checkbox for pending only */}
                                {reg.status === 'pending' && (
                                  <input
                                    type="checkbox"
                                    data-testid={`reg-checkbox-${reg.id}`}
                                    checked={selectedRegs.includes(reg.id)}
                                    onChange={e => {
                                      e.stopPropagation();
                                      setSelectedRegs(prev => e.target.checked ? [...prev, reg.id] : prev.filter(id => id !== reg.id));
                                    }}
                                    onClick={e => e.stopPropagation()}
                                    className="h-4 w-4 mt-1 rounded border-gray-300 accent-gray-900 flex-shrink-0"
                                  />
                                )}
                                {/* Avatar */}
                                <div className={`h-12 w-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                                  reg.status === 'pending'  ? 'bg-orange-100' :
                                  reg.status === 'approved' ? 'bg-emerald-100' :
                                  reg.status === 'inactive' ? 'bg-gray-100' :
                                  reg.status === 'deleted'  ? 'bg-zinc-100' :
                                  'bg-red-100'
                                }`}>
                                  <Users className={`h-6 w-6 ${
                                    reg.status === 'pending'  ? 'text-orange-500' :
                                    reg.status === 'approved' ? 'text-emerald-600' :
                                    reg.status === 'inactive' ? 'text-gray-400' :
                                    reg.status === 'deleted'  ? 'text-zinc-400' :
                                    'text-red-500'
                                  }`} />
                                </div>

                                <div>
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <h4 className="font-semibold text-gray-900">{reg.full_name}</h4>
                                    {reg.from_unfccc && (
                                      <span
                                        data-testid={`unfccc-badge-${reg.id}`}
                                        title={tr ? 'UNFCCC E-posta Listesi üzerinden aktive oldu' : 'Activated via UNFCCC Email List'}
                                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide bg-indigo-100 text-indigo-700 border border-indigo-200"
                                      >
                                        UNFCCC
                                      </span>
                                    )}
                                    {reg.zone && (
                                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${zoneBadge(reg.zone)}`}>
                                        {zoneLabel(reg.zone)}
                                      </span>
                                    )}
                                    {/* UNFCCC lock warning — approve anında bloklanmış başvurular için */}
                                    {reg.unfccc_block_reason && reg.status === 'pending' && (
                                      <span
                                        data-testid={`unfccc-lock-badge-${reg.id}`}
                                        title={reg.unfccc_block_reason}
                                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-amber-100 text-amber-800 border border-amber-300"
                                      >
                                        <AlertTriangle className="h-3 w-3" />
                                        {tr ? 'UNFCCC Kilidi' : 'UNFCCC Lock'}
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-sm text-gray-500">{reg.email}</p>

                                  {/* Full reason banner when UNFCCC-blocked */}
                                  {reg.unfccc_block_reason && reg.status === 'pending' && (
                                    <div
                                      data-testid={`unfccc-lock-reason-${reg.id}`}
                                      className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 leading-relaxed"
                                    >
                                      <span className="font-semibold">{tr ? 'Son onay denemesi engellendi:' : 'Last approval attempt blocked:'}</span>{' '}
                                      {reg.unfccc_block_reason}
                                      {reg.unfccc_block_at && (
                                        <span className="block mt-0.5 text-amber-600/80">
                                          {new Date(reg.unfccc_block_at).toLocaleString()}
                                        </span>
                                      )}
                                    </div>
                                  )}

                                  <div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-1.5 text-xs text-gray-400">
                                    {reg.country && <span>{tr ? 'Ülke:' : 'Country:'} <strong className="text-gray-600">{reg.country}</strong></span>}
                                    {(reg.company || reg.organization) && <span>{tr ? 'Kurum/Firma:' : 'Org/Company:'} <strong className="text-gray-600">{reg.company || reg.organization}</strong></span>}
                                    {reg.title && <span>{tr ? 'Görev:' : 'Title:'} <strong className="text-gray-600">{reg.title}</strong></span>}
                                    {reg.supplier_category_name && <span>{tr ? 'Kategori:' : 'Category:'} <strong className="text-amber-700">{reg.supplier_category_name}{reg.supplier_sub_category_name ? ` / ${reg.supplier_sub_category_name}` : ''}</strong></span>}
                                  </div>

                                  <p className="text-xs text-gray-300 mt-1.5">
                                    {new Date(reg.created_at).toLocaleString()}
                                    {reg.reviewed_at && <span className="ml-2">· {tr ? 'İncelendi:' : 'Reviewed:'} {new Date(reg.reviewed_at).toLocaleString()}</span>}
                                  </p>
                                </div>
                              </div>

                                {/* Actions / Status */}
                              <div className="flex flex-col items-end gap-2 flex-shrink-0">
                                {reg.status === 'pending' ? (
                                  <div className="flex gap-2">
                                    <Button data-testid={`approve-btn-${reg.id}`} size="sm"
                                      onClick={(e) => { e.stopPropagation(); handleApprove(reg.id); }}
                                      disabled={!!approvalLoading[reg.id]}
                                      className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full">
                                      {approvalLoading[reg.id] === 'approving'
                                        ? <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                                        : <><CheckCircle className="h-4 w-4 mr-1" />{tr ? 'Onayla' : 'Approve'}</>}
                                    </Button>
                                    <Button data-testid={`reject-btn-${reg.id}`} size="sm" variant="outline"
                                      onClick={(e) => { e.stopPropagation(); handleReject(reg.id); }}
                                      disabled={!!approvalLoading[reg.id]}
                                      className="border-red-300 text-red-600 hover:bg-red-50 rounded-full">
                                      {approvalLoading[reg.id] === 'rejecting'
                                        ? <div className="animate-spin h-4 w-4 border-2 border-red-400 border-t-transparent rounded-full" />
                                        : <><XCircle className="h-4 w-4 mr-1" />{tr ? 'Reddet' : 'Reject'}</>}
                                    </Button>
                                  </div>
                                ) : reg.status === 'deleted' ? (
                                  /* Silinmiş — sadece bilgi badge'i */
                                  <Badge className="rounded-full px-3 bg-zinc-100 text-zinc-600">
                                    {tr ? 'Silinmiş' : 'Deleted'}
                                  </Badge>
                                ) : reg.status === 'inactive' ? (
                                  /* Phase 86AT — Pasif: rozet + Aktife Al butonu */
                                  <div className="flex items-center gap-2">
                                    <Badge className="rounded-full px-3 bg-gray-100 text-gray-500">
                                      {tr ? 'Pasif' : 'Passive'}
                                    </Badge>
                                    <button
                                      data-testid={`reactivate-reg-${reg.id}`}
                                      onClick={(e) => { e.stopPropagation(); handleReactivateReg(reg.id); }}
                                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 transition-colors"
                                      title={tr ? 'Aktife Al' : 'Reactivate'}>
                                      <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.2}><path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/></svg>
                                      {tr ? 'Aktife Al' : 'Reactivate'}
                                    </button>
                                  </div>
                                ) : (
                                  /* Onaylı / Reddedilmiş — Pasife Al + Sil
                                     Phase 86AT: Sentetik UNFCCC/tenant kayıtları için Pasife Al gözüksün; Sil sadece gerçek registration'da. */
                                  <div className="flex items-center gap-2">
                                    <Badge className={`rounded-full px-3 ${reg.status === 'approved' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                                      {reg.status === 'approved' ? (tr ? 'Onaylandı' : 'Approved') : (tr ? 'Reddedildi' : 'Rejected')}
                                    </Badge>
                                    <button
                                      data-testid={`deactivate-reg-${reg.id}`}
                                      onClick={(e) => { e.stopPropagation(); handleDeactivateReg(reg.id); }}
                                      className="p-1.5 rounded-lg text-gray-300 hover:text-amber-500 hover:bg-amber-50 transition-colors"
                                      title={tr ? 'Pasife Al' : 'Deactivate'}>
                                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" /></svg>
                                    </button>
                                    {!reg.from_unfccc && !reg.from_tenant && (
                                      <button
                                        data-testid={`delete-reg-${reg.id}`}
                                        onClick={(e) => { e.stopPropagation(); handleDeleteReg(reg.id); }}
                                        className="p-1.5 rounded-lg text-gray-300 hover:text-red-500 hover:bg-red-50 transition-colors"
                                        title={tr ? 'Sil' : 'Delete'}>
                                        <Trash2 className="h-4 w-4" />
                                      </button>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  );
                })()}
              </TabsContent>

              {/* ── Stand Değişiklik Talepleri ── */}
              <TabsContent value="stand-requests" className="space-y-4">
                <div className="flex items-center justify-between mb-2">
                  <h2 className="text-lg font-bold text-gray-800">
                    {tr ? 'Stand Değişiklik Talepleri' : 'Stand Change Requests'}
                  </h2>
                  <span className="text-xs bg-amber-100 text-amber-700 px-2.5 py-1 rounded-full font-bold">
                    {standChangeRequests.filter(r => r.status === 'pending').length} {tr ? 'bekleyen' : 'pending'}
                  </span>
                </div>

                {standChangeRequests.length === 0 ? (
                  <div className="text-center py-16 text-gray-400">
                    <p className="text-sm">{tr ? 'Henüz talep yok.' : 'No requests yet.'}</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {standChangeRequests.map(req => (
                      <div key={req.id} className={`rounded-2xl border p-4 ${req.status === 'pending' ? 'border-amber-200 bg-amber-50' : req.status === 'awaiting_customer_choice' ? 'border-blue-200 bg-blue-50' : req.status === 'approved' && req.customer_area_approval_status !== 'approved' ? 'border-purple-200 bg-purple-50' : req.status === 'approved' ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}`}>
                        <div className="flex items-start justify-between gap-4 flex-wrap">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap mb-1">
                              <span className="font-bold text-sm text-gray-800">{req.tenant_name || req.tenant_slug}</span>
                              {req.country && <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">{req.country}</span>}
                              {req.current_stand_no && <span className="font-mono text-xs bg-gray-200 text-gray-700 px-2 py-0.5 rounded">{req.current_stand_no}</span>}
                              {/* Phase 86Z — Status pill is now lifecycle-aware:
                                  An "approved" SCR with direct_assigned where
                                  the customer hasn't yet re-approved the new
                                  stand area sits in a distinct state we label
                                  "Müşteri Onayı Bekleniyor". Solves the report
                                  that "Approved" was misleading right after a
                                  Direct Assign before customer ack. */}
                              {(() => {
                                const isApproved = req.status === 'approved';
                                const customerOk = req.customer_area_approval_status === 'approved';
                                const awaitingAck = isApproved && !customerOk;
                                let cls, txt;
                                if (req.status === 'pending') { cls = 'bg-yellow-200 text-yellow-800'; txt = tr ? 'Bekliyor' : 'Pending'; }
                                else if (req.status === 'awaiting_customer_choice') { cls = 'bg-blue-200 text-blue-800'; txt = tr ? 'Müşteri Yanıtı Bekleniyor' : 'Awaiting Customer'; }
                                else if (awaitingAck) { cls = 'bg-purple-200 text-purple-800'; txt = tr ? 'Müşteri Onayı Bekleniyor' : 'Awaiting Customer Approval'; }
                                else if (isApproved) { cls = 'bg-green-200 text-green-800'; txt = tr ? 'Tamamlandı' : 'Completed'; }
                                else if (req.status === 'declined_by_customer') { cls = 'bg-orange-200 text-orange-800'; txt = tr ? 'Müşteri Vazgeçti' : 'Declined by Customer'; }
                                else { cls = 'bg-red-200 text-red-800'; txt = tr ? 'Reddedildi' : 'Rejected'; }
                                return <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${cls}`}>{txt}</span>;
                              })()}
                            </div>

                            {/* Talep detayları */}
                            <div className="flex flex-wrap gap-3 text-xs text-gray-600 mt-2">
                              {req.change_types?.map(t => (
                                <span key={t} className="flex items-center gap-1 bg-white border border-gray-200 px-2 py-0.5 rounded-full">
                                  {t === 'area'       ? `📐 ${tr ? 'Alan' : 'Area'}${req.requested_area ? `: ${req.requested_area}m²` : ''}` :
                                   t === 'location'   ? `📍 ${tr ? 'Konum' : 'Location'}${req.requested_location ? `: ${req.requested_location}` : ''}` :
                                                        `🏪 ${tr ? 'Stand Tipi' : 'Stand Type'}${req.requested_stand_type ? `: ${req.requested_stand_type}` : ''}`}
                                </span>
                              ))}
                            </div>

                            {/* Mevcut vs talep */}
                            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-xs text-gray-500">
                              <span>{tr ? 'Mevcut' : 'Current'}: {req.current_area}m² · {req.current_open_sides?.join(', ') || '—'}</span>
                              {req.notes && (
                                req.notes.length > 80 ? (
                                  <button
                                    type="button"
                                    onClick={() => setChangeReqNoteModal({ text: req.notes, country: req.country, stand_no: req.stand_no })}
                                    data-testid={`change-req-note-${req.id}`}
                                    className="italic text-gray-500 hover:text-amber-700 bg-white border border-amber-200 hover:border-amber-400 rounded-md px-2 py-0.5 inline-flex items-center gap-1 max-w-md text-left transition-colors"
                                    title={tr ? 'Tam notu görüntüle' : 'View full note'}
                                  >
                                    <span className="truncate">"{req.notes.slice(0, 80)}…"</span>
                                    <span className="text-[10px] font-bold not-italic text-amber-700 flex-shrink-0">
                                      {tr ? 'Tamamı →' : 'Read more →'}
                                    </span>
                                  </button>
                                ) : (
                                  <span className="italic text-gray-400">"{req.notes}"</span>
                                )
                              )}
                            </div>

                            <div className="flex items-center gap-3 mt-1">
                              <p className="text-xs text-gray-400">
                                {new Date(req.created_at).toLocaleString(tr ? 'tr-TR' : 'en-US', {day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit'})}
                              </p>
                              {req.remind_count > 0 && (
                                <span className="flex items-center gap-1 text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-semibold">
                                  <Bell className="h-3 w-3" />
                                  {req.remind_count} {tr ? 'hatırlatma' : 'reminder(s)'}
                                </span>
                              )}
                              {/* Phase 86AT — Ultra Password gated delete.
                                  Always available on every card (any status)
                                  so admins can purge stale or no-longer-
                                  relevant change requests. The modal asks
                                  for the shared Ultra Password before the
                                  DELETE call fires. */}
                              <button
                                type="button"
                                data-testid={`delete-change-req-${req.id}`}
                                onClick={() => setCrDeleteModal({ req, password: '', loading: false, error: '' })}
                                title={tr ? 'Ultra Password ile sil' : 'Delete with Ultra Password'}
                                className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded-md border border-rose-200 bg-white hover:bg-rose-50 text-rose-600 hover:text-rose-700 text-[11px] font-semibold transition-colors">
                                <Trash2 className="h-3 w-3" />
                                {tr ? 'Sil' : 'Delete'}
                              </button>
                            </div>                          </div>

                          {/* Onay/Red/Alternatif butonları (sadece pending'de) */}
                          {req.status === 'pending' && (() => {
                            const own = ownershipForReq(req);
                            const blocked = !own.allowed;
                            const ownerLabel = own.ownerName;
                            const fireReview = (status) => {
                              if (own.isOverride) {
                                setCrOverride({
                                  reqId: req.id, ownerName: ownerLabel, action: 'review',
                                  onConfirm: () => { setCrOverride(null); handleReviewStandRequest(req.id, status); },
                                });
                              } else {
                                handleReviewStandRequest(req.id, status);
                              }
                            };
                            const firePropose = () => {
                              if (own.isOverride) {
                                setCrOverride({
                                  reqId: req.id, ownerName: ownerLabel, action: 'propose',
                                  onConfirm: () => { setCrOverride(null); setStandPickerReqId(req.id); },
                                });
                              } else {
                                setStandPickerReqId(req.id);
                              }
                            };
                            return (
                              <div className="flex flex-col gap-2 shrink-0 w-full mt-3 border-t border-amber-100 pt-3">
                                {/* Ownership notice */}
                                {ownerLabel && (
                                  <div className="text-[11px] rounded-lg px-2 py-1 border bg-amber-50 border-amber-200 text-amber-800" data-testid={`cr-ownership-${req.id}`}>
                                    {tr ? 'Stand atayanı' : 'Stand assigned by'}: <strong>{ownerLabel}</strong>
                                    {blocked && <span className="ml-1">· {tr ? 'sadece o admin veya Super Admin' : 'only that admin or a Super Admin can review'}</span>}
                                    {own.isOverride && <span className="ml-1">· {tr ? 'Super Admin override' : 'Super Admin override'}</span>}
                                  </div>
                                )}
                                <input
                                  type="text"
                                  placeholder={tr ? 'Admin notu (isteğe bağlı)' : 'Admin note (optional)'}
                                  value={reviewNote[req.id] || ''}
                                  onChange={e => setReviewNote(prev => ({...prev, [req.id]: e.target.value}))}
                                  disabled={blocked}
                                  className="text-xs border border-gray-300 rounded-lg px-2 py-1 w-full focus:outline-none focus:ring-1 focus:ring-amber-400 disabled:bg-gray-50 disabled:cursor-not-allowed"
                                />
                                <div className="flex flex-wrap gap-2">
                                  <button
                                    data-testid={`reject-stand-req-${req.id}`}
                                    onClick={() => fireReview('rejected')}
                                    disabled={!!reviewLoading[req.id] || blocked}
                                    title={blocked ? `${tr ? 'Sadece' : 'Only'} ${ownerLabel}` : ''}
                                    className="flex items-center gap-1 px-3 py-1.5 rounded-xl bg-red-100 hover:bg-red-200 text-red-700 text-xs font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                                    <XCircle className="h-3.5 w-3.5" />
                                    {tr ? 'Reddet' : 'Reject'}
                                  </button>
                                  {/* Phase 86X — "Approve" button removed.
                                      A bare approve only flipped the request
                                      status without performing any actual
                                      stand re-allocation, which confused
                                      admins ("I clicked approve, why did
                                      nothing happen?"). The real intent is
                                      either (a) propose alternatives via
                                      "Suggest from Grid" or (b) commit a
                                      direct re-assignment via "Direct
                                      Assign". Both perform the cascade
                                      (free old / assign new / migrate
                                      orders). Removing the no-op button
                                      eliminates the dead path. */}
                                  <button
                                    data-testid={`propose-alt-btn-${req.id}`}
                                    onClick={firePropose}
                                    disabled={blocked}
                                    title={blocked ? `${tr ? 'Sadece' : 'Only'} ${ownerLabel}` : ''}
                                    className="flex items-center gap-1 px-3 py-1.5 rounded-xl bg-amber-100 hover:bg-amber-200 text-amber-800 text-xs font-bold transition-colors ml-auto disabled:opacity-50 disabled:cursor-not-allowed">
                                    <Plus className="h-3.5 w-3.5" />
                                    {tr ? 'Grid\'den Alternatif Öner' : 'Suggest from Grid'}
                                  </button>
                                  <button
                                    data-testid={`direct-assign-btn-${req.id}`}
                                    onClick={() => {
                                      const open = () => {
                                        setPickerMode(prev => ({ ...prev, [req.id]: 'direct' }));
                                        setStandPickerReqId(req.id);
                                      };
                                      if (own.isOverride) {
                                        setCrOverride({
                                          reqId: req.id, ownerName: ownerLabel, action: 'direct',
                                          onConfirm: () => { setCrOverride(null); open(); },
                                        });
                                      } else { open(); }
                                    }}
                                    disabled={blocked}
                                    title={tr ? 'Müşteri seçim yapmadan direkt yeni stand ata' : 'Directly assign a new stand without customer selection'}
                                    className="flex items-center gap-1 px-3 py-1.5 rounded-xl bg-violet-100 hover:bg-violet-200 text-violet-800 text-xs font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                                    <CheckCircle className="h-3.5 w-3.5" />
                                    {tr ? 'Direkt Ata' : 'Direct Assign'}
                                  </button>
                                </div>
                              </div>
                            );
                          })()}

                          {/* Müşteri yanıtı bekliyor */}
                          {req.status === 'awaiting_customer_choice' && (
                            <div className="mt-3 pt-3 border-t border-blue-100 w-full">
                              <div className="flex items-center gap-2 text-xs text-blue-700 font-semibold mb-2">
                                <Clock className="h-3.5 w-3.5" />
                                {tr ? 'Müşteri yanıtı bekleniyor' : 'Awaiting customer response'}
                                <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                                  {req.alternatives?.length || 0} {tr ? 'alternatif gönderildi' : 'alternatives sent'}
                                </span>
                              </div>
                              {req.alternatives?.map((alt, i) => (
                                <div key={alt.id} className="text-xs bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 mb-1">
                                  <span className="font-bold text-blue-800 mr-2">{i+1}.</span>
                                  {alt.stand_no && <span className="font-mono bg-white px-1 rounded mr-1">{alt.stand_no}</span>}
                                  {alt.hall && <span className="text-gray-500 mr-1">{alt.hall}</span>}
                                  {alt.area && <span className="text-gray-600 mr-1">{alt.area}m²</span>}
                                  {alt.stand_type && <span className="text-indigo-600 capitalize mr-1">{alt.stand_type}</span>}
                                  {alt.location_note && <span className="text-gray-400 italic">{alt.location_note}</span>}
                                </div>
                              ))}
                            </div>
                          )}

                          {req.status !== 'pending' && req.status !== 'awaiting_customer_choice' && req.admin_note && (
                            <p className="text-xs italic text-gray-500 shrink-0 max-w-xs">{req.admin_note}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* ── STAND DESIGNS TAB — Phase 86AO 2-column layout ── */}
              <TabsContent value="stand-designs" className="space-y-0">
                {/* Phase 86AZ.31 — Admin-only test data purge bar */}
                <StandDesignsAdminBar language={language} />
                <div className="grid grid-cols-12 gap-4">
                  <aside className="col-span-12 md:col-span-4 lg:col-span-3">
                    <TenantStandSummary language={language} clickable vertical />
                  </aside>
                  <section className="col-span-12 md:col-span-8 lg:col-span-9">
                    <AdminStandDesignsSection language={language} embedded />
                  </section>
                </div>
              </TabsContent>

              {/* ── ISG TAB — Phase 86AZ.57 ── */}
              <TabsContent value="isg" className="space-y-0">
                <UltraAdminISG tr={tr} />
              </TabsContent>

              {/* ── ACTIVITY TAB — last 50 admin actions w/ conflict pills ── */}
              <TabsContent value="activity" className="space-y-4">
                <AdminActivityFeed tr={tr} />
              </TabsContent>

              <TabsContent value="settings" className="space-y-6">
                <BusinessHoursSettings language={language} />
              </TabsContent>

            </Tabs>
          </CardContent>
        </Card>
        </div>
      </div>
    </div>

    {/* Stand Picker Modal */}
    {standPickerReqId && (
      <StandPickerModal
        language={language}
        // Item 3 — Direct mode forces single-pick + tells the modal to label
        // the confirm action as "Direkt Ata" instead of "Alternatif Öner".
        mode={pickerMode[standPickerReqId] === 'direct' ? 'direct' : 'propose'}
        onClose={() => {
          setStandPickerReqId(null);
          setPickerMode(prev => ({ ...prev, [standPickerReqId]: null }));
        }}
        adminNote={reviewNote[standPickerReqId] || ''}
        setAdminNote={(val) => setReviewNote(prev => ({...prev, [standPickerReqId]: val}))}
        onPropose={(selectedStands) => handleProposeAlternatives(standPickerReqId, selectedStands)}
      />
    )}

    {/* Customer Note Modal — for long change-request notes */}
    {changeReqNoteModal && (
      <div
        className="fixed inset-0 z-[120] flex items-center justify-center bg-black/50 p-4"
        onClick={() => setChangeReqNoteModal(null)}
        data-testid="change-req-note-modal-overlay"
      >
        <div
          className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden"
          onClick={(e) => e.stopPropagation()}
          data-testid="change-req-note-modal"
        >
          <div className="flex items-start justify-between px-5 py-4 border-b border-gray-100 bg-amber-50">
            <div className="min-w-0">
              <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
                <FileText className="h-4 w-4 text-amber-600" />
                {tr ? 'Müşteri Notu' : 'Customer Note'}
              </h3>
              <div className="flex flex-wrap items-center gap-2 mt-1">
                {changeReqNoteModal.country && (
                  <span className="text-[11px] bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-semibold">
                    {changeReqNoteModal.country}
                  </span>
                )}
                {changeReqNoteModal.stand_no && (
                  <span className="text-[11px] font-mono bg-gray-200 text-gray-700 px-2 py-0.5 rounded">
                    {changeReqNoteModal.stand_no}
                  </span>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={() => setChangeReqNoteModal(null)}
              className="p-1 rounded-md hover:bg-amber-100 text-gray-500 hover:text-gray-700 transition-colors flex-shrink-0"
              data-testid="change-req-note-modal-close"
              aria-label={tr ? 'Kapat' : 'Close'}
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="px-5 py-4 max-h-[60vh] overflow-y-auto">
            <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
              {changeReqNoteModal.text}
            </p>
          </div>
          <div className="px-5 py-3 border-t border-gray-100 bg-gray-50 flex justify-end">
            <Button
              onClick={() => setChangeReqNoteModal(null)}
              data-testid="change-req-note-modal-ok"
              className="bg-gray-800 hover:bg-gray-900 text-white"
            >
              {tr ? 'Tamam' : 'OK'}
            </Button>
          </div>
        </div>
      </div>
    )}
    {/* Super Admin Override Modal — Stand Change Request flow ────────────
        Mirrors HallEditorPage: when a Super Admin reviews/proposes on a
        change request whose tenant's current stand was assigned by another
        admin, we surface an explicit confirm before firing the API call. */}
    {crOverride && (
      <div className="fixed inset-0 z-[130] flex items-center justify-center bg-black/55 p-4" data-testid="cr-override-modal">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-full bg-rose-100 flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-6 h-6 text-rose-600" />
            </div>
            <div>
              <h3 className="font-bold text-gray-800 text-lg">
                {tr ? "Başka admin'in talebine müdahale" : "Overriding another admin"}
              </h3>
              <p className="text-xs text-gray-500 mt-0.5">
                {tr ? 'Super Admin yetkisi · Audit log\'a kaydedilecek' : 'Super Admin override · will be audit-logged'}
              </p>
            </div>
          </div>
          <p className="text-sm text-gray-700 mb-4 leading-relaxed">
            {tr ? 'Bu talep' : 'This request belongs to a tenant whose stand was assigned by'}
            {' '}<strong className="text-rose-700">{crOverride.ownerName}</strong>
            {tr ? ' tarafından atanmış bir tenant\'a ait. Yine de devam etmek istiyor musunuz?' : '. Do you want to continue anyway?'}
          </p>
          <div className="flex gap-3">
            <button
              data-testid="cr-override-cancel"
              onClick={() => setCrOverride(null)}
              className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 transition-colors">
              {tr ? 'Vazgeç' : 'Cancel'}
            </button>
            <button
              data-testid="cr-override-confirm"
              onClick={() => crOverride.onConfirm && crOverride.onConfirm()}
              className="flex-1 py-2.5 rounded-xl text-white text-sm font-semibold transition-colors"
              style={{ background: 'linear-gradient(135deg,#e11d48,#be123c)' }}>
              {tr ? 'Devam Et' : 'Proceed'}
            </button>
          </div>
        </div>
      </div>
    )}
    {/* Phase 86AT — Delete Change Request modal. Ultra Password gated.
        Mirrors the password-confirm UX used elsewhere (tenant delete). */}
    {crDeleteModal && (
      <div className="fixed inset-0 z-[135] flex items-center justify-center bg-black/55 p-4" data-testid="cr-delete-modal">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-full bg-rose-100 flex items-center justify-center flex-shrink-0">
              <Trash2 className="w-6 h-6 text-rose-600" />
            </div>
            <div>
              <h3 className="font-bold text-gray-800 text-lg">
                {tr ? 'Değişiklik Talebini Sil' : 'Delete Change Request'}
              </h3>
              <p className="text-xs text-gray-500 mt-0.5">
                {tr ? 'Bu işlem geri alınamaz · Audit log\'a yazılır' : 'This action is irreversible · audit-logged'}
              </p>
            </div>
          </div>
          <div className="text-sm text-gray-700 mb-3 leading-relaxed bg-gray-50 border border-gray-200 rounded-lg p-3">
            <div><strong>{crDeleteModal.req?.tenant_name || crDeleteModal.req?.tenant_slug}</strong>
              {crDeleteModal.req?.country && <span className="ml-2 text-xs text-gray-500">· {crDeleteModal.req.country}</span>}
              {crDeleteModal.req?.current_stand_no && <span className="ml-2 text-xs font-mono bg-white border border-gray-200 rounded px-1">{crDeleteModal.req.current_stand_no}</span>}
            </div>
            {crDeleteModal.req?.change_types?.length > 0 && (
              <div className="mt-1 text-xs text-gray-500">
                {crDeleteModal.req.change_types.join(', ')}
              </div>
            )}
          </div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">
            {tr ? 'Ultra Password' : 'Ultra Password'}
          </label>
          <input
            type="password"
            autoFocus
            data-testid="cr-delete-password-input"
            value={crDeleteModal.password || ''}
            onChange={e => setCrDeleteModal(prev => ({ ...prev, password: e.target.value, error: '' }))}
            onKeyDown={e => { if (e.key === 'Enter') handleDeleteChangeRequest(); }}
            placeholder={tr ? 'Ultra Password girin' : 'Enter Ultra Password'}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-rose-300"
          />
          {crDeleteModal.error && (
            <p className="text-xs text-rose-600 mt-2" data-testid="cr-delete-error">{crDeleteModal.error}</p>
          )}
          <div className="flex gap-3 mt-5">
            <button
              data-testid="cr-delete-cancel"
              onClick={() => setCrDeleteModal(null)}
              disabled={crDeleteModal.loading}
              className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-50">
              {tr ? 'Vazgeç' : 'Cancel'}
            </button>
            <button
              data-testid="cr-delete-confirm"
              onClick={handleDeleteChangeRequest}
              disabled={crDeleteModal.loading || !(crDeleteModal.password || '').trim()}
              className="flex-1 py-2.5 rounded-xl text-white text-sm font-semibold transition-colors disabled:opacity-50"
              style={{ background: 'linear-gradient(135deg,#e11d48,#be123c)' }}>
              {crDeleteModal.loading ? (tr ? 'Siliniyor…' : 'Deleting…') : (tr ? 'Sil' : 'Delete')}
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default UltraAdminDashboard;

// ─────────────────────────────────────────────────────────────
// Admin Stand Designs Section
// ─────────────────────────────────────────────────────────────
const UA_API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Phase 86AZ.36 — DesignReviewBar moved to a top-level component so the
// parent's polling re-renders no longer unmount it and reset its local
// `showApprove` / `showReject` / textarea state. Inputs stay open until
// the admin explicitly cancels or submits.
function DesignReviewBar({ designId, tr, onDone }) {
  const [submitting, setSubmitting] = useState(false);
  const [showReject, setShowReject] = useState(false);
  const [reason, setReason] = useState('');
  // Phase 86AZ.48 — Approval not'u için hazır şablon metin.
  const DEFAULT_APPROVE_NOTE = tr
    ? 'Stand tasarımınız onaylanmıştır, tebrik ederiz. Fiyat teklifiniz en kısa sürede hazırlanarak tarafınıza iletilecektir.'
    : 'Your stand design has been approved, congratulations. Your price quote will be prepared and sent to you shortly.';
  const [approveNote, setApproveNote] = useState(DEFAULT_APPROVE_NOTE);
  const [showApprove, setShowApprove] = useState(false);
  const submit = async (kind) => {
    setSubmitting(true);
    try {
      if (kind === 'approve') {
        await apiClient.post(`/stand-designs/admin/${designId}/approve`, { note: approveNote || null });
        onDone('approved', approveNote || null);
      } else {
        if (reason.trim().length < 4) { alert(tr ? 'Lütfen reddetme gerekçesi (en az 4 karakter) girin.' : 'Please provide a reason (min 4 chars).'); setSubmitting(false); return; }
        await apiClient.post(`/stand-designs/admin/${designId}/reject`, { reason });
        onDone('rejected', reason);
      }
    } catch (e) {
      alert(e?.response?.data?.detail || (tr ? 'İşlem başarısız.' : 'Action failed.'));
    } finally { setSubmitting(false); }
  };
  return (
    <div className="border-t border-gray-100 pt-4 mt-2" data-testid="design-review-bar">
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
        {tr ? 'Tasarım İnceleme' : 'Design Review'}
      </p>
      {!showApprove && !showReject && (
        <div className="flex flex-col sm:flex-row gap-2">
          <button type="button" data-testid="design-approve-open" disabled={submitting}
            onClick={() => setShowApprove(true)}
            className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-sm transition-colors disabled:opacity-50">
            <CheckCircle className="h-4 w-4" />
            {tr ? 'Tasarımı Onayla' : 'Approve Design'}
          </button>
          <button type="button" data-testid="design-reject-open" disabled={submitting}
            onClick={() => setShowReject(true)}
            className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-red-50 hover:bg-red-100 text-red-700 border border-red-200 font-semibold text-sm transition-colors disabled:opacity-50">
            <XCircle className="h-4 w-4" />
            {tr ? 'Reddet / Revizyon İste' : 'Reject / Request Revision'}
          </button>
        </div>
      )}
      {showApprove && (
        <div className="space-y-2 p-3 rounded-xl bg-emerald-50 border border-emerald-200">
          <p className="text-xs font-bold text-emerald-700">{tr ? 'Onay notu (opsiyonel)' : 'Approval note (optional)'}</p>
          <textarea data-testid="design-approve-note" value={approveNote}
            onChange={e => setApproveNote(e.target.value)} maxLength={1000}
            placeholder={tr ? 'Müşteriye iletilecek mesaj (örn. tebrikler, üretime geçebiliriz)…' : 'Message to customer (e.g. ready for production)…'}
            className="w-full text-sm border border-emerald-200 rounded-lg p-2 bg-white focus:ring-2 focus:ring-emerald-300 focus:outline-none min-h-[60px]" />
          <div className="flex gap-2">
            <button type="button" disabled={submitting} data-testid="design-approve-confirm"
              onClick={() => submit('approve')}
              className="flex-1 px-3 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold disabled:opacity-50">
              {submitting ? (tr ? 'Gönderiliyor…' : 'Submitting…') : (tr ? 'Onayı Gönder' : 'Send Approval')}
            </button>
            <button type="button" disabled={submitting}
              onClick={() => { setShowApprove(false); setApproveNote(''); }}
              className="px-3 py-2 rounded-lg bg-white border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50">
              {tr ? 'İptal' : 'Cancel'}
            </button>
          </div>
        </div>
      )}
      {showReject && (
        <div className="space-y-2 p-3 rounded-xl bg-red-50 border border-red-200">
          <p className="text-xs font-bold text-red-700">{tr ? 'Reddetme Gerekçesi (zorunlu)' : 'Rejection Reason (required)'}</p>
          <textarea data-testid="design-reject-reason" value={reason}
            onChange={e => setReason(e.target.value)} maxLength={1000}
            placeholder={tr ? 'Müşteriye iletilecek revizyon talebi — neyin değişmesi gerek?' : 'Revision request to the customer — what needs changing?'}
            className="w-full text-sm border border-red-200 rounded-lg p-2 bg-white focus:ring-2 focus:ring-red-300 focus:outline-none min-h-[80px]" />
          <div className="flex items-center justify-between gap-3">
            <span className={`text-[10px] ${reason.length < 4 ? 'text-red-500' : 'text-gray-400'}`}>
              {reason.length}/1000 {reason.length < 4 && (tr ? '· en az 4 karakter' : '· min 4 chars')}
            </span>
            <div className="flex gap-2">
              <button type="button" disabled={submitting || reason.trim().length < 4}
                data-testid="design-reject-confirm" onClick={() => submit('reject')}
                className="px-3 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-semibold disabled:opacity-50">
                {submitting ? (tr ? 'Gönderiliyor…' : 'Submitting…') : (tr ? 'Reddet' : 'Reject')}
              </button>
              <button type="button" disabled={submitting}
                onClick={() => { setShowReject(false); setReason(''); }}
                className="px-3 py-2 rounded-lg bg-white border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50">
                {tr ? 'İptal' : 'Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Phase 86AZ.31 — Destructive "Wipe Test Data" CTA used in dev/QA.
// Removes ALL stand design requests, messages, RFQs, bids and the
// associated uploaded files. Requires the admin to type DELETE-ALL
// to confirm.
// Phase 86AZ.40 — Direct Price Offer panel. Admin sends a fixed-price
// offer to ONE contractor at a time. Contractor accepts/rejects from
// their own dashboard. Once accepted, admin clicks Finalize to award.
// Phase 86AZ.46 — Admin Contractor Chats panel.
// Direct offer'ı olan her contractor için ayrı sub-tab. Onay (finalize)
// beklemeden, teklif gönderildiği an chat thread'i açık.
function AdminContractorChatsPanel({ tr, design }) {
  const [offers, setOffers] = useState([]);
  const [activeOfferId, setActiveOfferId] = useState(null);

  const fetchOffers = async () => {
    if (!design?.id) return;
    try {
      const r = await apiClient.get(`/stand-designs/admin/${design.id}/direct-offers`);
      const list = (r.data?.offers || []).filter(o => o.status !== 'cancelled');
      setOffers(list);
      if (list.length > 0 && !list.find(o => o.id === activeOfferId)) {
        setActiveOfferId(list[0].id);
      }
    } catch {}
  };
  useEffect(() => {
    fetchOffers();
    const t = setInterval(fetchOffers, 10000);
    return () => clearInterval(t);
  }, [design?.id]);

  if (offers.length === 0) {
    return (
      <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center">
        <Hammer className="h-12 w-12 text-gray-200 mx-auto mb-4" />
        <p className="font-medium text-gray-500 mb-1">
          {tr ? 'Henüz fiyat teklifi gönderilmedi' : 'No price offer sent yet'}
        </p>
        <p className="text-xs text-gray-400">
          {tr ? '"Fiyat Al" sekmesinden bir contractor seçip teklif gönderin; sohbet otomatik açılır.'
              : 'Send an offer from the "Get Price" tab; the chat will open here.'}
        </p>
      </div>
    );
  }

  const active = offers.find(o => o.id === activeOfferId) || offers[0];

  return (
    <div className="space-y-3">
      {offers.length > 1 && (
        <div className="flex gap-2 overflow-x-auto border-b border-gray-100 pb-0" data-testid="contractor-chat-subtabs">
          {offers.map(o => {
            const isActive = activeOfferId === o.id;
            return (
              <button key={o.id} type="button"
                onClick={() => setActiveOfferId(o.id)}
                className={`flex items-center gap-1.5 px-3 pb-2 -mb-px border-b-2 text-xs font-semibold flex-shrink-0 transition-colors ${
                  isActive ? 'border-orange-500 text-orange-600' : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}>
                {o.contractor_name}
                <span className={`text-[9px] uppercase font-bold px-1.5 py-0.5 rounded-full ${
                  o.status === 'pending' ? 'bg-amber-100 text-amber-700'
                  : o.status === 'accepted' ? 'bg-emerald-100 text-emerald-700'
                  : o.status === 'finalized' ? 'bg-blue-100 text-blue-700'
                  : 'bg-red-100 text-red-700'
                }`}>{o.status}</span>
              </button>
            );
          })}
        </div>
      )}
      {active && <DirectOfferChatThread key={active.id} tr={tr} offer={active} role="ultra_admin" />}
    </div>
  );
}

// Re-usable chat thread for direct-offer per-contractor conversation.
// Used both by admin Contractor Chat sub-tab and contractor-side card.
// Phase 86AZ.54 — Shares the global `window.__chatBlobCache` so attachments
// opened on the contractor side are already cached on the admin side and
// vice versa (same tab session).
const __adminChatBlobCache = (typeof window !== 'undefined' && window.__chatBlobCache)
  || (typeof window !== 'undefined' ? (window.__chatBlobCache = new Map()) : new Map());

async function adminGetCachedBlobUrl(url) {
  if (!url) return url;
  const hit = __adminChatBlobCache.get(url);
  if (hit) {
    __adminChatBlobCache.delete(url);
    __adminChatBlobCache.set(url, hit);
    return hit;
  }
  try {
    const token = typeof window !== 'undefined' ? localStorage.getItem('cop31_ultra_token') : null;
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const resp = await fetch(url, { headers, credentials: 'same-origin' });
    if (!resp.ok) return url;
    const blob = await resp.blob();
    const blobUrl = URL.createObjectURL(blob);
    __adminChatBlobCache.set(url, blobUrl);
    while (__adminChatBlobCache.size > 30) {
      const oldest = __adminChatBlobCache.keys().next().value;
      const oldUrl = __adminChatBlobCache.get(oldest);
      __adminChatBlobCache.delete(oldest);
      try { URL.revokeObjectURL(oldUrl); } catch { /* ignore */ }
    }
    return blobUrl;
  } catch {
    return url;
  }
}

function DirectOfferChatThread({ tr, offer, role }) {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [file, setFile] = useState(null);
  const [sending, setSending] = useState(false);
  const [lightbox, setLightbox] = useState(null); // { url, name, ext, blobUrl?, loading? }
  const fileRef = useRef(null);
  const API = process.env.REACT_APP_BACKEND_URL;

  const openLightbox = async ({ url, name, ext }) => {
    const cached = __adminChatBlobCache.get(url);
    setLightbox({ url, name, ext, blobUrl: cached, loading: !cached });
    if (!cached) {
      const blobUrl = await adminGetCachedBlobUrl(url);
      setLightbox(curr => (curr && curr.url === url ? { ...curr, blobUrl, loading: false } : curr));
    }
  };

  const fetchMessages = async () => {
    try {
      const r = await apiClient.get(`/stand-designs/direct-offers/${offer.id}/messages`);
      setMessages(r.data?.messages || []);
    } catch { /* ignore */ }
  };
  useEffect(() => {
    fetchMessages();
    const t = setInterval(fetchMessages, 6000);
    return () => clearInterval(t);
  }, [offer.id]);

  const locked = offer.status === 'rejected' || offer.status === 'cancelled' || offer.chat_locked;

  const send = async () => {
    if (locked) return;
    if (!text.trim() && !file) return;
    setSending(true);
    try {
      const fd = new FormData();
      if (text.trim()) fd.append('message', text);
      if (file) fd.append('attachment', file);
      await apiClient.post(`/stand-designs/direct-offers/${offer.id}/messages`, fd,
        { headers: { 'Content-Type': 'multipart/form-data' } });
      setText(''); setFile(null);
      await fetchMessages();
    } catch (e) {
      alert(e?.response?.data?.detail || 'failed');
    } finally { setSending(false); }
  };

  const isImageName = (n) => /\.(jpe?g|png|webp|gif|bmp|heic|heif|svg)$/i.test(n || '');
  const isPdfName   = (n) => /\.pdf$/i.test(n || '');

  return (
    <div className="bg-white rounded-2xl border border-gray-100 flex flex-col overflow-hidden" style={{minHeight: 480}}>
      <div className="px-4 py-3 border-b border-gray-100 bg-gradient-to-r from-orange-50 to-amber-50">
        <p className="font-bold text-gray-800 text-sm">{offer.contractor_name}</p>
        <p className="text-[10px] text-gray-500">
          {offer.price_per_m2} {offer.currency}/m² · {offer.area_m2} m² ·
          {offer.total_amount != null ? ` ${offer.total_amount} ${offer.currency}` : ''} · <strong className="uppercase">{offer.status}</strong>
        </p>
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-2 bg-gray-50" style={{maxHeight: 360}}>
        {locked && (
          <div data-testid="admin-chat-locked-banner"
            className="text-[11px] font-semibold text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-2 text-center">
            {tr ? 'Bu teklif kapatıldı — sohbet salt-okunur. Yeni mesaj gönderilemez.'
                : 'This offer is closed — chat is read-only. New messages cannot be sent.'}
          </div>
        )}
        {messages.length === 0 ? (
          <p className="text-xs text-gray-400 text-center py-8">{tr ? 'Henüz mesaj yok.' : 'No messages yet.'}</p>
        ) : messages.map(m => {
          const mine = m.sender_role === role;
          const att = m.attachment;
          const attUrl = att ? `${API}/api/uploads/${att.filename}` : null;
          const attIsImg = att && isImageName(att.original_name || att.filename);
          const attIsPdf = att && isPdfName(att.original_name || att.filename);
          return (
            <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[78%] px-3 py-2 rounded-2xl text-sm shadow-sm ${
                mine ? 'bg-orange-500 text-white rounded-br-sm' : 'bg-white border border-gray-200 text-gray-800 rounded-bl-sm'
              }`}>
                {m.message && <p className="whitespace-pre-wrap break-words">{m.message}</p>}
                {att && attIsImg && (
                  <button type="button"
                    onClick={() => openLightbox({ url: attUrl, name: att.original_name, ext: 'img' })}
                    className="block mt-1.5 rounded-lg overflow-hidden border border-white/30 hover:opacity-90 transition-opacity"
                    data-testid={`admin-chat-attachment-img-${m.id}`}>
                    <img src={attUrl} alt={att.original_name}
                      className="max-w-[220px] max-h-[160px] object-cover block bg-gray-100" />
                  </button>
                )}
                {att && attIsPdf && (
                  <button type="button"
                    onClick={() => openLightbox({ url: attUrl, name: att.original_name, ext: 'pdf' })}
                    className={`mt-1.5 flex items-center gap-2 px-2.5 py-1.5 rounded-lg border text-xs font-semibold ${
                      mine ? 'bg-white/15 border-white/30 hover:bg-white/25 text-white'
                           : 'bg-red-50 border-red-200 hover:bg-red-100 text-red-700'}`}
                    data-testid={`admin-chat-attachment-pdf-${m.id}`}>
                    <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${mine ? 'bg-white/30 text-white' : 'bg-red-200 text-red-800'}`}>PDF</span>
                    <span className="max-w-[160px] truncate">{att.original_name}</span>
                  </button>
                )}
                {att && !attIsImg && !attIsPdf && (
                  <a href={attUrl} target="_blank" rel="noopener noreferrer"
                    className={`mt-1 inline-flex items-center gap-1 text-xs underline ${mine ? 'text-white/90' : 'text-orange-600'}`}>
                    <Paperclip className="h-3 w-3" />
                    {att.original_name}
                  </a>
                )}
                <p className={`text-[9px] mt-1 ${mine ? 'text-white/70' : 'text-gray-400'}`}>
                  {new Date(m.created_at).toLocaleTimeString(tr ? 'tr-TR' : 'en-US', {hour: '2-digit', minute: '2-digit'})}
                </p>
              </div>
            </div>
          );
        })}
      </div>
      <div className="border-t border-gray-100 p-3 flex items-center gap-2">
        <input ref={fileRef} type="file" className="hidden"
          onChange={e => setFile(e.target.files?.[0] || null)} />
        <button type="button" onClick={() => fileRef.current?.click()} disabled={locked}
          className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 disabled:opacity-30 disabled:cursor-not-allowed" title={tr ? 'Dosya ekle' : 'Attach'}>
          <Paperclip className="h-4 w-4" />
        </button>
        {file && (
          <span className="text-[10px] text-gray-500 bg-gray-100 px-2 py-1 rounded-full max-w-[120px] truncate">
            {file.name}
            <button onClick={() => setFile(null)} className="ml-1 text-red-500">×</button>
          </span>
        )}
        <input type="text" value={text} onChange={e => setText(e.target.value)} disabled={locked}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder={locked ? (tr ? 'Sohbet kapatıldı' : 'Chat is closed') : (tr ? 'Mesaj yazın…' : 'Type a message…')}
          className="flex-1 text-sm border border-gray-200 rounded-full px-3 py-2 focus:ring-2 focus:ring-orange-200 focus:outline-none disabled:bg-gray-100 disabled:text-gray-400" />
        <button type="button" onClick={send} disabled={sending || locked || (!text.trim() && !file)}
          className="p-2 rounded-full bg-orange-500 hover:bg-orange-600 text-white disabled:opacity-40">
          <Send className="h-4 w-4" />
        </button>
      </div>
      {lightbox && (
        <div className="fixed inset-0 z-[10010] bg-black/90 flex items-center justify-center"
          onClick={(e) => { if (e.target === e.currentTarget) setLightbox(null); }}
          data-testid="admin-chat-attachment-modal">
          <div className="absolute top-0 left-0 right-0 px-5 py-3 flex items-center justify-between text-white bg-gradient-to-b from-black/60 to-transparent">
            <span className="text-xs text-white/80 truncate max-w-[60%]">{lightbox.name}</span>
            <div className="flex items-center gap-2">
              <a href={`${lightbox.url}${lightbox.url.includes('?') ? '&' : '?'}disposition=attachment`} download={lightbox.name} target="_blank" rel="noopener noreferrer"
                onClick={e => e.stopPropagation()}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 text-xs font-semibold">
                {tr ? 'İndir' : 'Download'}
              </a>
              <button type="button" onClick={() => setLightbox(null)}
                className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-white/10 hover:bg-white/20">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
          <div className="max-w-[92vw] max-h-[88vh] w-full h-full flex items-center justify-center px-10 py-14">
            {lightbox.loading && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="text-white/70 text-xs font-medium animate-pulse">
                  {tr ? 'Yükleniyor…' : 'Loading…'}
                </div>
              </div>
            )}
            {lightbox.ext === 'img' ? (
              <img src={lightbox.blobUrl || lightbox.url} alt={lightbox.name} className="max-w-full max-h-full object-contain rounded-lg shadow-2xl" />
            ) : (
              <iframe src={lightbox.blobUrl || lightbox.url} title={lightbox.name} className="w-full h-full bg-white rounded-lg shadow-2xl" />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Phase 86AZ.45 — Assigned Contractor panel — direct-offer awarded result.
// "Send RFQ" akışı kaldırıldı; finalized direct offer'dan contractor +
// fiyat bilgisini gösterir. Award edilmemişse boş state.
function AssignedContractorPanel({ tr, design }) {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    const fetchOffers = async () => {
      if (!design?.id) return;
      try {
        const r = await apiClient.get(`/stand-designs/admin/${design.id}/direct-offers`);
        if (!cancelled) setOffers(r.data?.offers || []);
      } catch {}
      finally { if (!cancelled) setLoading(false); }
    };
    fetchOffers();
    const t = setInterval(fetchOffers, 10000);
    return () => { cancelled = true; clearInterval(t); };
  }, [design?.id]);

  const finalized = offers.find(o => o.status === 'finalized');

  if (loading) {
    return <div className="text-center py-12 text-gray-400 text-sm">{tr ? 'Yükleniyor…' : 'Loading…'}</div>;
  }

  if (!finalized) {
    return (
      <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center" data-testid="assigned-contractor-empty">
        <DollarSign className="h-12 w-12 text-gray-200 mx-auto mb-4" />
        <p className="font-medium text-gray-500 mb-1">{tr ? 'Henüz yüklenici atanmadı' : 'No contractor assigned yet'}</p>
        <p className="text-xs text-gray-400">
          {tr ? '"Fiyat Al" sekmesinden bir contractor\'a teklif gönderin; kabul edip onaylayınca burada görünecek.'
              : 'Send an offer from the "Get Price" tab; once accepted & finalized, the contractor will appear here.'}
        </p>
      </div>
    );
  }

  const currency = finalized.currency || 'USD';
  return (
    <div className="space-y-3" data-testid="assigned-contractor-winner-card">
      <div className="rounded-2xl border-2 border-emerald-400 bg-gradient-to-br from-emerald-50 to-teal-50 p-6 shadow-md">
        <div className="flex items-start gap-4">
          <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center flex-shrink-0 shadow-md">
            <CheckCircle className="h-8 w-8 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="text-xl font-bold text-gray-800 truncate">{finalized.contractor_name}</h3>
              <span className="bg-emerald-500 text-white text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full">
                {tr ? 'Atandı' : 'Awarded'}
              </span>
            </div>
            <p className="text-xs text-gray-500">
              {tr ? 'Onay Tarihi: ' : 'Awarded at: '}
              {finalized.finalized_at ? new Date(finalized.finalized_at).toLocaleString(tr ? 'tr-TR' : 'en-US') : '—'}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3 mt-5">
          <div className="bg-white rounded-xl p-3 border border-emerald-100">
            <p className="text-[10px] text-emerald-600 uppercase font-bold mb-1">{tr ? 'Fiyat/m²' : 'Price/m²'}</p>
            <p className="font-mono font-bold text-emerald-900 text-base">{finalized.price_per_m2} {currency}</p>
          </div>
          <div className="bg-white rounded-xl p-3 border border-emerald-100">
            <p className="text-[10px] text-emerald-600 uppercase font-bold mb-1">{tr ? 'Alan' : 'Area'}</p>
            <p className="font-mono font-bold text-emerald-800 text-base">{finalized.area_m2} m²</p>
          </div>
          <div className="bg-white rounded-xl p-3 border border-emerald-100">
            <p className="text-[10px] text-emerald-600 uppercase font-bold mb-1">{tr ? 'Toplam' : 'Total'}</p>
            <p className="font-mono font-bold text-emerald-900 text-lg">
              {finalized.total_amount != null ? `${finalized.total_amount} ${currency}` : '—'}
            </p>
          </div>
        </div>

        {finalized.admin_note && (
          <div className="mt-3 bg-white/60 rounded-xl px-3 py-2 text-xs text-gray-700">
            <span className="font-bold text-emerald-800">{tr ? 'Sizin Notunuz: ' : 'Your note: '}</span>
            {finalized.admin_note}
          </div>
        )}
        {finalized.contractor_response_note && (
          <div className="mt-2 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 text-xs text-gray-700">
            <span className="font-bold text-amber-800">{tr ? 'Yüklenici Notu: ' : 'Contractor Note: '}</span>
            {finalized.contractor_response_note}
          </div>
        )}
      </div>
    </div>
  );
}

function DirectOfferPanel({ tr, design, onAwarded }) {
  const [contractors, setContractors] = useState([]);
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [contractorId, setContractorId] = useState('');
  const [pricePerM2, setPricePerM2] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [finalizingId, setFinalizingId] = useState(null);
  // Phase 86AZ.47 — Custom confirm modals (no native browser alerts).
  const [confirmFinalize, setConfirmFinalize] = useState(null); // { offer, note }
  const [confirmDelete, setConfirmDelete] = useState(null);     // { offerId }
  const area = Number(design?.stand_area || 0);
  const totalPreview = pricePerM2 && area ? (Number(pricePerM2) * area).toFixed(2) : null;

  const fetchAll = async () => {
    try {
      const [contRes, offRes] = await Promise.all([
        apiClient.get('/stand-designs/admin/contractors-list'),
        apiClient.get(`/stand-designs/admin/${design.id}/direct-offers`),
      ]);
      setContractors(contRes.data || []);
      setOffers(offRes.data?.offers || []);
    } catch {}
    finally { setLoading(false); }
  };
  useEffect(() => { if (design?.id) fetchAll(); /* eslint-disable-next-line */ }, [design?.id]);

  const send = async () => {
    if (!contractorId) { alert(tr ? 'Yüklenici seçin.' : 'Select a contractor.'); return; }
    const p = Number(pricePerM2);
    if (!p || p <= 0) { alert(tr ? 'Geçerli bir m² fiyatı girin.' : 'Enter a valid price/m².'); return; }
    setSubmitting(true);
    try {
      await apiClient.post(`/stand-designs/admin/${design.id}/direct-offer`, {
        contractor_user_id: contractorId,
        price_per_m2: p,
        currency,
        note: note || null,
      });
      setContractorId(''); setPricePerM2(''); setNote('');
      await fetchAll();
    } catch (e) {
      alert(e?.response?.data?.detail || (tr ? 'Gönderilemedi.' : 'Could not send.'));
    } finally { setSubmitting(false); }
  };

  // Phase 86AZ.47 — Build a polite, dynamic default approval note that
  // gets pre-filled into the confirm modal. Admin can tweak before submit.
  const buildDefaultFinalizeNote = (o) => {
    const total = o.total_amount != null ? `${o.total_amount} ${o.currency}` : '—';
    if (tr) {
      return `Sayın ${o.contractor_name},

"${design?.title || 'Stand Üretim Talebi'}" projesi için sunduğunuz ${o.price_per_m2} ${o.currency}/m² (toplam ${total}) tutarındaki fiyat teklifiniz resmi olarak onaylanmıştır. Üretim sürecine ivedilikle başlamanızı rica eder, başarılı bir iş birliği dileriz.

Detaylar için yönetici paneli üzerinden iletişim kurabilirsiniz.

Saygılarımızla,
COP 31 Antalya Platformu`;
    }
    return `Dear ${o.contractor_name},

Your price offer of ${o.price_per_m2} ${o.currency}/m² (total ${total}) for the project "${design?.title || 'Stand Production Request'}" has been officially approved. We kindly ask you to commence the production process at your earliest convenience and wish you a successful collaboration.

For further details, please reach out via the administrator panel.

Best regards,
COP 31 Antalya Platform`;
  };

  const openFinalizeConfirm = (offer) => {
    setConfirmFinalize({ offer, note: buildDefaultFinalizeNote(offer) });
  };

  const finalize = async () => {
    if (!confirmFinalize?.offer) return;
    const offerId = confirmFinalize.offer.id;
    setFinalizingId(offerId);
    try {
      await apiClient.post(`/stand-designs/admin/direct-offer/${offerId}/finalize`, {
        admin_finalize_note: confirmFinalize.note || null,
      });
      setConfirmFinalize(null);
      await fetchAll();
      onAwarded?.();
    } catch (e) {
      alert(e?.response?.data?.detail || (tr ? 'İşlem başarısız.' : 'Action failed.'));
    } finally { setFinalizingId(null); }
  };

  // Phase 86AZ.42 — Hard-delete single offer row (test data / hatalı satır).
  const deleteOffer = async () => {
    if (!confirmDelete?.offerId) return;
    try {
      await apiClient.delete(`/stand-designs/admin/direct-offer/${confirmDelete.offerId}`);
      setConfirmDelete(null);
      await fetchAll();
    } catch (e) {
      alert(e?.response?.data?.detail || (tr ? 'Silinemedi.' : 'Could not delete.'));
    }
  };

  const statusPill = (s) => {
    const map = {
      pending:   { txt: tr ? 'Beklemede' : 'Pending',   cls: 'bg-amber-100 text-amber-700' },
      accepted:  { txt: tr ? 'Kabul Etti' : 'Accepted', cls: 'bg-emerald-100 text-emerald-700' },
      rejected:  { txt: tr ? 'Reddetti' : 'Rejected',   cls: 'bg-red-100 text-red-700' },
      finalized: { txt: tr ? 'Onaylandı' : 'Awarded',   cls: 'bg-blue-100 text-blue-700' },
      cancelled: { txt: tr ? 'İptal' : 'Cancelled',     cls: 'bg-gray-100 text-gray-500' },
    };
    const m = map[s] || { txt: s, cls: 'bg-gray-100 text-gray-600' };
    return <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${m.cls}`}>{m.txt}</span>;
  };

  return (
    <div className="border-t border-gray-100 pt-4 mt-2" data-testid="direct-offer-panel">
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-1.5">
        <DollarSign className="h-3.5 w-3.5" />
        {tr ? 'Doğrudan Fiyat Teklifi' : 'Direct Price Offer'}
      </p>

      {/* Form */}
      <div className="space-y-2 p-3 rounded-xl bg-violet-50 border border-violet-200">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <div>
            <label className="block text-[10px] font-bold text-violet-700 mb-1 uppercase">{tr ? 'Yüklenici' : 'Contractor'}</label>
            <select data-testid="direct-offer-contractor" value={contractorId} onChange={e => setContractorId(e.target.value)}
              className="w-full text-sm border border-violet-200 rounded-lg p-2 bg-white focus:ring-2 focus:ring-violet-300 focus:outline-none">
              <option value="">{tr ? '— Yüklenici seçin —' : '— Select a contractor —'}</option>
              {contractors.map(c => (
                <option key={c.user?.id || c.id} value={c.user?.id || ''}>
                  {c.company_name || c.user?.full_name || c.email}
                </option>
              ))}
            </select>
            {/* Phase 86AZ.50 — Contractor preview (website + about) for picking */}
            {contractorId && (() => {
              const sel = contractors.find(c => (c.user?.id || c.id) === contractorId);
              if (!sel) return null;
              const site = sel.website;
              const about = sel.about_us;
              if (!site && !about) return null;
              return (
                <div className="mt-2 rounded-lg border border-violet-100 bg-white px-2.5 py-2 space-y-1.5" data-testid="contractor-preview-card">
                  {site && (
                    <a href={/^https?:\/\//i.test(site) ? site : `https://${site}`}
                       target="_blank" rel="noopener noreferrer"
                       className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-violet-700 hover:underline">
                      <Globe className="h-3 w-3" /> {site}
                    </a>
                  )}
                  {about && (
                    <p className="text-[11px] text-gray-600 leading-relaxed whitespace-pre-wrap line-clamp-4">{about}</p>
                  )}
                </div>
              );
            })()}
          </div>
          <div>
            <label className="block text-[10px] font-bold text-violet-700 mb-1 uppercase">{tr ? 'Para Birimi' : 'Currency'}</label>
            <select data-testid="direct-offer-currency" value={currency} onChange={e => setCurrency(e.target.value)}
              className="w-full text-sm border border-violet-200 rounded-lg p-2 bg-white focus:ring-2 focus:ring-violet-300 focus:outline-none">
              <option value="USD">USD ($)</option>
              <option value="EUR">EUR (€)</option>
              <option value="TRY">TRY (₺)</option>
              <option value="GBP">GBP (£)</option>
            </select>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <div>
            <label className="block text-[10px] font-bold text-violet-700 mb-1 uppercase">{tr ? 'Fiyat / m²' : 'Price / m²'}</label>
            <input data-testid="direct-offer-price-per-m2" type="number" min="0" step="0.01"
              value={pricePerM2} onChange={e => setPricePerM2(e.target.value)}
              placeholder="250.00"
              className="w-full text-sm border border-violet-200 rounded-lg p-2 bg-white focus:ring-2 focus:ring-violet-300 focus:outline-none" />
          </div>
          <div>
            <label className="block text-[10px] font-bold text-violet-700 mb-1 uppercase">
              {tr ? `Toplam (${area} m²)` : `Total (${area} m²)`}
            </label>
            <div className="text-sm border border-violet-200 rounded-lg p-2 bg-white font-mono font-bold text-violet-900">
              {totalPreview ? `${totalPreview} ${currency}` : '—'}
            </div>
          </div>
        </div>
        <textarea data-testid="direct-offer-note" value={note} onChange={e => setNote(e.target.value)}
          maxLength={1000} rows={2}
          placeholder={tr ? 'Yüklenici için kısa not (opsiyonel)…' : 'Short note for contractor (optional)…'}
          className="w-full text-sm border border-violet-200 rounded-lg p-2 bg-white focus:ring-2 focus:ring-violet-300 focus:outline-none" />
        <button type="button" data-testid="direct-offer-send" disabled={submitting || !contractorId || !pricePerM2}
          onClick={send}
          className="w-full px-3 py-2 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-sm font-bold disabled:opacity-40 disabled:cursor-not-allowed">
          {submitting ? (tr ? 'Gönderiliyor…' : 'Sending…') : (tr ? 'Teklifi Gönder' : 'Send Offer')}
        </button>
      </div>

      {/* Offer history */}
      {loading ? (
        <p className="text-xs text-gray-400 mt-3 text-center">{tr ? 'Yükleniyor…' : 'Loading…'}</p>
      ) : offers.length === 0 ? (
        <p className="text-xs text-gray-400 mt-3 text-center">{tr ? 'Henüz teklif gönderilmedi.' : 'No offers sent yet.'}</p>
      ) : (
        <div className="mt-3 space-y-2">
          {offers.map(o => (
            <div key={o.id} className="border border-gray-200 rounded-xl p-3 bg-white" data-testid={`direct-offer-row-${o.id}`}>
              <div className="flex items-start justify-between gap-2 mb-1">
                <div className="min-w-0">
                  <p className="text-sm font-bold text-gray-800 truncate">{o.contractor_name}</p>
                  <p className="text-[10px] text-gray-400 mt-0.5">
                    {new Date(o.created_at).toLocaleString(tr ? 'tr-TR' : 'en-US')}
                  </p>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  {statusPill(o.status)}
                  {/* Phase 86AZ.42 — Tek satır silme */}
                  <button type="button"
                    data-testid={`direct-offer-delete-${o.id}`}
                    title={tr ? 'Bu satırı sil' : 'Delete this row'}
                    onClick={() => setConfirmDelete({ offerId: o.id })}
                    className="h-6 w-6 rounded-full bg-gray-100 hover:bg-red-100 text-gray-400 hover:text-red-600 flex items-center justify-center transition-colors">
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 my-2 text-xs">
                <div>
                  <p className="text-[10px] text-gray-400 uppercase">{tr ? 'Fiyat/m²' : 'Price/m²'}</p>
                  <p className="font-mono font-bold text-gray-800">{o.price_per_m2} {o.currency}</p>
                </div>
                <div>
                  <p className="text-[10px] text-gray-400 uppercase">{tr ? 'Alan' : 'Area'}</p>
                  <p className="font-mono text-gray-700">{o.area_m2} m²</p>
                </div>
                <div>
                  <p className="text-[10px] text-gray-400 uppercase">{tr ? 'Toplam' : 'Total'}</p>
                  <p className="font-mono font-bold text-violet-700">
                    {o.total_amount != null ? `${o.total_amount} ${o.currency}` : '—'}
                  </p>
                </div>
              </div>
              {o.admin_note && (
                <p className="text-[11px] text-gray-500 bg-gray-50 rounded-lg px-2 py-1 mt-1">
                  <span className="font-bold">{tr ? 'Notunuz: ' : 'Your note: '}</span>{o.admin_note}
                </p>
              )}
              {o.contractor_response_note && (
                <p className="text-[11px] text-gray-600 bg-amber-50 border border-amber-100 rounded-lg px-2 py-1 mt-1">
                  <span className="font-bold">{tr ? 'Yüklenici Notu: ' : 'Contractor Note: '}</span>{o.contractor_response_note}
                </p>
              )}
              {o.admin_finalize_note && (
                <p className="text-[11px] text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg px-2 py-1 mt-1 whitespace-pre-wrap">
                  <span className="font-bold">{tr ? 'Onay Notu: ' : 'Approval Note: '}</span>{o.admin_finalize_note}
                </p>
              )}
              {o.status === 'accepted' && (
                <button type="button" data-testid={`direct-offer-finalize-${o.id}`}
                  disabled={finalizingId === o.id} onClick={() => openFinalizeConfirm(o)}
                  className="mt-2 w-full px-3 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold disabled:opacity-40 inline-flex items-center justify-center gap-1.5">
                  <CheckCircle className="h-3.5 w-3.5" />
                  {finalizingId === o.id ? (tr ? 'Onaylanıyor…' : 'Awarding…') : (tr ? 'Bu Teklifi Onayla & Yükleniciyi Ata' : 'Award & Assign Contractor')}
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Phase 86AZ.47 — Custom Finalize Confirm Modal (no window.confirm) */}
      {confirmFinalize && (
        <div className="fixed inset-0 z-[10001] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setConfirmFinalize(null); }}
          data-testid="finalize-confirm-modal">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 bg-emerald-50">
              <div className="flex items-center gap-2 text-emerald-700">
                <CheckCircle className="h-5 w-5" />
                <h3 className="font-bold text-base">
                  {tr ? 'Teklifi Onayla & Yükleniciyi Ata' : 'Award & Assign Contractor'}
                </h3>
              </div>
              <p className="text-xs text-emerald-700/80 mt-1">
                {tr
                  ? `${confirmFinalize.offer.contractor_name} — ${confirmFinalize.offer.price_per_m2} ${confirmFinalize.offer.currency}/m² · Toplam ${confirmFinalize.offer.total_amount} ${confirmFinalize.offer.currency}`
                  : `${confirmFinalize.offer.contractor_name} — ${confirmFinalize.offer.price_per_m2} ${confirmFinalize.offer.currency}/m² · Total ${confirmFinalize.offer.total_amount} ${confirmFinalize.offer.currency}`}
              </p>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <label className="block text-[11px] font-bold text-gray-600 uppercase tracking-wide mb-1.5">
                  {tr ? 'Onay Notu (yükleniciye iletilecek)' : 'Approval note (sent to contractor)'}
                </label>
                <textarea
                  data-testid="finalize-confirm-note"
                  value={confirmFinalize.note}
                  onChange={e => setConfirmFinalize({ ...confirmFinalize, note: e.target.value })}
                  rows={8}
                  className="w-full text-sm border border-gray-200 rounded-xl p-3 bg-white focus:ring-2 focus:ring-emerald-300 focus:outline-none font-sans leading-relaxed"
                />
                <p className="text-[10px] text-gray-400 mt-1">
                  {tr
                    ? 'Bu metin teklif kaydında saklanır ve onaylanan yükleniciye gönderilir.'
                    : 'This text is stored on the offer and shared with the awarded contractor.'}
                </p>
              </div>
              <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                {tr
                  ? 'Onay sonrası bu işe ait diğer tüm yüklenici teklifleri otomatik reddedilecek, sohbetleri kapatılacak ve nazik bir bilgilendirme e-postası iletilecektir.'
                  : 'After approval, all other contractor offers for this job will be auto-rejected, their chats will be closed and a polite notification email will be sent.'}
              </div>
            </div>
            <div className="px-5 py-3 border-t border-gray-100 bg-gray-50 flex justify-end gap-2">
              <button type="button"
                data-testid="finalize-confirm-cancel"
                onClick={() => setConfirmFinalize(null)}
                disabled={finalizingId === confirmFinalize.offer.id}
                className="px-4 py-2 rounded-lg text-sm font-semibold text-gray-600 hover:bg-gray-100 disabled:opacity-40">
                {tr ? 'Vazgeç' : 'Cancel'}
              </button>
              <button type="button"
                data-testid="finalize-confirm-submit"
                onClick={finalize}
                disabled={finalizingId === confirmFinalize.offer.id}
                className="px-4 py-2 rounded-lg text-sm font-bold bg-emerald-600 hover:bg-emerald-700 text-white inline-flex items-center gap-1.5 disabled:opacity-40">
                <CheckCircle className="h-4 w-4" />
                {finalizingId === confirmFinalize.offer.id
                  ? (tr ? 'Onaylanıyor…' : 'Awarding…')
                  : (tr ? 'Onayla & Ata' : 'Approve & Assign')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Phase 86AZ.47 — Custom Delete Confirm Modal */}
      {confirmDelete && (
        <div className="fixed inset-0 z-[10001] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setConfirmDelete(null); }}
          data-testid="offer-delete-confirm-modal">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 bg-red-50 flex items-center gap-2 text-red-700">
              <X className="h-5 w-5" />
              <h3 className="font-bold text-base">{tr ? 'Teklif Satırını Sil' : 'Delete Offer Row'}</h3>
            </div>
            <div className="p-5 text-sm text-gray-700">
              {tr ? 'Bu teklif satırı kalıcı olarak silinecek. Devam etmek istiyor musunuz?'
                  : 'This offer row will be permanently deleted. Do you want to continue?'}
            </div>
            <div className="px-5 py-3 border-t border-gray-100 bg-gray-50 flex justify-end gap-2">
              <button type="button"
                data-testid="offer-delete-cancel"
                onClick={() => setConfirmDelete(null)}
                className="px-4 py-2 rounded-lg text-sm font-semibold text-gray-600 hover:bg-gray-100">
                {tr ? 'Vazgeç' : 'Cancel'}
              </button>
              <button type="button"
                data-testid="offer-delete-submit"
                onClick={deleteOffer}
                className="px-4 py-2 rounded-lg text-sm font-bold bg-red-600 hover:bg-red-700 text-white">
                {tr ? 'Sil' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StandDesignsAdminBar({ language }) {
  const tr = language === 'tr';
  const [open, setOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const handleWipe = async () => {
    setSubmitting(true);
    setResult(null);
    try {
      const r = await apiClient.post(`/stand-designs/admin/wipe-test-data`, { confirm: 'DELETE-ALL' });
      setResult(r.data);
      // Reload after a beat so the underlying section refetches empty state.
      setTimeout(() => { window.location.reload(); }, 1400);
    } catch (e) {
      setResult({ error: e?.response?.data?.detail || 'failed' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mb-3 flex items-center justify-end gap-2" data-testid="stand-designs-admin-bar">
      <button
        type="button"
        data-testid="wipe-test-data-open"
        onClick={() => { setOpen(true); setConfirmText(''); setResult(null); }}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-50 hover:bg-red-100 text-red-700 border border-red-200 text-xs font-semibold transition-colors"
      >
        <Trash2 className="h-3.5 w-3.5" />
        {tr ? 'Test Verilerini Sil' : 'Wipe Test Data'}
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-center justify-center px-4"
          onClick={(e) => { if (e.target === e.currentTarget && !submitting) setOpen(false); }}
        >
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
            <div className="px-5 py-4 bg-gradient-to-r from-red-500 to-red-600 text-white">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5" />
                <h3 className="font-bold text-base">
                  {tr ? 'Tüm Tasarım Verilerini Sil' : 'Wipe All Design Data'}
                </h3>
              </div>
              <p className="text-xs text-white/85 mt-1.5">
                {tr
                  ? 'Bu işlem geri alınamaz. Tüm tasarım talepleri, mesajlar, teklif çağrıları ve yüklenen dosyalar hem müşteri hem yönetici tarafında silinir.'
                  : 'This cannot be undone. All stand design requests, messages, RFQs, bids and uploaded files will be removed for both customer and admin sides.'}
              </p>
            </div>
            <div className="p-5 space-y-3">
              {result && !result.error && (
                <div className="rounded-xl bg-emerald-50 border border-emerald-200 px-3 py-2 text-xs text-emerald-700 space-y-0.5">
                  <p className="font-semibold">{tr ? 'Tamamlandı:' : 'Done:'}</p>
                  <p>· {tr ? 'Talepler' : 'Requests'}: {result.deleted?.requests || 0}</p>
                  <p>· {tr ? 'Mesajlar' : 'Messages'}: {result.deleted?.messages || 0}</p>
                  <p>· RFQ: {result.deleted?.rfqs || 0} · {tr ? 'Teklifler' : 'Bids'}: {result.deleted?.bids || 0}</p>
                  <p>· {tr ? 'Bildirimler' : 'Notifications'}: {result.deleted?.notifications || 0}</p>
                  <p>· {tr ? 'Dosyalar' : 'Files'}: {result.files_removed || 0}</p>
                  <p className="text-[10px] text-emerald-600 mt-1">{tr ? 'Sayfa yenileniyor…' : 'Reloading…'}</p>
                </div>
              )}
              {result?.error && (
                <div className="rounded-xl bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700">
                  {result.error}
                </div>
              )}
              {!result?.deleted && (
                <>
                  <p className="text-sm text-gray-600">
                    {tr ? 'Onaylamak için aşağıya '  : 'To confirm, type '}
                    <code className="font-mono bg-gray-100 text-red-600 px-1.5 py-0.5 rounded text-xs font-bold">DELETE-ALL</code>
                    {tr ? ' yazın.' : ' below.'}
                  </p>
                  <input
                    data-testid="wipe-confirm-input"
                    type="text"
                    value={confirmText}
                    // Phase 86AZ.31 hotfix — Visual `uppercase` is a CSS transform
                    // only; the actual state stays as typed. We normalise here so
                    // the equality check below matches what the user sees.
                    onChange={e => setConfirmText(e.target.value.toUpperCase())}
                    placeholder="DELETE-ALL"
                    disabled={submitting}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono uppercase tracking-wide focus:ring-2 focus:ring-red-200 focus:outline-none disabled:opacity-50"
                    autoFocus
                  />
                  <div className="flex justify-end gap-2 pt-2">
                    <button
                      type="button"
                      data-testid="wipe-cancel"
                      disabled={submitting}
                      onClick={() => setOpen(false)}
                      className="px-3 py-1.5 rounded-lg bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 text-sm font-medium disabled:opacity-50"
                    >
                      {tr ? 'İptal' : 'Cancel'}
                    </button>
                    <button
                      type="button"
                      data-testid="wipe-confirm-btn"
                      disabled={submitting || confirmText.trim().toUpperCase() !== 'DELETE-ALL'}
                      onClick={handleWipe}
                      className="px-4 py-1.5 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-bold disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                      {submitting ? (tr ? 'Siliniyor…' : 'Wiping…') : (tr ? 'Sil' : 'Wipe')}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function AdminStandDesignsSection({ language, embedded = false }) {
  const tr = language === 'tr';
  const token = localStorage.getItem('cop31_ultra_token');
  const headers = { Authorization: `Bearer ${token}` };

  const [designs, setDesigns] = useState([]);
  const [selectedDesign, setSelectedDesign] = useState(null);
  const [contractors, setContractors] = useState([]);
  const [selectedContractors, setSelectedContractors] = useState([]);
  const [messages, setMessages] = useState([]);
  const [bids, setBids] = useState([]);
  const [msgText, setMsgText] = useState('');
  const [msgFile, setMsgFile] = useState(null);
  const [msgLoading, setMsgLoading] = useState(false);
  const [previewFile, setPreviewFile] = useState(null); // {url, name}
  const [assignLoading, setAssignLoading] = useState(false);
  const [approveLoading, setApproveLoading] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('list'); // list | detail
  const [detailTab, setDetailTab] = useState('info'); // info | chat | assign | bids | contractor-chat
  // Phase 86AP — When admin clicks a country in the sidebar that has no design
  // yet we surface a country-aware empty state instead of leaving the previous
  // brief on screen. This holds {tenant_id, country} of that latest pick.
  const [selectedEmptyCountry, setSelectedEmptyCountry] = useState(null);
  const msgFileRef = useRef(null);
  const messagesEndRef = useRef(null);
  const pollMsgRef = useRef(null);
  const selectedDesignRef = useRef(null);
  // Contractor chat state
  const [contractorChatRfq, setContractorChatRfq] = useState(null);
  const [contractorMsgs, setContractorMsgs] = useState([]);
  const [contractorMsgText, setContractorMsgText] = useState('');
  const [contractorMsgFile, setContractorMsgFile] = useState(null);
  const [contractorMsgLoading, setContractorMsgLoading] = useState(false);
  const [contractorGlobalUnread, setContractorGlobalUnread] = useState(0);
  const contractorMsgFileRef = useRef(null);
  const contractorMsgsEndRef = useRef(null);
  const pollContractorRef = useRef(null);
  const contractorChatRfqRef = useRef(null);
  // Bid detail modal
  const [bidDetailModal, setBidDetailModal] = useState(null); // bid object or null

  useEffect(() => {
    fetchDesigns();
    fetchContractors();
    // Poll contractor global unread every 8s
    const unreadInterval = setInterval(async () => {
      try {
        const r = await apiClient.get(`/stand-designs/admin/contractor-unread-count`);
        setContractorGlobalUnread(r.data.unread || 0);
      } catch {}
    }, 8000);
    // Initial fetch
    apiClient.get(`/stand-designs/admin/contractor-unread-count`)
      .then(r => setContractorGlobalUnread(r.data.unread || 0)).catch(() => {});
    return () => clearInterval(unreadInterval);
  }, []);

  // Poll messages every 5s while a design is open
  useEffect(() => {
    selectedDesignRef.current = selectedDesign;
    clearInterval(pollMsgRef.current);
    if (selectedDesign) {
      fetchMessages(selectedDesign.id);
      fetchBids(selectedDesign.id);
      // Mark customer messages as read
      adminMarkRead(selectedDesign.id);
      pollMsgRef.current = setInterval(() => {
        if (selectedDesignRef.current) fetchMessages(selectedDesignRef.current.id);
      }, 5000);
    }
    return () => clearInterval(pollMsgRef.current);
  }, [selectedDesign?.id]);

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);
  useEffect(() => { contractorMsgsEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [contractorMsgs]);

  const fetchContractorMsgs = async (rfqId) => {
    try {
      const r = await apiClient.get(`/stand-designs/admin/rfq/${rfqId}/messages`);
      setContractorMsgs(r.data || []);
      // Mark as read
      await apiClient.post(`/stand-designs/admin/rfq/${rfqId}/messages/mark-read`, {});
      setContractorGlobalUnread(prev => Math.max(0, prev - 1));
    } catch {}
  };

  const openContractorChat = (rfq) => {
    setContractorChatRfq(rfq);
    contractorChatRfqRef.current = rfq;
    clearInterval(pollContractorRef.current);
    fetchContractorMsgs(rfq.id);
    pollContractorRef.current = setInterval(() => {
      if (contractorChatRfqRef.current) fetchContractorMsgs(contractorChatRfqRef.current.id);
    }, 5000);
  };

  const closeContractorChat = () => {
    clearInterval(pollContractorRef.current);
    setContractorChatRfq(null);
    contractorChatRfqRef.current = null;
    setContractorMsgs([]);
  };

  const sendContractorMsg = async () => {
    if (!contractorMsgText.trim() && !contractorMsgFile) return;
    setContractorMsgLoading(true);
    try {
      const fd = new FormData();
      fd.append('message', contractorMsgText);
      if (contractorMsgFile) fd.append('attachment', contractorMsgFile);
      const r = await apiClient.post(`/stand-designs/admin/rfq/${contractorChatRfq.id}/messages`, fd, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }
      });
      setContractorMsgs(prev => [...prev, r.data]);
      setContractorMsgText('');
      setContractorMsgFile(null);
    } catch (e) {
      toast.error(tr ? 'Mesaj gönderilemedi.' : 'Could not send message.');
    } finally { setContractorMsgLoading(false); }
  };

  const adminMarkRead = async (designId) => {
    try {
      await apiClient.post(`/stand-designs/admin/${designId}/messages/mark-read`, {});
    } catch {}
  };

  const fetchDesigns = async () => {
    setLoading(true);
    try {
      const r = await apiClient.get(`/stand-designs/admin/all`);
      setDesigns(r.data?.designs || []);
    } catch {}
    finally { setLoading(false); }
  };

  const fetchContractors = async () => {
    try {
      const r = await apiClient.get(`/stand-designs/admin/contractors-list`);
      setContractors(r.data || []);
    } catch {}
  };

  const fetchMessages = async (designId) => {
    try {
      const r = await apiClient.get(`/stand-designs/admin/${designId}/messages`);
      setMessages(r.data || []);
    } catch {}
  };

  const fetchBids = async (designId) => {
    try {
      const r = await apiClient.get(`/stand-designs/admin/${designId}/bids`);
      setBids(r.data || []);
    } catch {}
  };

  const openDesign = async (design) => {
    try {
      const r = await apiClient.get(`/stand-designs/admin/${design.id}`);
      setSelectedDesign(r.data);
      const assigned = (r.data?.rfqs || []).map(rfq => rfq.contractor_tenant_id);
      setSelectedContractors(assigned);
    } catch {
      setSelectedDesign(design);
    }
    setActiveView('detail');
    setDetailTab('info');
  };

  // Phase 86AM — Sibling TenantStandSummary cards dispatch this event when a
  // country card is clicked. We look up the design for that tenant; if none
  // exists yet (customer hasn't uploaded a design), surface a clear toast so
  // the admin understands why the panel below stays empty.
  useEffect(() => {
    const handler = async (e) => {
      const tenantId = e.detail?.tenant_id;
      const country = e.detail?.country || '';
      if (!tenantId) return;
      // Refresh designs first so a freshly-uploaded one is picked up
      let pool = designs;
      try {
        const r = await apiClient.get(`/stand-designs/admin/all`);
        pool = r.data?.designs || [];
        setDesigns(pool);
      } catch {}
      const match = pool.find(d => d.tenant_id === tenantId);
      if (match) {
        setSelectedEmptyCountry(null);
        openDesign(match);
        // Smooth scroll to detail panel after open
        setTimeout(() => {
          document.querySelector('[data-testid="admin-design-detail-panel"]')
            ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 50);
      } else {
        // Phase 86AP — Clear stale design from the previous country and pivot
        // the right panel into a country-aware empty state.
        setSelectedDesign(null);
        setActiveView('list');
        setSelectedEmptyCountry({ tenant_id: tenantId, country });
      }
    };
    window.addEventListener('cop31:open-stand-design-by-tenant', handler);
    return () => window.removeEventListener('cop31:open-stand-design-by-tenant', handler);
  }, [designs, tr]);

  const sendMessage = async () => {
    if (!msgText.trim() && !msgFile) return;
    setMsgLoading(true);
    try {
      const fd = new FormData();
      fd.append('message', msgText);
      if (msgFile) fd.append('attachment', msgFile);
      const r = await apiClient.post(`/stand-designs/admin/${selectedDesign.id}/messages`, fd, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }
      });
      setMessages(prev => [...prev, r.data]);
      setMsgText(''); setMsgFile(null);
    } catch {}
    finally { setMsgLoading(false); }
  };

  const assignContractors = async () => {
    if (selectedContractors.length === 0) {
      toast.error(tr ? 'En az bir yüklenici seçin.' : 'Select at least one contractor.'); return;
    }
    setAssignLoading(true);
    try {
      await apiClient.post(`/stand-designs/admin/${selectedDesign.id}/assign`,
        selectedContractors
      );
      toast.success(tr ? 'Yüklenicilere iletildi!' : 'Sent to contractors!');
      fetchDesigns();
      openDesign(selectedDesign);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Hata oluştu.');
    } finally { setAssignLoading(false); }
  };

  const approveBid = async (bidId) => {
    setApproveLoading(p => ({ ...p, [bidId]: true }));
    try {
      await apiClient.post(`/stand-designs/admin/${selectedDesign.id}/approve-bid/${bidId}`, {});
      toast.success(tr ? 'Teklif onaylandı!' : 'Bid approved!');
      fetchBids(selectedDesign.id);
      fetchDesigns();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Hata oluştu.');
    } finally { setApproveLoading(p => ({ ...p, [bidId]: false })); }
  };

  const DESIGN_STATUS_COLORS = {
    submitted: 'bg-blue-100 text-blue-700',
    under_review: 'bg-purple-100 text-purple-700',
    rfq_sent: 'bg-amber-100 text-amber-700',
    bid_received: 'bg-orange-100 text-orange-700',
    awarded: 'bg-emerald-100 text-emerald-700',
    approved: 'bg-emerald-100 text-emerald-700',
    rejected: 'bg-red-100 text-red-700',
  };
  const DESIGN_STATUS_LABELS = {
    submitted: tr ? 'Gönderildi' : 'Submitted',
    under_review: tr ? 'İnceleniyor' : 'Under Review',
    rfq_sent: tr ? 'Teklif Alınıyor' : 'RFQ Sent',
    bid_received: tr ? 'Teklif Geldi' : 'Bid Received',
    awarded: tr ? 'Tamamlandı' : 'Awarded',
    approved: tr ? 'Onaylandı' : 'Approved',
    rejected: tr ? 'Reddedildi' : 'Rejected',
  };

  // Phase 86AZ.30 — Approve/Reject action bar — defined outside this
  // component (see top-level `DesignReviewBar` function below) so polling
  // re-renders don't unmount it and reset its local state. (Phase 86AZ.36 fix.)

  const CURRENCY_LABELS = { usd: 'USD', eur: 'EUR', try: 'TRY', gbp: 'GBP' };

  if (loading) return (
    <div className="flex justify-center py-12">
      <div className="animate-spin h-8 w-8 border-4 border-orange-500 border-t-transparent rounded-full" />
    </div>
  );

  if (activeView === 'detail' && selectedDesign) return (
    <div className="space-y-5" data-testid="admin-design-detail-panel">
      {/* Phase 86AM — Active country indicator */}
      <div className="flex items-center justify-between bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-2xl px-4 py-2.5">
        <div className="flex items-center gap-3 min-w-0">
          <div className="h-9 w-9 rounded-full bg-gradient-to-br from-orange-400 to-red-500 flex items-center justify-center flex-shrink-0">
            <span className="text-white text-xs font-bold">{(selectedDesign.tenant_name || '?')[0]?.toUpperCase()}</span>
          </div>
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-wider text-orange-600 font-bold">{tr ? 'Aktif Tasarım' : 'Active Design'}</p>
            <p className="font-bold text-gray-800 text-sm truncate">{selectedDesign.tenant_name} <span className="text-gray-400 font-normal">·</span> <span className="text-gray-600">{selectedDesign.title}</span></p>
          </div>
        </div>
        <button onClick={() => setActiveView('list')} data-testid="admin-design-back-btn" className="flex-shrink-0 flex items-center gap-1.5 text-xs text-orange-700 hover:text-orange-900 font-semibold bg-white/80 hover:bg-white border border-orange-200 rounded-full px-3 py-1.5 transition-colors">
          ← {tr ? 'Geri' : 'Back'}
        </button>
      </div>

      {/* Detail Tabs */}
      <div className="flex gap-2 border-b border-gray-200 pb-0 overflow-x-auto">
        {[
          { id: 'info', label: tr ? 'Stand Brief' : 'Stand Brief' },
          {
            id: 'chat',
            label: tr ? 'Müşteri Yazışması' : 'Customer Chat',
            badge: selectedDesign?.customer_unread > 0 ? selectedDesign.customer_unread : null,
          },
          // Phase 86AZ.41 — Direct price offer flow taşındı: ayrı "Get Price" tab.
          { id: 'get-price', label: tr ? 'Fiyat Al' : 'Get Price' },
          // Phase 86AZ.39 — Bids sekmesi şimdilik gizlendi (kullanıcı talebi).
          // { id: 'bids', label: tr ? `Teklifler (${bids.length})` : `Bids (${bids.length})` },
          { id: 'assign', label: tr ? 'Atanmış Yüklenici' : 'Assigned Contractor' },
          {
            id: 'contractor-chat',
            label: tr ? 'Yüklenici Yazışması' : 'Contractor Chat',
            badge: contractorGlobalUnread > 0 ? contractorGlobalUnread : (selectedDesign?.contractor_unread > 0 ? selectedDesign.contractor_unread : null)
          },
        ].map(tab => (
          <button key={tab.id} onClick={() => {
            if (tab.id !== 'contractor-chat') {
              closeContractorChat();
            } else if (tab.id === 'contractor-chat' && !contractorChatRfqRef.current && (selectedDesign?.rfqs || []).length > 0) {
              // Auto-open first contractor when switching to this tab
              openContractorChat(selectedDesign.rfqs[0]);
            }
            setDetailTab(tab.id);
          }}
            className={`relative px-4 py-2.5 text-sm font-medium border-b-2 transition-colors flex-shrink-0 flex items-center gap-1.5 ${
              detailTab === tab.id ? 'border-orange-500 text-orange-600' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}>
            {tab.label}
            {tab.badge && (
              <span className="h-4 min-w-4 px-1 bg-orange-500 text-white text-xs font-bold rounded-full flex items-center justify-center animate-pulse">
                {tab.badge}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* INFO */}
      {/* INFO */}
      {detailTab === 'info' && (
        <div className="bg-white rounded-2xl border border-gray-100 p-6 space-y-4">
          <div className="flex items-start justify-between">
            <h2 className="font-bold text-gray-800 text-lg">{selectedDesign.title}</h2>
            <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${DESIGN_STATUS_COLORS[selectedDesign.status] || 'bg-gray-100 text-gray-600'}`}>
              {DESIGN_STATUS_LABELS[selectedDesign.status] || selectedDesign.status}
            </span>
          </div>
          <p className="text-sm text-gray-500">{tr ? 'Müşteri:' : 'Customer:'} <strong>{selectedDesign.tenant_name}</strong></p>

          {/* Phase 86AZ.30 — Reviewer banner (visible after approve/reject) */}
          {selectedDesign.review_status === 'approved' && (
            <div className="px-4 py-3 rounded-xl border border-emerald-200 bg-emerald-50 flex items-start gap-3">
              <CheckCircle className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-bold text-emerald-800">{tr ? 'Tasarım onaylandı.' : 'Design approved.'}</p>
                {selectedDesign.reviewer_note && <p className="text-xs text-emerald-700 mt-1 whitespace-pre-wrap">{selectedDesign.reviewer_note}</p>}
                {selectedDesign.reviewed_at && (
                  <p className="text-[10px] text-emerald-600/70 mt-1">
                    {new Date(selectedDesign.reviewed_at).toLocaleString(tr ? 'tr-TR' : 'en-US')}
                  </p>
                )}
              </div>
            </div>
          )}
          {selectedDesign.review_status === 'rejected' && (
            <div className="px-4 py-3 rounded-xl border border-red-200 bg-red-50 flex items-start gap-3">
              <XCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-bold text-red-800">{tr ? 'Tasarım reddedildi.' : 'Design rejected.'}</p>
                {selectedDesign.reviewer_note && <p className="text-xs text-red-700 mt-1 whitespace-pre-wrap">{selectedDesign.reviewer_note}</p>}
                {selectedDesign.reviewed_at && (
                  <p className="text-[10px] text-red-600/70 mt-1">
                    {new Date(selectedDesign.reviewed_at).toLocaleString(tr ? 'tr-TR' : 'en-US')}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Description — render rich HTML */}
          {selectedDesign.description && selectedDesign.description !== '<p></p>' && (
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5">{tr ? 'Notlar / Özel İstekler' : 'Notes / Special Requests'}</p>
              <div
                className="text-sm text-gray-700 leading-relaxed bg-gray-50 rounded-xl px-4 py-3 border border-gray-100
                  [&_ul]:list-disc [&_ul]:pl-4 [&_ol]:list-decimal [&_ol]:pl-4
                  [&_li]:my-0.5 [&_strong]:font-bold [&_em]:italic [&_s]:line-through
                  [&_hr]:border-gray-200 [&_p]:my-1"
                dangerouslySetInnerHTML={{ __html: selectedDesign.description }}
              />
            </div>
          )}

          {/* Stand info — grid cards (only non-empty fields) */}
          {[
            { label: 'Hall', value: selectedDesign.hall },
            { label: 'Stand No', value: selectedDesign.stand_no },
            { label: tr ? 'Alan' : 'Area', value: selectedDesign.stand_area ? `${selectedDesign.stand_area} m²` : null },
          ].some(i => i.value) && (
            <div className="grid grid-cols-3 gap-3">
              {[
                { label: 'Hall', value: selectedDesign.hall },
                { label: 'Stand No', value: selectedDesign.stand_no },
                { label: tr ? 'Alan' : 'Area', value: selectedDesign.stand_area ? `${selectedDesign.stand_area} m²` : null },
              ].map(({ label, value }) => (
                <div key={label} className={`rounded-xl p-3 border ${value ? 'bg-emerald-50 border-emerald-100' : 'bg-gray-50 border-gray-100'}`}>
                  <p className="text-xs text-gray-400 mb-0.5">{label}</p>
                  <p className="font-bold text-emerald-800 text-sm font-mono">{value || '—'}</p>
                </div>
              ))}
            </div>
          )}

          {/* Phase 86AZ.30 — Deadline + Budget surface */}
          {(selectedDesign.deadline || selectedDesign.budget_min || selectedDesign.budget_max) && (
            <div className="grid grid-cols-2 gap-3">
              {selectedDesign.deadline && (
                <div className="rounded-xl p-3 border border-purple-100 bg-purple-50">
                  <p className="text-xs text-gray-400 mb-0.5">{tr ? 'Termin' : 'Deadline'}</p>
                  <p className="font-bold text-purple-800 text-sm">
                    {new Date(selectedDesign.deadline).toLocaleDateString(tr ? 'tr-TR' : 'en-US', { day: 'numeric', month: 'long', year: 'numeric' })}
                  </p>
                </div>
              )}
              {(selectedDesign.budget_min || selectedDesign.budget_max) && (
                <div className="rounded-xl p-3 border border-amber-100 bg-amber-50">
                  <p className="text-xs text-gray-400 mb-0.5">{tr ? 'Bütçe Aralığı' : 'Budget Range'}</p>
                  <p className="font-bold text-amber-800 text-sm">
                    {selectedDesign.budget_min ? `${(selectedDesign.budget_min).toLocaleString()} ` : ''}
                    {selectedDesign.budget_min && selectedDesign.budget_max ? '–' : ''}
                    {selectedDesign.budget_max ? ` ${(selectedDesign.budget_max).toLocaleString()}` : ''}
                    <span className="text-xs ml-1 uppercase font-mono text-amber-600">{selectedDesign.budget_currency || 'USD'}</span>
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Design files — compact grid cards */}
          {selectedDesign.design_files?.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                <Paperclip className="h-3.5 w-3.5" />
                {tr ? 'Tasarım Dosyaları' : 'Design Files'}
                <span className="text-gray-300 font-normal">({selectedDesign.design_files.length})</span>
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                {selectedDesign.design_files.map((f, i) => {
                  const isPdf = (f.filename || '').endsWith('.pdf') || (f.original_name || '').endsWith('.pdf');
                  const fileUrl = `${window.location.origin}/api/uploads/${f.filename}`;
                  const sizeMb = f.size ? (f.size < 1024*1024 ? `${(f.size/1024).toFixed(0)} KB` : `${(f.size/1024/1024).toFixed(1)} MB`) : '';
                  return (
                    <button key={i} type="button"
                      onClick={() => setPreviewFile({ url: fileUrl, name: f.original_name, type: isPdf ? 'pdf' : 'ppt' })}
                      className="group flex flex-col gap-1.5 p-2.5 border border-gray-200 rounded-xl bg-gray-50 hover:border-orange-300 hover:bg-orange-50 transition-all text-left w-full">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold ${isPdf ? 'bg-red-100 text-red-600' : 'bg-orange-100 text-orange-600'}`}>
                        {isPdf ? 'PDF' : 'PPT'}
                      </div>
                      <p className="text-xs text-gray-700 font-medium leading-tight line-clamp-2 break-all">{f.original_name}</p>
                      {sizeMb && <p className="text-xs text-gray-400">{sizeMb}</p>}
                      <span className="text-xs text-orange-500 group-hover:underline flex items-center gap-0.5 mt-auto">
                        <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/></svg>
                        {tr ? 'Önizle' : 'Preview'}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Phase 86AZ.30 — Technical files (DWG / engineering PDFs / PPT).
              Tracked independently from design files; we surface them here so
              admin can review the production brief alongside the creative one. */}
          {selectedDesign.technical_files?.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                <Paperclip className="h-3.5 w-3.5" />
                {tr ? 'Teknik Dosyalar' : 'Technical Files'}
                <span className="text-gray-300 font-normal">({selectedDesign.technical_files.length})</span>
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                {selectedDesign.technical_files.map((f, i) => {
                  const isPdf = (f.filename || '').endsWith('.pdf') || (f.original_name || '').endsWith('.pdf');
                  const fileUrl = `${window.location.origin}/api/uploads/${f.filename}`;
                  const sizeMb = f.size ? (f.size < 1024*1024 ? `${(f.size/1024).toFixed(0)} KB` : `${(f.size/1024/1024).toFixed(1)} MB`) : '';
                  return (
                    <button key={i} type="button"
                      onClick={() => setPreviewFile({ url: fileUrl, name: f.original_name, type: isPdf ? 'pdf' : 'ppt' })}
                      className="group flex flex-col gap-1.5 p-2.5 border border-gray-200 rounded-xl bg-gray-50 hover:border-blue-300 hover:bg-blue-50 transition-all text-left w-full">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold ${isPdf ? 'bg-red-100 text-red-600' : 'bg-blue-100 text-blue-600'}`}>
                        {isPdf ? 'PDF' : 'PPT'}
                      </div>
                      <p className="text-xs text-gray-700 font-medium leading-tight line-clamp-2 break-all">{f.original_name}</p>
                      {sizeMb && <p className="text-xs text-gray-400">{sizeMb}</p>}
                      <span className="text-xs text-blue-500 group-hover:underline flex items-center gap-0.5 mt-auto">
                        <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/></svg>
                        {tr ? 'Önizle' : 'Preview'}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Phase 86AZ.30 — Approve / Reject action bar.
              Visible while review_status is null or pending. Once decided,
              the banner above replaces it. */}
          {!['approved', 'rejected'].includes(selectedDesign.review_status || '') && (
            <DesignReviewBar
              tr={tr}
              designId={selectedDesign.id}
              onDone={(verdict, note) => setSelectedDesign(prev => ({
                ...prev,
                review_status: verdict,
                reviewer_note: note,
                reviewed_at: new Date().toISOString(),
                status: verdict,
              }))}
            />
          )}
        </div>
      )}

      {/* GET PRICE (Phase 86AZ.41) — Direct Price Offer flow lives in its own tab */}
      {detailTab === 'get-price' && (
        <div className="bg-white rounded-2xl border border-gray-100 p-6">
          <DirectOfferPanel
            tr={tr}
            design={selectedDesign}
            onAwarded={() => setSelectedDesign(prev => ({ ...prev, status: 'awarded' }))}
          />
        </div>
      )}

      {/* CUSTOMER CHAT */}
      {detailTab === 'chat' && (
        <div className="bg-white rounded-2xl border border-gray-100 flex flex-col overflow-hidden max-w-2xl" style={{minHeight: 400}}>
          {/* Header */}
          <div className="px-5 py-4 border-b border-gray-100 bg-white">
            <div className="flex items-center gap-3">
              <div className="relative">
                <div className="h-10 w-10 rounded-full bg-gradient-to-br from-gray-700 to-gray-900 flex items-center justify-center shadow-sm">
                  <span className="text-white text-xs font-bold">{(selectedDesign.tenant_name || '?')[0].toUpperCase()}</span>
                </div>
                <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-white bg-emerald-400" />
              </div>
              <div>
                <p className="font-semibold text-gray-900 text-sm">{selectedDesign.tenant_name}</p>
                <p className="text-xs text-gray-400">{tr ? 'Müşteri ile yazışma' : 'Chat with customer'}</p>
              </div>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-1 bg-gray-50/40" style={{maxHeight: 340}}>
            {messages.length === 0 && (
              <div className="flex justify-center py-8">
                <span className="text-xs text-gray-400 bg-white border border-gray-100 rounded-full px-3 py-1">
                  {tr ? 'Henüz mesaj yok — ilk mesajı siz gönderin.' : 'No messages yet — send the first one.'}
                </span>
              </div>
            )}
            {messages.map((msg, idx) => {
              const isMe = msg.sender_role === 'ultra_admin';
              const prevMsg = messages[idx - 1];
              const showDate = !prevMsg || new Date(msg.created_at).toDateString() !== new Date(prevMsg.created_at).toDateString();
              return (
                <React.Fragment key={msg.id}>
                  {showDate && (
                    <div className="flex items-center gap-3 py-2">
                      <div className="flex-1 h-px bg-gray-200" />
                      <span className="text-xs text-gray-400 font-medium whitespace-nowrap">
                        {new Date(msg.created_at).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long' })}
                      </span>
                      <div className="flex-1 h-px bg-gray-200" />
                    </div>
                  )}
                  <div className={`flex items-end gap-2 ${isMe ? 'justify-end' : 'justify-start'}`}>
                    {!isMe && (
                      <div className="h-7 w-7 rounded-full bg-gradient-to-br from-gray-600 to-gray-800 flex items-center justify-center flex-shrink-0 mb-0.5 shadow-sm">
                        <span className="text-white text-xs font-bold">{(selectedDesign.tenant_name || '?')[0].toUpperCase()}</span>
                      </div>
                    )}
                    <div className="max-w-[78%]">
                      {!isMe && <p className="text-xs font-semibold text-gray-500 mb-1 ml-1">{msg.sender_name}</p>}
                      <div className={`px-4 py-2.5 rounded-2xl shadow-sm text-sm leading-relaxed
                        ${isMe
                          ? 'bg-orange-500 text-white rounded-br-none'
                          : 'bg-white border border-gray-100 text-gray-800 rounded-bl-none'}`}>
                        <p>{msg.message}</p>
                        {msg.attachment && (() => {
                          const fileUrl = `${window.location.origin}/api/uploads/${msg.attachment.filename}`;
                          const ext = ((msg.attachment.original_name || msg.attachment.filename || '').split('.').pop() || '').toLowerCase();
                          const isImage = ['png','jpg','jpeg','gif','webp','svg','bmp','heic','heif'].includes(ext);
                          const isPreviewable = isImage || ['pdf','ppt','pptx'].includes(ext);
                          // Phase 86AI — Inline preview for chat attachments.
                          //   • image  → tıklanınca tam ekran modal (zoom)
                          //   • pdf/ppt → tıklanınca iframe ile aynı modal
                          //   • diğer  → eski indirme linki (excel, word, zip…)
                          if (isImage) {
                            return (
                              <button
                                type="button"
                                onClick={(e) => { e.preventDefault(); setPreviewFile({ url: fileUrl, name: msg.attachment.original_name, type: 'image' }); }}
                                className="block mt-2 rounded-xl overflow-hidden border border-white/30 hover:opacity-90 transition-opacity"
                                data-testid={`chat-img-${msg.id || ''}`}
                              >
                                <img src={fileUrl} alt={msg.attachment.original_name} className="max-h-48 w-auto object-cover" />
                              </button>
                            );
                          }
                          if (isPreviewable) {
                            return (
                              <button
                                type="button"
                                onClick={(e) => { e.preventDefault(); setPreviewFile({ url: fileUrl, name: msg.attachment.original_name, type: ext }); }}
                                className={`flex items-center gap-1.5 mt-2 text-xs rounded-lg px-2 py-1.5 ${isMe ? 'bg-orange-400/50 text-orange-100 hover:bg-orange-400' : 'bg-orange-50 text-orange-600 hover:bg-orange-100'} transition-colors`}
                              >
                                <FileText className="h-3 w-3 flex-shrink-0" />
                                <span className="truncate">{msg.attachment.original_name}</span>
                                <span className="text-[10px] uppercase opacity-70">{ext}</span>
                              </button>
                            );
                          }
                          return (
                            <a href={fileUrl} download target="_blank" rel="noreferrer"
                              className={`flex items-center gap-1.5 mt-2 text-xs rounded-lg px-2 py-1.5 ${isMe ? 'bg-orange-400/50 text-orange-100 hover:bg-orange-400' : 'bg-orange-50 text-orange-600 hover:bg-orange-100'} transition-colors`}>
                              <Paperclip className="h-3 w-3 flex-shrink-0" />
                              <span className="truncate">{msg.attachment.original_name}</span>
                              <span className="text-[10px] uppercase opacity-70">{ext || 'file'}</span>
                            </a>
                          );
                        })()}
                      </div>
                      <div className={`flex items-center gap-1 mt-1 px-1 ${isMe ? 'justify-end' : 'justify-start'}`}>
                        <span className="text-xs text-gray-400">
                          {new Date(msg.created_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                        </span>
                        {isMe && (
                          <svg className="h-3 w-3 text-orange-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                    </div>
                  </div>
                </React.Fragment>
              );
            })}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="px-4 py-3 border-t border-gray-100 bg-white">
            {msgFile && (
              <div className="flex items-center gap-2 mb-2 px-3 py-1.5 bg-orange-50 border border-orange-200 rounded-xl">
                <Paperclip className="h-3 w-3 text-orange-500" />
                <span className="text-xs flex-1 truncate text-gray-700">{msgFile.name}</span>
                <button onClick={() => setMsgFile(null)} className="text-gray-400 hover:text-red-500"><X className="h-3 w-3" /></button>
              </div>
            )}
            <div className="flex items-center gap-2">
              <input ref={msgFileRef} type="file" className="hidden" onChange={e => { setMsgFile(e.target.files?.[0] || null); e.target.value = ''; }} />
              <button onClick={() => msgFileRef.current?.click()}
                className="h-9 w-9 flex items-center justify-center rounded-xl text-gray-400 hover:text-orange-500 hover:bg-orange-50 transition-colors flex-shrink-0">
                <Paperclip className="h-4 w-4" />
              </button>
              <input data-testid="admin-design-msg-input" type="text" value={msgText} onChange={e => setMsgText(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendMessage()}
                className="flex-1 px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-300 focus:bg-white transition-all"
                placeholder={tr ? 'Müşteriye mesaj yazın...' : 'Write a message to customer...'} />
              <button data-testid="admin-design-send-msg" onClick={sendMessage} disabled={msgLoading}
                className="h-9 w-9 flex items-center justify-center bg-orange-500 hover:bg-orange-600 text-white rounded-xl flex-shrink-0 transition-colors shadow-sm disabled:opacity-50">
                {msgLoading
                  ? <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                  : <Send className="h-4 w-4" />}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ASSIGNED CONTRACTOR — Phase 86AZ.45 — RFQ akışı yerine doğrudan
          finalized direct-offer'dan yüklenici + fiyat gösteren basit panel. */}
      {detailTab === 'assign' && (
        <AssignedContractorPanel tr={tr} design={selectedDesign} />
      )}
      {/* CONTRACTOR CHATS — Phase 86AZ.46 — per direct offer */}
      {detailTab === 'contractor-chat' && (
        <AdminContractorChatsPanel tr={tr} design={selectedDesign} />
      )}

      {/* Phase 86AY — File Preview Modal also lives inside the detail view
          so PDF/PPT preview from the Stand Brief tab actually opens. */}
      {previewFile && (() => {
        let resolvedType = previewFile.type;
        if (!resolvedType) {
          const ext = ((previewFile.name || previewFile.url || '').split('.').pop() || '').toLowerCase();
          if (['png','jpg','jpeg','gif','webp','svg','bmp','heic','heif'].includes(ext)) resolvedType = 'image';
          else if (ext === 'pdf') resolvedType = 'pdf';
          else if (['ppt','pptx'].includes(ext)) resolvedType = 'ppt';
          else resolvedType = 'pdf';
        }
        return (
          <div className="fixed inset-0 z-[200] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
               onClick={() => setPreviewFile(null)} data-testid="design-preview-modal">
            <button type="button" onClick={(e) => { e.stopPropagation(); setPreviewFile(null); }}
              className="absolute top-4 right-4 h-10 w-10 rounded-full bg-white/15 hover:bg-white/30 text-white flex items-center justify-center backdrop-blur-md" aria-label="Close">
              <X className="h-5 w-5" />
            </button>
            <div className="absolute top-4 left-4 max-w-[60%] truncate text-white text-sm font-semibold bg-black/40 px-3 py-1.5 rounded-full">
              {previewFile.name}
            </div>
            {resolvedType === 'image' ? (
              <img src={previewFile.url} alt={previewFile.name}
                className="max-h-[90vh] max-w-[90vw] object-contain rounded-2xl shadow-2xl"
                onClick={(e) => e.stopPropagation()} />
            ) : resolvedType === 'pdf' ? (
              <iframe src={previewFile.url} title={previewFile.name}
                className="bg-white rounded-2xl shadow-2xl"
                style={{ width: 'min(95vw, 1200px)', height: '90vh' }}
                onClick={(e) => e.stopPropagation()} />
            ) : (
              <iframe src={`https://docs.google.com/gview?embedded=true&url=${encodeURIComponent(previewFile.url)}`}
                title={previewFile.name}
                className="bg-white rounded-2xl shadow-2xl"
                style={{ width: 'min(95vw, 1200px)', height: '90vh' }}
                onClick={(e) => e.stopPropagation()} />
            )}
            <a href={previewFile.url} download target="_blank" rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="absolute bottom-6 right-6 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/15 hover:bg-white/30 text-white text-xs font-semibold backdrop-blur-md">
              {tr ? 'İndir' : 'Download'}
            </a>
          </div>
        );
      })()}
    </div>
  );

  return (
    <div className="space-y-4">
      {embedded ? (
        // Phase 86AO — Embedded mode: country picker is the sibling left column.
        // Until admin clicks a country we render a friendly empty placeholder.
        // Phase 86AP — When admin clicks a country with no uploaded design we
        // pivot to a country-specific empty state so the previous country's
        // brief no longer lingers on the right.
        selectedEmptyCountry ? (
          <div className="bg-white rounded-2xl border-2 border-dashed border-orange-200 p-12 text-center" data-testid="admin-design-empty-country">
            <div className="mx-auto h-14 w-14 rounded-2xl bg-gradient-to-br from-orange-100 to-amber-100 flex items-center justify-center mb-4">
              <svg className="h-7 w-7 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
            </div>
            <p className="text-lg font-bold text-gray-800">
              {selectedEmptyCountry.country}
              <span className="ml-2 text-xs uppercase tracking-wider text-orange-600 bg-orange-100 border border-orange-200 px-2 py-0.5 rounded-full font-bold align-middle">
                {tr ? 'Tasarım Bekleniyor' : 'Awaiting Design'}
              </span>
            </p>
            <p className="text-sm text-gray-500 mt-3 max-w-md mx-auto">
              {tr
                ? `${selectedEmptyCountry.country} henüz Custom Stand brifini ve tasarım dosyalarını yüklemedi. Müşteri yüklediğinde Stand Brief, Müşteri Yazışması, Teklifler, Atanmış Yüklenici ve Yüklenici Yazışması sekmeleri burada açılacak.`
                : `${selectedEmptyCountry.country} hasn't uploaded their Custom Stand brief and design files yet. Once the customer uploads them, Stand Brief, Customer Chat, Bids, Assigned Contractor and Contractor Chat tabs will open here.`}
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-dashed border-gray-200 p-12 text-center" data-testid="admin-design-empty-state">
            <div className="mx-auto h-14 w-14 rounded-2xl bg-gradient-to-br from-orange-100 to-amber-100 flex items-center justify-center mb-4">
              <svg className="h-7 w-7 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2M9 12h6M9 16h6"/>
              </svg>
            </div>
            <p className="font-semibold text-gray-700">{tr ? 'Sol menüden bir ülke seçin' : 'Select a country from the left menu'}</p>
            <p className="text-xs text-gray-400 mt-1.5 max-w-md mx-auto">
              {tr
                ? 'Seçtiğiniz ülkenin Stand Brief, Müşteri Yazışması, Teklifler, Atanmış Yüklenici ve Yüklenici Yazışması sekmeleri burada görünecek.'
                : 'Stand Brief, Customer Chat, Bids, Assigned Contractor and Contractor Chat tabs for the selected country will appear here.'}
            </p>
          </div>
        )
      ) : (
        <>
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-bold text-gray-800">{tr ? 'Stand Tasarım Talepleri' : 'Stand Design Requests'}</h3>
            <span className="text-sm text-gray-400">{designs.length} {tr ? 'toplam' : 'total'}</span>
          </div>

          {designs.length === 0 ? (
            <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center">
              <p className="text-gray-400">{tr ? 'Henüz tasarım talebi yok.' : 'No design requests yet.'}</p>
            </div>
          ) : designs.map(d => {
        const totalUnread = (d.customer_unread || 0) + (d.contractor_unread || 0);
        return (
        <div key={d.id} className="bg-white rounded-2xl border border-gray-100 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-semibold text-gray-800 flex items-center gap-2">
                {d.title}
                {/* Phase 86AH — Surface unread chat counts on the row itself
                    so admins notice new messages without first opening the
                    detail panel. Kırmızı pulse: customer · Mor pulse: contractor. */}
                {(d.customer_unread || 0) > 0 && (
                  <span
                    className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-500 text-white inline-flex items-center gap-1 animate-pulse"
                    title={tr ? 'Müşteriden okunmamış mesaj' : 'Unread customer message'}
                    data-testid={`row-customer-unread-${d.id}`}
                  >
                    <MessageSquare className="h-3 w-3" />{d.customer_unread}
                  </span>
                )}
                {(d.contractor_unread || 0) > 0 && (
                  <span
                    className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-purple-600 text-white inline-flex items-center gap-1 animate-pulse"
                    title={tr ? 'Yükleniciden okunmamış mesaj' : 'Unread contractor message'}
                    data-testid={`row-contractor-unread-${d.id}`}
                  >
                    <MessageSquare className="h-3 w-3" />{d.contractor_unread}
                  </span>
                )}
              </p>
              <p className="text-xs text-gray-400 mt-0.5">{d.tenant_name} — {d.hall && `Hall: ${d.hall}`} {d.stand_no && `| Stand: ${d.stand_no}`}</p>
            </div>
            <div className="flex items-center gap-2">
              <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${DESIGN_STATUS_COLORS[d.status] || 'bg-gray-100 text-gray-600'}`}>
                {DESIGN_STATUS_LABELS[d.status] || d.status}
              </span>
              <Button size="sm" data-testid={`admin-design-open-${d.id}`} onClick={() => openDesign(d)}
                className={`relative rounded-xl text-xs px-3 ${totalUnread > 0 ? 'bg-red-500 hover:bg-red-600 text-white border border-red-600' : 'bg-orange-50 hover:bg-orange-100 text-orange-700 border border-orange-200'}`}>
                {tr ? 'İncele' : 'Review'}
                {totalUnread > 0 && (
                  <span className="absolute -top-2 -right-2 h-5 min-w-5 px-1 rounded-full bg-white text-red-600 text-[10px] font-bold flex items-center justify-center border border-red-500 shadow-sm">
                    {totalUnread}
                  </span>
                )}
              </Button>
            </div>
          </div>
        </div>
        );
      })}
        </>
      )}

      {/* Phase 86AI — File Preview Modal (image / pdf / ppt).
          Image → zoom-in. PDF/PPT → embedded iframe. Diğer dosyalar (excel,
          word vb.) chat içinde indirme link'i olarak kalır. */}
      {previewFile && (
        <div
          className="fixed inset-0 z-[200] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setPreviewFile(null)}
          data-testid="chat-preview-modal"
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setPreviewFile(null); }}
            className="absolute top-4 right-4 h-10 w-10 rounded-full bg-white/15 hover:bg-white/30 text-white flex items-center justify-center backdrop-blur-md"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
          <div className="absolute top-4 left-4 max-w-[60%] truncate text-white text-sm font-semibold bg-black/40 px-3 py-1.5 rounded-full">
            {previewFile.name}
          </div>
          {/* PDF/PPT embed via google-docs viewer (works for ppt/pptx too) */}
          {previewFile.type === 'image' ? (
            <img
              src={previewFile.url}
              alt={previewFile.name}
              className="max-h-[90vh] max-w-[90vw] object-contain rounded-2xl shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          ) : previewFile.type === 'pdf' ? (
            <iframe
              src={previewFile.url}
              title={previewFile.name}
              className="bg-white rounded-2xl shadow-2xl"
              style={{ width: 'min(95vw, 1200px)', height: '90vh' }}
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            // ppt/pptx — Google docs embedded viewer.
            <iframe
              src={`https://docs.google.com/gview?embedded=true&url=${encodeURIComponent(previewFile.url)}`}
              title={previewFile.name}
              className="bg-white rounded-2xl shadow-2xl"
              style={{ width: 'min(95vw, 1200px)', height: '90vh' }}
              onClick={(e) => e.stopPropagation()}
            />
          )}
          {/* Bottom-bar: download fallback */}
          <a
            href={previewFile.url}
            download
            target="_blank"
            rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="absolute bottom-6 right-6 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/15 hover:bg-white/30 text-white text-xs font-semibold backdrop-blur-md"
          >
            <Paperclip className="h-3.5 w-3.5" />
            {tr ? 'İndir' : 'Download'}
          </a>
        </div>
      )}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
// Business Hours Settings Component
// ─────────────────────────────────────────────────────────────
const DAYS_TR = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'];
const DAYS_EN = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const HOURS = Array.from({length: 25}, (_, i) => i); // 0..24

function BusinessHoursSettings({ language }) {
  const tr = language === 'tr';
  const token = localStorage.getItem('cop31_ultra_token');
  const headers = { Authorization: `Bearer ${token}` };
  const BACKEND = process.env.REACT_APP_BACKEND_URL;

  const [bh, setBh] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    apiClient.get(`/ultra-admin/business-hours`)
      .then(r => setBh(r.data))
      .catch(() => setBh({
        timezone: 'Europe/Istanbul', start_hour: 9, end_hour: 18,
        working_days: [1,2,3,4,5],
        tr_message: 'Hafta içi 09:00–18:00 saatleri arasında (Türkiye saatiyle) yanıt verilmektedir.',
        en_message: 'Our team replies on weekdays between 09:00–18:00 (Turkey Time, UTC+3).',
        team_name_tr: 'COP 31 Destek Ekibi',
        team_name_en: 'COP 31 Support Team',
      }))
      .finally(() => setLoading(false));
  }, []);

  const toggleDay = (d) => {
    setBh(prev => {
      const days = prev.working_days.includes(d)
        ? prev.working_days.filter(x => x !== d)
        : [...prev.working_days, d].sort((a,b) => a-b);
      return { ...prev, working_days: days };
    });
  };

  const save = async () => {
    setSaving(true); setSaved(false);
    try {
      const r = await apiClient.put(`/ultra-admin/business-hours`, bh);
      setBh(r.data);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch {}
    finally { setSaving(false); }
  };

  // Live preview of current status
  const getPreviewStatus = () => {
    if (!bh) return null;
    try {
      const now = new Date();
      const ist = new Date(now.toLocaleString('en-US', { timeZone: bh.timezone || 'Europe/Istanbul' }));
      const h = ist.getHours(), d = ist.getDay() === 0 ? 7 : ist.getDay();
      const isWorkDay = (bh.working_days || []).includes(d);
      const isWorkHour = h >= bh.start_hour && h < bh.end_hour;
      return { online: isWorkDay && isWorkHour, timeStr: ist.toLocaleTimeString('tr-TR', {hour:'2-digit',minute:'2-digit'}) };
    } catch { return null; }
  };

  const preview = getPreviewStatus();

  if (loading) return <div className="flex justify-center py-16"><div className="animate-spin h-8 w-8 border-4 border-orange-500 border-t-transparent rounded-full" /></div>;

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">{tr ? 'Mesai Saatleri' : 'Business Hours'}</h2>
          <p className="text-sm text-gray-500 mt-0.5">{tr ? 'Müşteri destek chat widgetında gösterilir.' : 'Shown in the customer support chat widget.'}</p>
        </div>
        {preview && (
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold border ${preview.online ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-gray-100 border-gray-200 text-gray-500'}`}>
            <span className={`h-2 w-2 rounded-full ${preview.online ? 'bg-emerald-500 animate-pulse' : 'bg-gray-400'}`} />
            {preview.online
              ? (tr ? `Şu an Çevrimiçi (${preview.timeStr})` : `Currently Online (${preview.timeStr})`)
              : (tr ? `Şu an Mesai Dışı (${preview.timeStr})` : `Currently Off Hours (${preview.timeStr})`)}
          </div>
        )}
      </div>

      {/* Working Days */}
      <div className="bg-white rounded-2xl border border-gray-200 p-5">
        <h3 className="font-semibold text-gray-800 text-sm mb-4 flex items-center gap-2">
          <svg className="h-4 w-4 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>
          {tr ? 'Çalışma Günleri' : 'Working Days'}
        </h3>
        <div className="flex gap-2">
          {(tr ? DAYS_TR : DAYS_EN).map((label, i) => {
            const dayNum = i + 1;
            const active = bh?.working_days?.includes(dayNum);
            return (
              <button key={dayNum} onClick={() => toggleDay(dayNum)}
                className={`h-10 w-10 rounded-xl text-xs font-bold transition-all ${active ? 'bg-orange-500 text-white shadow-sm' : 'bg-gray-100 text-gray-500 hover:bg-orange-100 hover:text-orange-600'}`}>
                {label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Hours */}
      <div className="bg-white rounded-2xl border border-gray-200 p-5">
        <h3 className="font-semibold text-gray-800 text-sm mb-4 flex items-center gap-2">
          <svg className="h-4 w-4 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
          {tr ? 'Çalışma Saatleri' : 'Working Hours'}
        </h3>
        <div className="flex items-center gap-4">
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-gray-500">{tr ? 'Başlangıç' : 'Start'}</label>
            <select value={bh?.start_hour ?? 9} onChange={e => setBh(p => ({...p, start_hour: +e.target.value}))}
              className="w-24 px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-300">
              {HOURS.slice(0,24).map(h => <option key={h} value={h}>{String(h).padStart(2,'0')}:00</option>)}
            </select>
          </div>
          <div className="text-gray-400 mt-5">–</div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-gray-500">{tr ? 'Bitiş' : 'End'}</label>
            <select value={bh?.end_hour ?? 18} onChange={e => setBh(p => ({...p, end_hour: +e.target.value}))}
              className="w-24 px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-300">
              {HOURS.slice(1).map(h => <option key={h} value={h}>{String(h).padStart(2,'0')}:00</option>)}
            </select>
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-gray-500">{tr ? 'Saat Dilimi' : 'Timezone'}</label>
            <select value={bh?.timezone ?? 'Europe/Istanbul'} onChange={e => setBh(p => ({...p, timezone: e.target.value}))}
              className="w-48 px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-300">
              <option value="Europe/Istanbul">Europe/Istanbul (UTC+3)</option>
              <option value="UTC">UTC</option>
              <option value="Europe/London">Europe/London</option>
              <option value="Europe/Berlin">Europe/Berlin (UTC+1)</option>
              <option value="America/New_York">America/New_York (UTC-5)</option>
            </select>
          </div>
        </div>
      </div>

      {/* Team Name */}
      <div className="bg-white rounded-2xl border border-gray-200 p-5">
        <h3 className="font-semibold text-gray-800 text-sm mb-4 flex items-center gap-2">
          <svg className="h-4 w-4 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0"/></svg>
          {tr ? 'Ekip Adı' : 'Team Name'}
        </h3>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-gray-500 flex items-center gap-1">🇹🇷 {tr ? 'Türkçe' : 'Turkish'}</label>
            <input value={bh?.team_name_tr ?? ''} onChange={e => setBh(p => ({...p, team_name_tr: e.target.value}))}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-300"
              placeholder="COP 31 Destek Ekibi" />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-gray-500 flex items-center gap-1">🇬🇧 {tr ? 'İngilizce' : 'English'}</label>
            <input value={bh?.team_name_en ?? ''} onChange={e => setBh(p => ({...p, team_name_en: e.target.value}))}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-300"
              placeholder="COP 31 Support Team" />
          </div>
        </div>
      </div>

      {/* Welcome Messages */}
      <div className="bg-white rounded-2xl border border-gray-200 p-5">
        <h3 className="font-semibold text-gray-800 text-sm mb-4 flex items-center gap-2">
          <svg className="h-4 w-4 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"/></svg>
          {tr ? 'Hoş Geldiniz Mesajları' : 'Welcome Messages'}
        </h3>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-gray-500 flex items-center gap-1">🇹🇷 {tr ? 'Türkçe mesaj' : 'Turkish message'}</label>
            <textarea value={bh?.tr_message ?? ''} onChange={e => setBh(p => ({...p, tr_message: e.target.value}))}
              className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-300 resize-none h-20"
              placeholder="Merhaba! Stand üretim talebinizi aldık..." />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-gray-500 flex items-center gap-1">🇬🇧 {tr ? 'İngilizce mesaj' : 'English message'}</label>
            <textarea value={bh?.en_message ?? ''} onChange={e => setBh(p => ({...p, en_message: e.target.value}))}
              className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-300 resize-none h-20"
              placeholder="Hello! We have received your stand production request..." />
          </div>
        </div>
      </div>

      {/* Save button */}
      <div className="flex items-center gap-3">
        <button data-testid="save-business-hours" onClick={save} disabled={saving}
          className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-6 py-2.5 rounded-xl text-sm font-semibold transition-colors disabled:opacity-50">
          {saving
            ? <><span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />{tr ? 'Kaydediliyor...' : 'Saving...'}</>
            : (tr ? 'Ayarları Kaydet' : 'Save Settings')}
        </button>
        {saved && (
          <span className="flex items-center gap-1.5 text-emerald-600 text-sm font-medium">
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7"/></svg>
            {tr ? 'Kaydedildi!' : 'Saved!'}
          </span>
        )}
      </div>
    </div>
  );
}


/* ─── Tenant Stand Durumu Özeti ─────────────────────────────── */
function TenantStandSummary({ language, clickable = false, vertical = false }) {
  const tr = language === 'tr';
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  // Phase 86AZ.37 — Default to "Pending" since admin's primary task is to
  // review incoming designs. "All" filter removed.
  const [activeStatus, setActiveStatus] = React.useState('design_pending');
  // Phase 86AH — 4-column grid + 20-per-page pagination so the country list
  // doesn't grow into an unbounded scrolling wall when there are 100+
  // tenants. Page resets to 1 whenever the active status filter changes.
  const [page, setPage] = React.useState(1);
  const PAGE_SIZE = 20;
  // Phase 86AO — In vertical/sidebar mode track which country is currently
  // selected so we can highlight the active row.
  const [activeTenantId, setActiveTenantId] = React.useState(null);
  React.useEffect(() => { setPage(1); }, [activeStatus]);
  React.useEffect(() => {
    if (!vertical) return;
    const handler = (e) => setActiveTenantId(e.detail?.tenant_id || null);
    window.addEventListener('cop31:open-stand-design-by-tenant', handler);
    return () => window.removeEventListener('cop31:open-stand-design-by-tenant', handler);
  }, [vertical]);

  React.useEffect(() => {
    const token = localStorage.getItem('cop31_ultra_token');
    if (!token) return;
    apiClient.get('/ultra-admin/tenant-stand-summary')
      .then(r => { setItems(Array.isArray(r.data) ? r.data : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  // Phase 86AZ.37 — Pill list trimmed to just two design review states
  // ("Pending" / "Approved") per kullanıcı talebi. Diğer status'lar
  // (no_application, ready_no_selection, stand_selected) bu listenin
  // amacı (tasarım onay/red akışı) dışında kalıyordu.
  const STATUS_CFG = [
    {
      key: 'design_pending',
      label: tr ? 'Beklemede' : 'Pending',
      activeCls: 'bg-amber-500 text-white',
      badgeCls: 'bg-amber-50 text-amber-700 border border-amber-200',
      dotCls: 'bg-amber-400',
    },
    {
      key: 'design_approved',
      label: tr ? 'Onaylı' : 'Approved',
      activeCls: 'bg-emerald-600 text-white',
      badgeCls: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
      dotCls: 'bg-emerald-400',
    },
  ];

  // Phase 86AO — Sidebar (vertical) mode sorts alphabetically by company_name
  // for stable, predictable ordering on the Stand Designs page.
  // Phase 86AP — Sidebar mode also exposes a comprehensive search box that
  // matches across country / email / slug / stand_no / hall name / area /
  // design type / layout type / status text.
  const [search, setSearch] = React.useState('');
  // Phase 86AZ.29 — Zone tab (Blue Zone | Green Zone). Backend now returns
  // tenants from both zones with `zone` on each row.
  const [activeZone, setActiveZone] = React.useState('blue_zone');
  const zoneFiltered = items.filter(i => (i.zone || 'blue_zone') === activeZone);
  // Phase 86AZ.37 — Filter pill is always one of the design_* keys now;
  // no more "all" fallback.
  const baseFiltered = zoneFiltered.filter(i => i.status === activeStatus);
  let filtered = vertical
    ? [...baseFiltered].sort((a, b) => (a.company_name || '').localeCompare(b.company_name || '', undefined, { sensitivity: 'base' }))
    : baseFiltered;
  if (vertical && search.trim()) {
    const q = search.trim().toLowerCase();
    filtered = filtered.filter(i => {
      const haystack = [
        i.company_name, i.email, i.slug, i.country,
        i.assigned_stand_no, i.assigned_hall_name,
        i.requested_design_type, i.requested_layout_type, i.stand_type,
        i.requested_area != null ? `${i.requested_area} m²` : '',
        i.requested_area != null ? `${i.requested_area}m2` : '',
        i.status,
      ].filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(q);
    });
  }

  // Counts per zone (used in the zone tab badges)
  const blueCount = items.filter(i => (i.zone || 'blue_zone') === 'blue_zone').length;
  const greenCount = items.filter(i => i.zone === 'green_zone').length;

  const statusLabel = (s) => STATUS_CFG.find(c => c.key === s) || STATUS_CFG[1];

  if (loading) return (
    <div className="text-sm text-gray-400 py-4 text-center">{tr ? 'Yükleniyor...' : 'Loading...'}</div>
  );

  return (
    <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden mb-4">
      {/* Başlık */}
      <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
        <div>
          <h4 className="font-semibold text-gray-900 text-sm">
            {tr ? 'Ülke Stand Durumu' : 'Country Stand Status'}
          </h4>
          <p className="text-xs text-gray-400 mt-0.5">
            {activeZone === 'blue_zone'
              ? (tr ? 'Tüm Blue Zone katılımcılarının stand başvuru durumu' : 'Stand application status of all Blue Zone participants')
              : (tr ? 'Tüm Green Zone katılımcılarının stand başvuru durumu' : 'Stand application status of all Green Zone participants')}
          </p>
        </div>
        <span className="text-xs font-bold text-gray-500 bg-gray-100 px-2 py-1 rounded-full">{zoneFiltered.length}</span>
      </div>

      {/* Phase 86AZ.29 — Zone tab switcher (Blue Zone | Green Zone) */}
      <div className="px-3 pt-3 -mb-px flex items-center gap-1 border-b border-gray-100">
        {[
          { key: 'blue_zone',  labelTr: 'Blue Zone',  labelEn: 'Blue Zone',  count: blueCount,  accent: 'blue'  },
          { key: 'green_zone', labelTr: 'Green Zone', labelEn: 'Green Zone', count: greenCount, accent: 'emerald' },
        ].map(z => {
          const active = activeZone === z.key;
          const cls = active
            ? (z.accent === 'blue'
                ? 'text-blue-600 border-blue-500 bg-blue-50'
                : 'text-emerald-600 border-emerald-500 bg-emerald-50')
            : 'text-gray-500 border-transparent hover:text-gray-700 hover:bg-gray-50';
          const chipCls = active
            ? (z.accent === 'blue' ? 'bg-blue-100 text-blue-700' : 'bg-emerald-100 text-emerald-700')
            : 'bg-gray-100 text-gray-500';
          return (
            <button
              key={z.key}
              type="button"
              data-testid={`stand-zone-tab-${z.key}`}
              onClick={() => setActiveZone(z.key)}
              className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 border-b-2 text-xs font-semibold rounded-t-lg transition-colors ${cls}`}
            >
              <span className={`inline-block w-2 h-2 rounded-full ${z.accent === 'blue' ? 'bg-blue-500' : 'bg-emerald-500'}`} />
              {tr ? z.labelTr : z.labelEn}
              <span className={`inline-flex items-center justify-center min-w-[20px] h-4 px-1.5 rounded-full text-[10px] font-bold ${chipCls}`}>
                {z.count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Phase 86AP — Comprehensive search (vertical sidebar only) */}
      {vertical && (
        <div className="px-4 py-3 border-b border-gray-100">
          <div className="relative">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z"/>
            </svg>
            <input
              type="text"
              data-testid="country-list-search"
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
              placeholder={tr ? 'Ülke, stand, hall, m², email…' : 'Country, stand, hall, m², email…'}
              className="w-full pl-9 pr-8 py-2 text-sm border border-gray-200 rounded-xl bg-white focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition-colors"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 h-5 w-5 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-500"
                aria-label={tr ? 'Aramayı temizle' : 'Clear search'}
              >
                <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"/></svg>
              </button>
            )}
          </div>
        </div>
      )}

      {/* Durum tabları */}
      <div className="px-5 py-3 flex gap-2 flex-wrap border-b border-gray-50">
        {STATUS_CFG.map(({ key, label, activeCls }) => {
          // Phase 86AZ.37 — Always render Pending/Approved pills, even when
          // the active zone has 0 of that status — admin must be able to
          // switch filters. Count is from `zoneFiltered` (respects zone tab).
          const cnt = zoneFiltered.filter(i => i.status === key).length;
          return (
            <button
              key={key}
              onClick={() => setActiveStatus(key)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                activeStatus === key ? activeCls : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {label}
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${
                activeStatus === key ? 'bg-white/20' : 'bg-gray-200 text-gray-500'
              }`}>{cnt}</span>
            </button>
          );
        })}
      </div>

      {/* Liste — 4-col grid + 20-per-page pagination (Phase 86AH)
          OR alphabetical vertical sidebar list (Phase 86AO, vertical=true) */}
      {(() => {
        if (filtered.length === 0) {
          return (
            <p className="text-center text-gray-400 py-8 text-sm">
              {tr ? 'Bu kategoride kayıt yok.' : 'No records in this category.'}
            </p>
          );
        }
        // ─── Phase 86AO: Vertical compact list (sidebar usage) ─────────────
        if (vertical) {
          return (
            <div className="divide-y divide-gray-100 max-h-[70vh] overflow-y-auto">
              {filtered.map(item => {
                const cfg = statusLabel(item.status);
                const isActive = activeTenantId === item.id;
                const handleClick = () => {
                  if (!clickable) return;
                  window.dispatchEvent(new CustomEvent('cop31:open-stand-design-by-tenant', {
                    detail: { tenant_id: item.id, country: item.company_name }
                  }));
                };
                // Phase 86AP — Show hall_no, stand_no, m² in chip rows.
                const hasAlloc = !!(item.assigned_hall_name || item.assigned_stand_no);
                const areaText = item.assigned_stand_no
                  ? (item.requested_area ? `${item.requested_area} m²` : '')
                  : (item.requested_area ? `${item.requested_area} m²` : '');
                const designType = item.requested_design_type
                  ? (item.requested_design_type === 'modular'
                      ? (tr ? 'Modüler' : 'Modular')
                      : (tr ? 'Custom' : 'Custom'))
                  : '';
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={handleClick}
                    data-testid={`country-row-${item.id}`}
                    className={`w-full text-left flex items-start gap-3 px-4 py-4 transition-colors ${
                      isActive ? 'bg-orange-50 border-l-4 border-orange-500' : 'border-l-4 border-transparent hover:bg-gray-50'
                    }`}
                  >
                    <div className={`h-2.5 w-2.5 rounded-full flex-shrink-0 mt-1.5 ${cfg.dotCls || 'bg-gray-300'}`} />
                    <div className="flex-1 min-w-0 space-y-1.5">
                      <div className="flex items-center justify-between gap-2">
                        <p className={`text-sm font-bold truncate ${isActive ? 'text-orange-700' : 'text-gray-800'}`}>{item.company_name}</p>
                        {isActive && (
                          <svg className="h-4 w-4 text-orange-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7"/>
                          </svg>
                        )}
                      </div>
                      {hasAlloc ? (
                        <div className="flex flex-wrap items-center gap-1.5 text-[10px]">
                          {item.assigned_hall_name && (
                            <span className="bg-blue-50 text-blue-700 border border-blue-100 px-1.5 py-0.5 rounded font-semibold">
                              {item.assigned_hall_name}
                            </span>
                          )}
                          {item.assigned_stand_no && (
                            <span className="bg-purple-50 text-purple-700 border border-purple-100 px-1.5 py-0.5 rounded font-semibold">
                              {item.assigned_stand_no}
                            </span>
                          )}
                          {item.requested_area && (
                            <span className="bg-emerald-50 text-emerald-700 border border-emerald-100 px-1.5 py-0.5 rounded font-semibold">
                              {item.requested_area} m²
                            </span>
                          )}
                          {designType && (
                            <span className="bg-amber-50 text-amber-700 border border-amber-100 px-1.5 py-0.5 rounded font-semibold">
                              {designType}
                            </span>
                          )}
                        </div>
                      ) : (
                        <p className="text-[11px] text-gray-400 italic">{tr ? 'Henüz stand atanmadı' : 'No stand assigned yet'}</p>
                      )}
                      {item.email && (
                        <p className="text-[10px] text-gray-400 truncate" title={item.email}>{item.email}</p>
                      )}
                    </div>
                  </button>
                );
              })}
              {filtered.length === 0 && search.trim() && (
                <p className="text-center text-gray-400 py-6 text-sm">
                  {tr ? `"${search}" için sonuç yok.` : `No results for "${search}".`}
                </p>
              )}
            </div>
          );
        }
        const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
        const cur = Math.min(page, totalPages);
        const slice = filtered.slice((cur - 1) * PAGE_SIZE, cur * PAGE_SIZE);
        return (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 px-5 py-4">
              {slice.map(item => {
                const cfg = statusLabel(item.status);
                const hasRequest = !!(item.requested_area || item.requested_design_type);
                // Phase 86AM — On the Stand Designs tab cards are clickable;
                // clicking dispatches a DOM event so the sibling
                // AdminStandDesignsSection can open that tenant's design (or
                // surface a "not uploaded yet" toast if none exists).
                const handleCardClick = () => {
                  if (!clickable) return;
                  window.dispatchEvent(new CustomEvent('cop31:open-stand-design-by-tenant', {
                    detail: { tenant_id: item.id, country: item.company_name }
                  }));
                };
                return (
                  <div
                    key={item.id}
                    role={clickable ? 'button' : undefined}
                    tabIndex={clickable ? 0 : -1}
                    data-testid={clickable ? `country-card-${item.id}` : undefined}
                    onClick={handleCardClick}
                    onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleCardClick(); } } : undefined}
                    className={`rounded-2xl border bg-white p-3 flex flex-col transition-all ${
                      clickable
                        ? 'border-gray-200 hover:border-orange-400 hover:shadow-md cursor-pointer focus:outline-none focus:ring-2 focus:ring-orange-300'
                        : 'border-gray-100 hover:shadow-sm'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1.5 min-w-0">
                      <div className={`h-2 w-2 rounded-full flex-shrink-0 ${cfg.dotCls || 'bg-gray-300'}`} />
                      <p className="text-sm font-bold text-gray-800 truncate flex-1" title={item.company_name}>{item.company_name}</p>
                    </div>
                    <p className="text-[10px] text-gray-400 truncate mb-2" title={item.email}>{item.email}</p>
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-lg self-start mb-2 ${cfg.badgeCls || 'bg-gray-100 text-gray-500'}`}>
                      {cfg.label}
                    </span>
                    {hasRequest && (
                      <div className="flex flex-wrap items-center gap-1 mt-auto pt-2 border-t border-gray-50" data-testid={`stand-request-summary-${item.id}`}>
                        {item.requested_design_type && (
                          <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${item.requested_design_type === 'modular' ? 'bg-indigo-50 text-indigo-700 border border-indigo-200' : 'bg-fuchsia-50 text-fuchsia-700 border border-fuchsia-200'}`}>
                            {item.requested_design_type === 'modular' ? (tr ? 'Modüler' : 'Modular') : (tr ? 'Özel' : 'Custom')}
                          </span>
                        )}
                        {item.requested_area && (
                          <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-700 border border-gray-200">
                            {item.requested_area} m²
                          </span>
                        )}
                        {item.has_custom_design_pdf && (
                          <button
                            type="button"
                            onClick={async (e) => {
                              e.stopPropagation();
                              try {
                                const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ultra-admin/custom-design-pdf/${item.id}`);
                                const data = await r.json();
                                if (r.ok && data.url) window.open(data.url, '_blank', 'noopener');
                                else alert(data.detail || (tr ? 'PDF yüklenemedi.' : 'Failed to load PDF.'));
                              } catch (e) { alert(tr ? 'PDF yüklenemedi.' : 'Failed to load PDF.'); }
                            }}
                            data-testid={`view-custom-pdf-${item.id}`}
                            className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 transition-colors inline-flex items-center gap-1"
                          >
                            <FileText className="h-2.5 w-2.5" />
                            PDF
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-5 pb-4 pt-1 text-xs text-gray-500" data-testid="country-stand-pagination">
                <span>
                  {tr ? `${(cur - 1) * PAGE_SIZE + 1}-${Math.min(cur * PAGE_SIZE, filtered.length)} / ${filtered.length}` : `${(cur - 1) * PAGE_SIZE + 1}-${Math.min(cur * PAGE_SIZE, filtered.length)} of ${filtered.length}`}
                </span>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={cur <= 1}
                    className="px-2 py-1 rounded-lg bg-white border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed font-bold"
                  >‹</button>
                  <span className="px-2 font-mono">
                    {cur} / {totalPages}
                  </span>
                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={cur >= totalPages}
                    className="px-2 py-1 rounded-lg bg-white border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed font-bold"
                  >›</button>
                </div>
              </div>
            )}
          </>
        );
      })()}
    </div>
  );
}
